import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FiEdit2, FiSave, FiX, FiPlus, FiTrash2, FiUpload, FiFileText, FiEye,
    FiUser, FiCreditCard, FiBook, FiAward, FiGlobe, FiActivity, FiMapPin, FiPhone, FiCalendar, FiMail, FiShield, FiLock, FiCheckCircle, FiAlertTriangle, FiStar, FiChevronLeft, FiChevronRight, FiPrinter, FiCode, FiUsers, FiTrendingUp, FiHeart, FiBriefcase, FiClock, FiHelpCircle,
    FiArrowLeft, FiArrowRight, FiDownload, FiXCircle, FiCheck, FiDollarSign
} from 'react-icons/fi';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { RiDownloadCloudFill } from "react-icons/ri";
import { FaIdCard, FaAddressCard } from 'react-icons/fa';
import toast from 'react-hot-toast';

import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, differenceInYears, differenceInMonths } from 'date-fns';
import Tesseract from 'tesseract.js';
// html-to-image and jsPDF imports removed
import api from '../utils/api';
import ImageCropper from '../components/ImageCropper';
import EmployeePerformanceRevisionModal from '../components/EmployeePerformanceRevisionModal';
import { useUser } from '../contexts/UserContext';
import EmployeeGitActivity from '../charts/employee_git_activity';
import OverallTicketChart from '../charts/OverallTicketChart';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
// PerformanceDashboard and DynamicTable imports removed (source files not present)

// --- Attendance Report Helpers (shared with AttendanceDashboard) ---
const DEFAULT_SHIFT_START_MINS = 9 * 60;
const DEFAULT_SHIFT_END_MINS = 18 * 60;

const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const parseAttendanceTime = (timeStr) => {
    if (!timeStr || timeStr === '-' || !timeStr.trim()) return null;
    try {
        const cleanStr = timeStr.replace(/\(M\)|\(F\)|\(G\)/g, '').trim();
        const parts = cleanStr.split(' ');
        if (parts.length < 2) return null;
        const [time, modifier] = parts;
        let [hours, minutes] = time.split(':').map(Number);
        if (modifier.includes('PM') && hours < 12) hours += 12;
        if (modifier.includes('AM') && hours === 12) hours = 0;
        return { hours, minutes, totalMinutes: hours * 60 + minutes };
    } catch (e) {
        return null;
    }
};

const formatAttendanceDuration = (totalMins) => {
    if (totalMins <= 0) return '-';
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (h === 0) return `${m} Min`;
    if (m === 0) return `${h} ${h === 1 ? 'Hour' : 'Hours'}`;
    return `${h} ${h === 1 ? 'Hr' : 'Hrs'} ${m} Min`;
};

const calculateAttendanceLate = (checkInStr) => {
    if (!checkInStr || checkInStr === '-') return '-';
    const firstPunch = checkInStr.split(',')[0].trim();
    const inTime = parseAttendanceTime(firstPunch);
    if (!inTime) return '-';
    const diff = inTime.totalMinutes - DEFAULT_SHIFT_START_MINS;
    return diff > 0 ? formatAttendanceDuration(diff) : '-';
};

const calculateAttendanceOvertime = (checkOutStr) => {
    if (!checkOutStr || checkOutStr === '-') return '-';
    const punches = checkOutStr.split(',').map((s) => s.trim());
    const lastPunch = punches[punches.length - 1];
    const outTime = parseAttendanceTime(lastPunch);
    if (!outTime) return '-';
    const diff = outTime.totalMinutes - DEFAULT_SHIFT_END_MINS;
    return diff > 0 ? formatAttendanceDuration(diff) : '-';
};

const calculateAttendanceProduction = (checkInStr, checkOutStr) => {
    if (!checkInStr || checkInStr === '-' || !checkOutStr || checkOutStr === '-') return '-';
    const ins = checkInStr.split(',').map((s) => s.trim());
    const outs = checkOutStr.split(',').map((s) => s.trim());
    let totalMins = 0;
    const pairCount = Math.min(ins.length, outs.length);
    for (let i = 0; i < pairCount; i++) {
        const inTime = parseAttendanceTime(ins[i]);
        const outTime = parseAttendanceTime(outs[i]);
        if (inTime && outTime) {
            totalMins += outTime.totalMinutes - inTime.totalMinutes;
        }
    }
    return totalMins > 0 ? formatAttendanceDuration(totalMins) : '-';
};

const getAttendanceStatusBadgeClass = (status) => {
    const s = (status || '').toUpperCase().replace(/_/g, ' ').trim();
    if (s.includes('PRESENT')) return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    if (s.includes('ABSENT')) return 'bg-rose-100 text-rose-700 border border-rose-200';
    if (s.includes('LATE') || s.includes('WENT LATE')) return 'bg-amber-100 text-amber-700 border border-amber-200';
    if (s.includes('HALF DAY')) return 'bg-orange-100 text-orange-700 border border-orange-200';
    if (s.includes('LEAVE') || s === 'LOP') return 'bg-purple-100 text-purple-700 border border-purple-200';
    if (s.includes('PERM') || s === 'PERMISSION' || s.includes('AUTO')) return 'bg-violet-100 text-violet-700 border border-violet-200';
    if (s.includes('DUTY') || s === 'OD') return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
    return 'bg-gray-100 text-gray-600 border border-gray-200';
};

const normalizeAttendanceStatus = (status) => (status || '').toUpperCase().replace(/_/g, ' ').trim();

const attendanceStatusMatchesFilter = (row, statusFilter) => {
    const s = normalizeAttendanceStatus(row?.status);
    const hasLateDeviation = row?.lateVal && row.lateVal !== '-';

    if (!statusFilter || statusFilter === 'ALL') return true;
    if (statusFilter === 'PRESENT') {
        return (
            s.startsWith('PRESENT') ||
            s.startsWith('LATE') ||
            s.includes('WENT LATE') ||
            s.includes('WORK FROM HOME') ||
            s === 'WFH' ||
            s.startsWith('ON DUTY') ||
            s === 'OD' ||
            s.includes('PERM') ||
            s === 'PERMISSION' ||
            s.startsWith('AUTO')
        );
    }
    if (statusFilter === 'LATE') {
        return (
            s.startsWith('LATE') ||
            s.includes('WENT LATE') ||
            (s.includes('PERM') && hasLateDeviation) ||
            (s === 'PERMISSION' && hasLateDeviation)
        );
    }
    if (statusFilter === 'LOP') return s === 'LOP' || s.includes('LOP');
    if (statusFilter === 'LEAVE') {
        return (
            s.includes('LEAVE') ||
            s === 'ON LEAVE' ||
            s.includes('HALF DAY')
        );
    }
    if (statusFilter === 'PERMISSION') {
        return (
            s === 'PERMISSION' ||
            s.includes('PERM') ||
            s.startsWith('AUTO') ||
            s === 'PER'
        );
    }
    if (statusFilter === 'OD') return s.includes('ON DUTY') || s === 'OD';
    if (statusFilter === 'ABSENT') return s === 'ABSENT';
    if (statusFilter === 'MISSING_STATUS') {
        return (
            s === 'NOT MARKED' ||
            s === 'MISSING STATUS' ||
            s === ''
        );
    }
    return s === statusFilter;
};

const getAttendanceFilterMeta = (statusFilter) => {
    const map = {
        ALL: { label: 'Detailed Period Logs', pillClass: 'bg-gray-100 text-gray-700 border border-gray-200' },
        PRESENT: { label: 'Present Days', pillClass: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
        LATE: { label: 'Late Days', pillClass: 'bg-amber-50 text-amber-700 border border-amber-100' },
        LOP: { label: 'LOP Days', pillClass: 'bg-rose-50 text-rose-700 border border-rose-100' },
        LEAVE: { label: 'Leave Days', pillClass: 'bg-purple-50 text-purple-700 border border-purple-100' },
        PERMISSION: { label: 'Permission Days', pillClass: 'bg-pink-50 text-pink-700 border border-pink-100' },
        OD: { label: 'OD Days', pillClass: 'bg-indigo-50 text-indigo-700 border border-indigo-100' },
        ABSENT: { label: 'Absent Days', pillClass: 'bg-rose-50 text-rose-700 border border-rose-100' },
        MISSING_STATUS: { label: 'Missing Status Days', pillClass: 'bg-orange-50 text-orange-700 border border-orange-100' },
    };
    return map[statusFilter] || map.ALL;
};

/** Column layout per attendance popup type — only relevant fields */
const getAttendanceDetailPopupColumns = (statusFilter) => {
    const filter = statusFilter || 'ALL';
    switch (filter) {
        case 'LEAVE':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'details', label: 'Leave Type', align: 'left' },
                { key: 'leave_schedule', label: 'Schedule', align: 'center' },
            ];
        case 'LOP':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'details', label: 'Reason', align: 'left' },
                { key: 'leave_schedule', label: 'Schedule', align: 'center' },
            ];
        case 'ABSENT':
        case 'MISSING_STATUS':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'check_in', label: 'Check-In', align: 'center' },
                { key: 'check_out', label: 'Check-Out', align: 'center' },
                { key: 'punches', label: 'Punch Records', align: 'left' },
            ];
        case 'PERMISSION':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'check_in', label: 'Check-In', align: 'center' },
                { key: 'check_out', label: 'Check-Out', align: 'center' },
                { key: 'permission', label: 'Permission', align: 'center' },
            ];
        case 'LATE':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'check_in', label: 'Check-In', align: 'center' },
                { key: 'check_out', label: 'Check-Out', align: 'center' },
                { key: 'deviation', label: 'Late By', align: 'center' },
            ];
        case 'PRESENT':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'check_in', label: 'Check-In', align: 'center' },
                { key: 'check_out', label: 'Check-Out', align: 'center' },
                { key: 'production', label: 'Production', align: 'center' },
                { key: 'ot', label: 'OT', align: 'center' },
            ];
        case 'OD':
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'check_in', label: 'Check-In', align: 'center' },
                { key: 'check_out', label: 'Check-Out', align: 'center' },
                { key: 'details', label: 'Notes', align: 'left' },
            ];
        case 'ALL':
        default:
            return [
                { key: 'index', label: '#', align: 'center', width: 'w-12' },
                { key: 'date', label: 'Date', align: 'left' },
                { key: 'status', label: 'Status', align: 'center' },
                { key: 'check_in', label: 'Check-In', align: 'center' },
                { key: 'check_out', label: 'Check-Out', align: 'center' },
                { key: 'permission', label: 'Permission', align: 'center' },
                { key: 'deviation', label: 'Deviation', align: 'center' },
                { key: 'ot', label: 'OT', align: 'center' },
                { key: 'production', label: 'Production', align: 'center' },
                { key: 'punches', label: 'Punch Records', align: 'left' },
            ];
    }
};

const ATTENDANCE_STATUS_DATES_API_MAP = {
    LATE: 'LATE',
    LOP: 'LOP',
    LEAVE: 'ON_LEAVE',
    PERMISSION: 'PERMISSION',
    OD: 'OD',
    ABSENT: 'ABSENT',
    MISSING_STATUS: 'MISSING_STATUS',
};

const ATTENDANCE_PREFETCHED_STATUS_FILTERS = [...Object.keys(ATTENDANCE_STATUS_DATES_API_MAP)];

const mapStatusDatesApiRow = (item) => ({
    attendance_date: item.date,
    status: item.status,
    check_in: item.check_in && item.check_in !== '-' ? item.check_in : '',
    check_out: item.check_out && item.check_out !== '-' ? item.check_out : '',
    punch_records: item.punch_records || 'No Logs',
    leave_schedule: item.leave_schedule || null,
    is_unscheduled: item.is_unscheduled,
});

const getAttendanceDetailRowsForDisplay = (rows, statusFilter) => {
    if (!statusFilter || statusFilter === 'ALL' || ATTENDANCE_PREFETCHED_STATUS_FILTERS.includes(statusFilter)) {
        return rows;
    }
    return rows.filter((d) => attendanceStatusMatchesFilter(d, statusFilter));
};

// --- Constants & Config ---
const countryCodes = [
    { code: '+91', country: 'IN', flag: 'https://flagcdn.com/w20/in.png' },
    { code: '+1', country: 'US', flag: 'https://flagcdn.com/w20/us.png' },
    { code: '+44', country: 'GB', flag: 'https://flagcdn.com/w20/gb.png' },
    { code: '+971', country: 'AE', flag: 'https://flagcdn.com/w20/ae.png' },
    { code: '+65', country: 'SG', flag: 'https://flagcdn.com/w20/sg.png' },
];

// --- Helper Functions ---
const validatePhone = (phone) => {
    // Validates Indian numbers: +91 or just 10 digits starting with 6-9
    if (!phone) return false;
    const clean = phone.replace(/\D/g, '').slice(-10);
    return /^[6-9]\d{9}$/.test(clean);
};

const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
};

const validateIFSC = (ifsc) => {
    const re = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return re.test(ifsc);
};

const validateAadhaar = (num) => {
    const re = /^\d{12}$/;
    return re.test(num ? num.replace(/\s/g, '') : '');
};

const validatePAN = (num) => {
    // Indian PAN structure: 3 Letters, 4th Char (Type), 5th Letter, 4 Digits, 1 Letter
    // 4th char types: P(Individual), C(Company), H(HUF), F(Firm), A(AOP), T(Trust), B(BOI), L(Local), J(Artificial), G(Govt)
    const re = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]{1}$/;
    return re.test(num ? num.toUpperCase() : '');
};

const validateBankAccount = (num) => {
    const re = /^\d{9,18}$/;
    return re.test(num);
};

// --- Sub-Components ---
const EMPLOYEE_WIZARD_STEPS = [
    { id: 'personal-details', label: 'Personal Details' },
    { id: 'bank-id', label: 'Bank & ID Proof' },
    { id: 'education', label: 'Educational Details' },
];

const WIZARD_STEP_SECTIONS = {
    1: ['personal', 'community', 'health'],
    2: ['bank', 'identity'],
    3: ['academic', 'skills', 'certificates'],
};

const isEmptyAcademicRow = (item) => {
    if (!item) return true;
    return !item.qualification
        && !item.board
        && !item.degree
        && !(item.specialization || '').trim()
        && !(item.institute || '').trim()
        && !item.year
        && (item.percentage === undefined || item.percentage === '' || item.percentage === null)
        && !item.file;
};

const isEmptySkillRow = (item) => !(item?.skill || '').trim();

const isEmptyCertificateRow = (item) => {
    if (!item) return true;
    return !(item.title || '').trim()
        && !(item.issuing_authority || '').trim()
        && !item.issue_date
        && !item.file;
};

const isEmptyPreviousExperienceRow = (item) => {
    if (!item) return true;
    return !(item.company_name || '').trim()
        && !(item.job_role || '').trim()
        && !item.start_date
        && !item.end_date;
};

/** Drop unused step-3 placeholders so empty defaults are never persisted. */
const stripEmptyWizardStep3Rows = (profileData) => {
    const next = JSON.parse(JSON.stringify(profileData || {}));
    if (Array.isArray(next.academic_details)) {
        next.academic_details = next.academic_details.filter((row) => !isEmptyAcademicRow(row));
    }
    if (Array.isArray(next.skills)) {
        next.skills = next.skills.filter((row) => !isEmptySkillRow(row));
    }
    if (Array.isArray(next.certificates)) {
        next.certificates = next.certificates.filter((row) => !isEmptyCertificateRow(row));
    }
    return next;
};

const validateWizardStep3Extras = (d) => {
    const newErrors = {};
    const academics = (d.academic_details || []).filter((row) => !isEmptyAcademicRow(row));
    const skills = (d.skills || []).filter((row) => !isEmptySkillRow(row));

    if (!academics.length) {
        newErrors.academic_required = 'At least one Educational Qualification entry is required';
    }
    if (!skills.length) {
        newErrors.skills_required = 'At least one Professional Skill entry is required';
    }

    (d.skills || []).forEach((item, idx) => {
        if (isEmptySkillRow(item)) return;
        if (!item.category) newErrors[`skill_${idx}_category`] = 'Category Required';
        if (!item.skill?.trim()) newErrors[`skill_${idx}_name`] = 'Skill Name Required';
        if (!item.proficiency) newErrors[`skill_${idx}_proficiency`] = 'Proficiency Required';
    });

    (d.academic_details || []).forEach((item, idx) => {
        if (isEmptyAcademicRow(item)) return;
        if (!item.qualification) newErrors[`ac_qual_${idx}`] = 'Qualification Level Required';
        if (['10th', '12th'].includes(item.qualification)) {
            if (!item.board) newErrors[`ac_board_${idx}`] = 'Board / Stream Required';
        } else if (item.qualification) {
            if (!item.degree) newErrors[`ac_degree_${idx}`] = 'Degree Required';
            if (!item.specialization?.trim()) newErrors[`ac_spec_${idx}`] = 'Specialization Required';
        }
        if (item.qualification && !item.institute?.trim()) {
            newErrors[`ac_institute_${idx}`] = ['10th', '12th'].includes(item.qualification)
                ? 'School Name Required'
                : 'College / Institute Required';
        }
    });

    return newErrors;
};

const WIZARD_SECTION_EXTRA_ERROR_FILTER = {
    academic: (key) => key === 'academic_required' || key.startsWith('ac_'),
    skills: (key) => key === 'skills_required' || key.startsWith('skill_'),
    certificates: (key) => key === 'certificates_required' || key.startsWith('cert_'),
};

const WIZARD_SECTION_FIELDS = {
    academic: 'academic_details',
    skills: 'skills',
    certificates: 'certificates',
};

const isWizardRowErrorKey = (section, key, idx) => {
    if (section === 'academic') return key.startsWith('ac_') && key.endsWith(`_${idx}`);
    if (section === 'skills') return key.startsWith(`skill_${idx}_`);
    if (section === 'certificates') return key.startsWith(`cert_${idx}_`);
    return false;
};

const resolveIdentityProofType = (profileData) => {
    const d = profileData || {};
    if (d.aadhaar_number || d.aadhaar_file) return 'AADHAAR';
    if (d.pan_number || d.pan_file) return 'PAN';
    return '';
};

const buildIdentityVerificationStatus = (profileData) => {
    const d = profileData || {};
    return {
        PAN: d.pan_number && d.pan_file ? 'verified' : 'idle',
        AADHAAR: d.aadhaar_number && d.aadhaar_file ? 'verified' : 'idle',
    };
};

const SectionCard = ({ title, icon: Icon, children, isEditing, onEdit, onSave, onCancel, compact = false, summary = false, showUpdateOnView = false, onOpenWizard = null, showPerformanceRevisionOnView = false, onOpenPerformanceRevision = null }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full ${summary ? '' : compact ? 'mb-4' : 'mb-6'}`}
    >
        <div className={`flex items-center justify-between border-b border-gray-100 bg-gray-55/50 rounded-t-xl shrink-0 ${summary ? 'px-3 py-2' : compact ? 'px-4 py-2.5' : 'px-5 py-4'}`}>
            <div className="flex items-center gap-2 min-w-0">
                <div className={`bg-[#ffebee] text-[#C30010] rounded-lg shrink-0 ${summary ? 'p-1' : compact ? 'p-1.5' : 'p-2'}`}>
                    <Icon size={summary ? 14 : compact ? 16 : 18} />
                </div>
                <h3 className={`font-bold text-gray-800 uppercase tracking-wide truncate ${summary ? 'text-[11px] leading-tight' : compact ? 'text-xs' : 'text-sm'}`}>{title}</h3>
            </div>
            <div className="shrink-0 flex items-center gap-1.5">
                {!isEditing && showPerformanceRevisionOnView && onOpenPerformanceRevision && (
                    <button
                        onClick={onOpenPerformanceRevision}
                        className={`text-[#C30010] bg-white border border-red-200 hover:bg-[#ffebee] rounded-lg transition-all font-bold shadow-sm flex items-center gap-1 ${summary ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[10px] sm:text-xs'}`}
                    >
                        <FiAward size={summary ? 12 : 14} />
                        Performance Revision
                    </button>
                )}
                {!isEditing && showUpdateOnView && onOpenWizard && (
                    <button
                        onClick={onOpenWizard}
                        className={`text-white bg-[#C30010] hover:bg-[#a0000d] rounded-lg transition-all font-bold shadow-sm shadow-red-100 flex items-center gap-1 ${summary ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[10px] sm:text-xs'}`}
                    >
                        <FiSave size={summary ? 12 : 14} />
                        Update Form
                    </button>
                )}
                {!isEditing && onEdit && (
                    <button
                        onClick={onEdit}
                        className={`text-gray-400 hover:text-[#C30010] hover:bg-[#ffebee] rounded-full transition-all ${summary ? 'p-1' : 'p-2'}`}
                        title="Edit Section"
                    >
                        <FiEdit2 size={summary ? 14 : 16} />
                    </button>
                )}
                {isEditing && onSave && (
                    <>
                        <button
                            onClick={onCancel}
                            className={`text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all font-bold ${summary ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[10px] sm:text-xs'}`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onSave}
                            className={`text-white bg-[#C30010] hover:bg-[#a0000d] rounded-lg transition-all font-bold shadow-sm shadow-red-100 flex items-center gap-1 ${summary ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[10px] sm:text-xs'}`}
                        >
                            <FiSave size={summary ? 12 : 14} />
                            Update
                        </button>
                    </>
                )}
            </div>
        </div>
        <div className={`flex-1 min-h-0 ${summary ? 'p-3' : compact ? 'p-3 sm:p-4' : 'p-3 sm:p-6'}`}>
            {children}
        </div>
        {isEditing && (
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
                <button
                    onClick={onCancel}
                    className="px-3 sm:px-4 py-1 sm:py-1.5 text-[10px] sm:text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    className="px-3 sm:px-4 py-1 sm:py-1.5 text-[10px] sm:text-xs font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded-lg shadow-md shadow-red-100 transition flex items-center gap-1.5"
                >
                    <FiSave className="hidden sm:block" size={14} /> Save Changes
                </button>
            </div>
        )}
    </motion.div>
);

const InfoItem = ({ icon: Icon, label, value, isLink, href, extra }) => (
    <div className="flex items-start gap-3">
        <div className="p-1.5 bg-gray-50 text-gray-400 rounded-lg shrink-0 mt-0.5 border border-gray-100 shadow-sm animate-fade-in">
            <Icon size={14} className="text-[#C30010]" />
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">{label}</div>
            <div className="mt-1.5">
                {isLink ? (
                    <a href={href} className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline transition truncate block">
                        {value}
                    </a>
                ) : (
                    <span className="text-xs font-bold text-gray-800 break-words block leading-snug">
                        {value} {extra && <span className="text-[10px] text-gray-400 font-semibold ml-1.5 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">{extra}</span>}
                    </span>
                )}
            </div>
        </div>
    </div>
);

const Field = ({ label, value, icon: Icon, fullWidth, type, compact = false, wrap = false, onEditClick }) => {
    const isTel = type === 'tel' && !!value;
    return (
        <div className={`flex flex-col ${compact ? 'mb-0' : 'mb-2'} ${fullWidth ? 'col-span-full' : ''}`}>
            <label className={`font-bold text-gray-400 uppercase tracking-wider ${compact ? 'text-[9px] mb-0' : 'text-[10px] mb-0.5'} flex items-center gap-1`}>
                {label}
                {onEditClick && (
                    <button
                        type="button"
                        onClick={onEditClick}
                        className="text-gray-400 hover:text-[#C30010] transition-colors p-0.5 rounded cursor-pointer flex items-center justify-center"
                        title={`Edit ${label}`}
                    >
                        <FiEdit2 size={11} className="text-gray-400 hover:text-[#C30010]" />
                    </button>
                )}
            </label>
            <div className={`flex items-center gap-2 font-medium ${compact ? 'text-sm mt-0.5' : 'text-sm'}`}>
                {Icon && <Icon size={14} className="text-gray-400" />}
                {isTel ? (
                    <a href={`tel:${value.replace(/\s+/g, '')}`} className="text-blue-600 hover:text-blue-700 transition font-bold flex items-center gap-1.5">
                        <span className="truncate">{value}</span>
                    </a>
                ) : (
                    <span className={`w-full text-gray-800 ${wrap ? 'break-words leading-snug' : 'truncate'}`}>{value || '-'}</span>
                )}
            </div>
        </div>
    );
};


const customDatePickerStyles = `

    .react-datepicker {
        font-family: 'Inter', sans-serif;
        border: 1px solid #e5e7eb !important;
        border-radius: 16px !important;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        padding: 8px !important;
        background-color: white !important;
        font-size: 0.75rem !important;
        min-width: 240px !important;
    }
    .react-datepicker__header {
        background-color: white !important;
        border-bottom: 1px solid #f3f4f6 !important;
        padding-top: 0 !important;
        margin-bottom: 8px !important;
        padding-bottom: 8px !important;
    }
    .react-datepicker__day-names {
        display: flex !important;
        justify-content: space-between !important;
        margin-top: 8px !important;
    }
    .react-datepicker__day-name {
        color: #9ca3af !important;
        font-weight: 700 !important;
        width: 1.8rem !important;
        line-height: 1.8rem !important;
        margin: 0 !important;
        text-transform: uppercase;
        font-size: 0.75rem !important;
        text-align: center !important;
    }
    .react-datepicker__month {
        margin: 0 !important;
    }
    .react-datepicker__week {
        display: flex !important;
        justify-content: space-between !important;
    }
    .react-datepicker__day {
        margin: 0 !important;
        width: 1.8rem !important;
        line-height: 1.8rem !important;
        border-radius: 50% !important;
        color: #374151 !important;
        font-weight: 500 !important;
        font-size: 0.75rem !important;
        transition: all 0.2s ease;
    }
    .react-datepicker__day:hover {
        background-color: #fee2e2 !important;
        color: #C30010 !important;
    }
    .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
        background-color: #ec101fff !important;
        color: white !important;
        font-weight: 700 !important;
        box-shadow: 0 4px 6px -1px rgba(195, 0, 16, 0.3);
    }
    .react-datepicker__current-month, .react-datepicker-year-header {
        display: none !important;
    }
    .react-datepicker__day--today {
        border: 2px solid #C30010 !important;
        background-color: transparent;
        color: #C30010 !important;
        font-weight: 700 !important;
    }
    .react-datepicker__day--today.react-datepicker__day--selected {
        background-color: #C30010 !important;
        color: white !important;
    }
    .react-datepicker__day--disabled {
        color: #e5e7eb !important;
        cursor: default !important;
    }
    .react-datepicker__day--disabled:hover {
        background-color: transparent !important;
        color: #e5e7eb !important;
    }
    .react-datepicker__navigation {
        top: 24px !important;
    }
    .react-datepicker__navigation-icon::before {
        border-color: #4b5563 !important;
        border-width: 2px 2px 0 0 !important;
        width: 8px !important;
        height: 8px !important;
    }
    .react-datepicker-popper {
        z-index: 9999 !important;
    }
    /* Clean up the header selects */
    .react-datepicker__header select {
        padding: 2px 24px 2px 8px;
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        font-size: 0.75rem;
        background-color: #f9fafb;
        cursor: pointer;
        outline: none;
        color: #374151;
        font-weight: 600;
        transition: all 0.2s;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 4px center;
        background-size: 12px;
    }
    .react-datepicker__header select:hover {
        border-color: #d1d5db;
        background-color: white;
    }
    .react-datepicker__header select:focus {
        border-color: #C30010;
        box-shadow: 0 0 0 2px rgba(195, 0, 16, 0.1);
    }
    .datepicker-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid #f3f4f6;
    }
    .datepicker-done-btn {
        background-color: #C30010;
        color: white;
        padding: 6px 16px;
        border-radius: 8px;
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: background-color 0.2s;
    }
    .datepicker-done-btn:hover {
        background-color: #a0000d;
    }
`;

const InputField = ({ label, name, value, onChange, type = "text", fullWidth, options, as = "input", error, required, list, min, max, step, placeholder, minDate, maxDate, disabled, rows = 3, compact = false }) => (
    <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-1.5'} ${fullWidth ? 'col-span-full' : ''}`}>
        <style>{customDatePickerStyles}</style>
        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 inline-flex items-center gap-1">
            {label} {required && !disabled && <span className="text-red-500 font-bold">*</span>}
        </label>
        {as === 'select' ? (
            <select
                name={name}
                value={value || ''}
                onChange={onChange}
                disabled={disabled}
                className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800 cursor-pointer'} transition-all appearance-none`}
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1rem' }}
            >
                <option value="">Select...</option>
                {options && options.map((opt, i) => (
                    <option key={`${opt}-${i}`} value={opt}>{opt}</option>
                ))}
            </select>
        ) : as === 'textarea' ? (
            <textarea
                name={name}
                value={value || ''}
                onChange={onChange}
                rows={rows}
                disabled={disabled}
                placeholder={placeholder}
                className={`w-full ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2'} bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800'} transition-all resize-none`}
            />
        ) : type === 'date' ? (
            <div className="relative">
                <DatePicker
                    selected={value ? new Date(value) : null}
                    onChange={(date) => {
                        if (onChange) {
                            onChange({
                                target: {
                                    name,
                                    value: date ? format(date, 'yyyy-MM-dd') : ''
                                }
                            });
                        }
                    }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    disabled={disabled}
                    className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800'} transition-all block`}
                    wrapperClassName="w-full"
                    maxDate={maxDate}
                    minDate={minDate}
                    calendarClassName="custom-datepicker"
                    renderCustomHeader={({
                        date,
                        changeYear,
                        changeMonth,
                        decreaseMonth,
                        increaseMonth,
                        prevMonthButtonDisabled,
                        nextMonthButtonDisabled,
                    }) => (
                        <div className="flex items-center justify-between px-2 py-2 bg-white border-b border-gray-100 gap-2">
                            <button
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                            >
                                <FiChevronLeft size={18} />
                            </button>

                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <select
                                    value={date.getMonth()}
                                    onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                                    className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                                >
                                    {[
                                        "January", "February", "March", "April", "May", "June",
                                        "July", "August", "September", "October", "November", "December",
                                    ].map((option, index) => (
                                        <option key={option} value={index}>{option}</option>
                                    ))}
                                </select>
                                <select
                                    value={date.getFullYear()}
                                    onChange={({ target: { value } }) => changeYear(parseInt(value))}
                                    className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                                >
                                    {Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                            >
                                <FiChevronRight size={18} />
                            </button>
                        </div>
                    )}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <FiCalendar size={14} />
                </div>
            </div>
        ) : (
            <>
                <input
                    type={type}
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    list={list}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium ${disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : 'text-gray-800'} transition-all`}
                />
                {list && options && (
                    <datalist id={list}>
                        {options.map(opt => <option key={opt} value={opt} />)}
                    </datalist>
                )}
            </>
        )}
        {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
    </div>
);


const DatePickerField = ({ label, selected, onChange, maxDate = new Date(), minDate, required, error }) => (
    <div className="flex flex-col gap-1.5 w-full">
        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 inline-flex items-center gap-1">
            {label} {required && <span className="text-red-500 font-bold">*</span>}
        </label>
        <div className={`w-full relative ${error ? 'border border-red-500 rounded-xl' : ''}`}>
            <DatePicker
                selected={selected ? new Date(selected) : null}
                onChange={onChange}
                maxDate={maxDate}
                minDate={minDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                wrapperClassName="w-full"
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium text-gray-800 transition-all font-mono"
                onKeyDown={(e) => e.preventDefault()}
                renderCustomHeader={({
                    date,
                    changeYear,
                    changeMonth,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                }) => (
                    <div className="flex items-center justify-between px-2 py-2 bg-white border-b border-gray-100 gap-2">
                        <button
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                        >
                            <FiChevronLeft size={18} />
                        </button>

                        <div className="flex items-center gap-2 flex-1 justify-center">
                            <select
                                value={date.getMonth()}
                                onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                                className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                            >
                                {[
                                    "January", "February", "March", "April", "May", "June",
                                    "July", "August", "September", "October", "November", "December",
                                ].map((option, index) => (
                                    <option key={option} value={index}>{option}</option>
                                ))}
                            </select>
                            <select
                                value={date.getFullYear()}
                                onChange={({ target: { value } }) => changeYear(parseInt(value))}
                                className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-800 font-bold focus:outline-none focus:ring-1 focus:ring-[#C30010] cursor-pointer hover:border-[#C30010]/50 transition appearance-none"
                            >
                                {Array.from({ length: 120 }, (_, i) => new Date().getFullYear() - 100 + i).map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                            className="p-1.5 hover:bg-gray-100 rounded-full text-gray-600 focus:outline-none transition shrink-0"
                        >
                            <FiChevronRight size={18} />
                        </button>
                    </div>
                )}
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <FiCalendar size={14} />
            </div>
        </div>
        {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
    </div>
);

const PhoneInput = ({ label, name, value, onChange, error, required }) => {
    const [code, setCode] = useState('+91');
    const [number, setNumber] = useState('');

    useEffect(() => {
        if (value) {
            const parts = value.split(' ');
            if (parts.length > 1) {
                setCode(parts[0]);
                setNumber(parts[1]);
            } else if (value.startsWith('+')) {
                const found = countryCodes.find(c => value.startsWith(c.code));
                if (found) {
                    setCode(found.code);
                    setNumber(value.replace(found.code, '').trim());
                } else {
                    setNumber(value);
                }
            } else {
                setNumber(value);
            }
        }
    }, [value]);

    const handleNumberChange = (e) => {
        const val = e.target.value.replace(/\D/g, '');
        if (val.length > 0 && !/^[6-9]/.test(val)) return;
        if (val.length <= 10) {
            setNumber(val);
            onChange({ target: { name, value: `${code} ${val}` } });
        }
    };

    const currentCountry = countryCodes.find(c => c.code === code) || countryCodes[0];

    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">
                {label} {required && <span className="text-red-500 font-bold">*</span>}
            </label>
            <div className="flex gap-3">
                <div className="relative w-28">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
                        <img src={currentCountry.flag} alt="" className="w-5 h-3.5 rounded-sm object-cover border border-gray-100 shadow-sm" />
                    </div>
                    <select
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value);
                            onChange({ target: { name, value: `${e.target.value} ${number}` } });
                        }}
                        className="w-full pl-10 pr-2 py-2 bg-gray-50 border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-800 outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] transition-all cursor-pointer appearance-none"
                    >
                        {countryCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                </div>
                <input
                    type="text"
                    value={number}
                    onChange={handleNumberChange}
                    placeholder="XXXXXXXXXX"
                    className={`flex-1 px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-bold tracking-[0.1em] text-gray-800 transition-all font-mono`}
                />
            </div>
            {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
        </div>
    );
};

const FileUpload = ({ label, fileId, onUpload, currentFile, error, required }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">
            {label} {required && <span className="text-red-500 font-bold">*</span>}
        </label>
        <div className="flex items-center gap-4">
            <label className={`cursor-pointer inline-flex items-center gap-2.5 px-3 py-2 bg-red-50 border ${error ? 'border-red-500 text-red-600 ring-1 ring-red-50' : 'border-red-100 text-[#C30010] hover:bg-red-100 hover:border-[#C30010]'} rounded-xl shadow-sm  text-[12.5px] transition-all group shrink-0`}>
                <FiUpload className={`${error ? 'text-red-500' : 'text-[#C30010] group-hover:text-[#a0000d]'} transition-colors`} size={16} />
                <span className={`font-bold ${error ? 'text-red-600' : 'text-[#C30010] group-hover:text-[#a0000d]'} transition-colors`}>Choose Image / PDF</span>
                <input type="file" className="hidden" onChange={(e) => onUpload(fileId, e.target.files[0])} accept="image/*,application/pdf" />
            </label>

            {currentFile ? (
                <div className="flex items-center gap-3 overflow-hidden">
                    <span className="inline-flex items-center gap-2 text-[#C30010] bg-[#ffebee] px-3 py-2 rounded-xl border border-red-100 truncate shadow-sm" title={typeof currentFile === 'string' ? currentFile.split('/').pop() : currentFile.name}>
                        <FiFileText size={16} className="shrink-0" />
                        <span className="text-[11px] font-bold truncate max-w-[150px] uppercase tracking-tighter">{typeof currentFile === 'string' ? currentFile.split('/').pop() : currentFile.name}</span>
                    </span>
                    <a href={typeof currentFile === 'string' ? currentFile : URL.createObjectURL(currentFile)} target="_blank" rel="noopener noreferrer" className="shrink-0 h-10 w-10 flex items-center justify-center bg-white border border-gray-200 text-[#C30010] hover:bg-[#ffebee] rounded-xl shadow-sm transition-all" title="View Document">
                        <FiEye size={18} />
                    </a>
                </div>
            ) : (
                <span className="text-[11px] text-gray-400 font-medium italic opacity-60">No file selected</span>
            )}
        </div>
        {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
    </div>
);

const SearchableSelect = ({ label, value, onChange, options, error, required, placeholder, hideIcon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const filteredOptions = options ? [...options]
        .filter(opt => {
            const text = typeof opt === 'string' ? opt : (opt?.label || opt?.name || '');
            return text.toLowerCase().includes((value || '').toLowerCase());
        })
        .sort((a, b) => {
            const textA = typeof a === 'string' ? a : (a?.label || a?.name || '');
            const textB = typeof b === 'string' ? b : (b?.label || b?.name || '');
            return textA.localeCompare(textB, undefined, { sensitivity: 'base', numeric: true });
        })
    : [];

    return (
        <div className={`flex flex-col gap-1.5 relative col-span-full ${isOpen ? 'z-50' : 'z-auto'}`} ref={wrapperRef}>
            {label && (
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 inline-flex items-center gap-1">
                    {label} {required && <span className="text-red-500 font-bold">*</span>}
                </label>
            )}
            <div className="relative">
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder || "Type to search..."}
                    className={`w-full px-3 py-2 bg-white border ${error ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-medium text-gray-800 transition-all`}
                />
                {!hideIcon && (
                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C30010] transition-colors"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                )}
            </div>

            {isOpen && filteredOptions.length > 0 && (
                <div className="absolute top-full left-0 z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
                    {filteredOptions.map((opt, i) => (
                        <div
                            key={i}
                            onClick={() => {
                                onChange(opt);
                                setIsOpen(false);
                            }}
                            className="px-4 py-2.5 text-[12.5px] text-gray-700 hover:bg-[#ffebee] hover:text-[#C30010] cursor-pointer border-b border-gray-50 last:border-0 transition-all font-medium"
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
            {error && <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">{error}</span>}
        </div>
    );
};

// --- Wizard Components ---
const EmployeeUpdateWizard = ({
    steps,
    currentStep,
    completedSteps,
    onClose,
    onPrevious,
    onNext,
    onSave,
    onStepClick,
    isNextDisabled,
    isSaveDisabled = false,
    children,
}) => {
    const isLastStep = currentStep === steps.length;

    return (
        <div className="w-full bg-[#F8FAFC]">
            <div className="w-full py-1.5 sm:py-2 pb-20 sm:pb-24">
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h2 className="text-lg sm:text-xl font-black text-slate-800 tracking-tight">Employee Profile – Update Form</h2>
                            <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 font-medium">Complete each step to update employee details.</p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-[#C30010] hover:bg-[#a0000d] shadow-sm shadow-red-100 transition"
                        >
                            <FiArrowLeft size={14} />
                            <span>Back</span>
                        </button>
                    </div>

                    {/* Stepper — sticky progress bar */}
                    <div className="sticky top-0 z-30 mb-2 py-1.5 bg-[#F8FAFC]">
                        <div className="w-full mx-auto bg-white border border-slate-200/80 border-t-[3px] border-t-[#C30010] rounded-xl py-2.5 px-3 sm:px-5 shadow-[0_4px_14px_rgba(15,23,42,0.07)] ring-1 ring-[#C30010]/10 overflow-x-auto">
                            <div className="flex items-center justify-between min-w-[520px] px-2">
                                {steps.map((step, idx) => {
                                    const stepNum = idx + 1;
                                    const isCurrent = currentStep === stepNum;
                                    const isCompleted = completedSteps.includes(stepNum);

                                    return (
                                        <React.Fragment key={step.id}>
                                            <button
                                                type="button"
                                                onClick={() => onStepClick?.(stepNum)}
                                                className={`flex items-center gap-2 select-none transition-all duration-300 text-left ${isCurrent ? 'scale-[1.02]' : ''
                                                    } ${isCompleted || isCurrent ? 'cursor-pointer' : 'cursor-default'}`}
                                            >
                                                <div
                                                    className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px] border transition-all duration-300 ${isCurrent
                                                        ? 'bg-red-600 border-red-600 text-white shadow-md shadow-red-200'
                                                        : isCompleted
                                                            ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                                                            : 'bg-slate-50 border-slate-100 text-slate-400'
                                                        }`}
                                                >
                                                    {isCompleted ? <FiCheck className="text-xs stroke-[3]" /> : stepNum}
                                                </div>
                                                <div>
                                                    <span
                                                        className={`block text-[10px] font-bold uppercase tracking-wider ${isCurrent ? 'text-red-600' : isCompleted ? 'text-slate-800' : 'text-slate-400'
                                                            }`}
                                                    >
                                                        {step.label}
                                                    </span>
                                                    <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest">
                                                        {isCurrent ? 'Current Step' : isCompleted ? 'Completed' : 'Upcoming'}
                                                    </span>
                                                </div>
                                            </button>
                                            {idx < steps.length - 1 && (
                                                <div
                                                    className={`flex-1 h-0.5 mx-2 sm:mx-3 transition-all duration-500 ${isCompleted ? 'bg-emerald-200' : 'bg-slate-100'
                                                        }`}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Step content */}
                    <div className="bg-white border border-slate-100 rounded-2xl p-2 sm:p-3 shadow-md shadow-slate-100/30 animate-fade-in emp-update-wizard-content">
                        <div className="space-y-2">
                            {children}
                        </div>
                    </div>

                    {/* Navigation — sticky action buttons */}
                    <div className="fixed bottom-2 sm:bottom-2.5 left-0 right-0 z-40 pointer-events-none">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end items-center gap-3 pointer-events-auto">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={onPrevious}
                                    className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm font-bold text-[#C30010] bg-white border-2 border-[#C30010] hover:bg-red-50 transition shadow-md"
                                >
                                    <FiArrowLeft size={16} />
                                    <span>Previous</span>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={isLastStep ? onSave : onNext}
                                disabled={isLastStep ? isSaveDisabled : isNextDisabled}
                                className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm font-bold text-white transition shadow-md ${(isLastStep ? isSaveDisabled : isNextDisabled)
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-[#C30010] hover:bg-[#a0000d] shadow-red-100'
                                    }`}
                            >
                                <span>{isLastStep ? 'Save' : 'Next'}</span>
                                {isLastStep ? <FiSave size={16} /> : <FiArrowRight size={16} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .emp-update-wizard-content .space-y-6 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.5rem !important; }
                .emp-update-wizard-content .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.375rem !important; }
                .emp-update-wizard-content .space-y-3 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.25rem !important; }
                .emp-update-wizard-content .space-y-2 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.25rem !important; }
                .emp-update-wizard-content .space-y-1\\.5 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.125rem !important; }
                .emp-update-wizard-content .gap-6 { gap: 0.75rem !important; }
                .emp-update-wizard-content .gap-4 { gap: 0.5rem !important; }
                .emp-update-wizard-content .gap-3 { gap: 0.375rem !important; }
                .emp-update-wizard-content .p-6 { padding: 0.75rem !important; }
                .emp-update-wizard-content .p-5 { padding: 0.625rem !important; }
                .emp-update-wizard-content .p-4 { padding: 0.5rem !important; }
                .emp-update-wizard-content .px-5 { padding-left: 0.75rem !important; padding-right: 0.75rem !important; }
                .emp-update-wizard-content .py-4 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
                .emp-update-wizard-content .mt-6 { margin-top: 0.75rem !important; }
                .emp-update-wizard-content .mt-4 { margin-top: 0.5rem !important; }
                .emp-update-wizard-content .mb-6 { margin-bottom: 0.75rem !important; }
                .emp-update-wizard-content .mb-5 { margin-bottom: 0.625rem !important; }
                .emp-update-wizard-content .mb-4 { margin-bottom: 0.5rem !important; }
                .emp-update-wizard-content .pt-4 { padding-top: 0.5rem !important; }
                .emp-update-wizard-content .pt-3 { padding-top: 0.375rem !important; }
                .emp-update-wizard-content .min-h-\\[350px\\] { min-height: 220px !important; }
                .emp-update-wizard-content .w-36 { width: 7rem !important; }
                .emp-update-wizard-content .h-36 { height: 7rem !important; }
                .emp-wizard-section-gap { display: flex; flex-direction: column; gap: 0.75rem; }
                @media (min-width: 640px) { .emp-wizard-section-gap { gap: 0.875rem; } }
                .emp-wizard-section-gap-lg { gap: 1rem !important; }
                @media (min-width: 640px) { .emp-wizard-section-gap-lg { gap: 1.125rem !important; } }
                .emp-wizard-section-divider { border-top: 1px solid rgba(226, 232, 240, 0.9); margin: 0; }
                .emp-wizard-section-divider-lg {
                    border-top-color: rgba(203, 213, 225, 0.95);
                    margin: 0;
                }
            `}</style>
        </div>
    );
};

const WizardSectionActions = ({ onCancel, onSave, saveLabel = 'Save Changes' }) => (
    <div className="flex items-center justify-end gap-3 pt-3 mt-3">
        <button
            type="button"
            onClick={onCancel}
            className="px-4 sm:px-5 py-2 sm:py-2.5 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition shadow-sm"
        >
            Cancel
        </button>
        <button
            type="button"
            onClick={onSave}
            className="px-4 sm:px-5 py-2 sm:py-2.5 text-sm font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded-lg shadow-md shadow-red-100 transition flex items-center gap-2"
        >
            <FiSave className="hidden sm:block" size={16} />
            {saveLabel}
        </button>
    </div>
);

const WizardFormShell = ({ title, icon: Icon, children, inline = false }) => (
    <div className={`h-full ${inline ? 'border border-slate-100 rounded-xl p-2 sm:p-2.5 bg-white shadow-sm' : 'border-b border-slate-100 pb-2 last:border-b-0 last:pb-0'}`}>
        <h3 className="text-sm sm:text-base font-extrabold text-slate-800 tracking-tight mb-1.5 sm:mb-2 flex items-center gap-1.5">
            {Icon && (
                <span className="p-1 bg-[#ffebee] text-[#C30010] rounded-md">
                    <Icon size={15} />
                </span>
            )}
            {title}
        </h3>
        {children}
    </div>
);

// --- Main Component ---
export default function EmployeeDetails() {
    const navigate = useNavigate();
    const { user: globalUser, setUser: setGlobalUser } = useUser();

    // Derive roleId safely from globalUser
    const roleId = globalUser ? Number(globalUser.role_id || globalUser.user_role_id || 0) : 0;
    const isHROrAdmin = globalUser && [1].includes(roleId);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        profile_image: '',
        aadhaar_name: '', dob: null, blood_group: '', mobile: '', personal_email: '',
        address_permanent: '', address_communication: '',
        family_details: {}, // { father: {}, mother: {}, spouse: {} }
        joining_date: null, relieving_date: null, relieving_reason: '',
        aadhaar_number: '', aadhaar_file: '', pan_number: '', pan_file: '',
        academic_details: [], certificates: [], bank_details: [],
        community: '', caste: '', religion: '',
        languages_speak: [],
        health_conditions: '', insurance_opt: 'OPT-OUT',
        nominee_name: '', nominee_relationship: '', nominee_dob: null, nominee_phone: '',
        identity_details: [], // Initialize new field
        skills: [], // Initialize Skills
        role_history: [],
        previous_experience: [],
        performance_revision_reason: '',
        performance_revision_employee_reply: '',
        revised_rating: '',
        performance_revision_month: ''
    });

    const [performanceRevisionOpen, setPerformanceRevisionOpen] = useState(false);
    const [performanceRevisionSaving, setPerformanceRevisionSaving] = useState(false);

    const [promotionModal, setPromotionModal] = useState({
        open: false,
        newRole: '',
        startDate: '',
        prevRoleEndDate: '',
        prevRoleStartDate: ''
    });

    const [verificationStatus, setVerificationStatus] = useState({ PAN: 'idle', AADHAAR: 'idle' });
    const [selectedIdType, setSelectedIdType] = useState(''); // New state for dropdown
    const [editingIdentityType, setEditingIdentityType] = useState(null); // 'PAN' | 'AADHAAR' | null
    const [showIdentityAddForm, setShowIdentityAddForm] = useState(false);
    const [scannedData, setScannedData] = useState({ PAN: null, AADHAAR: null });
    const [identityModal, setIdentityModal] = useState({ open: false, type: null, file: null, preview: null });
    const [bankConfirmModal, setBankConfirmModal] = useState({ open: false, resolve: null });
    const scannerActiveRef = React.useRef(true);
    const [activeProfileTab, setActiveProfileTab] = useState('profile');
    const [activeView, setActiveView] = useState('attendance');
    const [hrNotes, setHrNotes] = useState([]);
    const [hrNotesLoading, setHrNotesLoading] = useState(false);
    const [lmsNotifications, setLmsNotifications] = useState([]);
    const [lmsNotificationsLoading, setLmsNotificationsLoading] = useState(false);
    const [ticketOverview, setTicketOverview] = useState([]);
    const [ticketClosureAnalytics, setTicketClosureAnalytics] = useState(null);
    const [ticketOverviewLoading, setTicketOverviewLoading] = useState(false);
    const [attendanceRow, setAttendanceRow] = useState(null);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [attendanceDetailEmp, setAttendanceDetailEmp] = useState(null);
    const [attendanceDetailData, setAttendanceDetailData] = useState([]);
    const [attendanceDetailLoading, setAttendanceDetailLoading] = useState(false);
    const attendanceExportTokenRef = React.useRef(null);
    const [leaveAnalytics, setLeaveAnalytics] = useState(null);
    const [leaveLoading, setLeaveLoading] = useState(false);
    const [summaryModalConfig, setSummaryModalConfig] = useState(null);
    const [performanceChart, setPerformanceChart] = useState({ categories: [], series: [] });
    const [performanceLoading, setPerformanceLoading] = useState(false);
    const [profileEmail, setProfileEmail] = useState('');
    const [handlingTimeUnit, setHandlingTimeUnit] = useState('Days');

    const now = new Date();
    const [attendanceYear, setAttendanceYear] = useState(now.getFullYear());
    const [attendanceMonth, setAttendanceMonth] = useState(now.getMonth() + 1);
    const [leaveYear, setLeaveYear] = useState(now.getFullYear());
    const [leaveMonth, setLeaveMonth] = useState(now.getMonth() + 1);
    const [performanceYear, setPerformanceYear] = useState(now.getFullYear());

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    const [editingSection, setEditingSection] = useState(null);
    const [tempData, setTempData] = useState({});
    const [errors, setErrors] = useState({});

    // Quick Job Role Edit States & Refs
    const [showJobRoleEditPopover, setShowJobRoleEditPopover] = useState(false);
    const [editedJobRole, setEditedJobRole] = useState('');
    const jobRolePopoverRef = useRef(null);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState(1);
    const [wizardCompletedSteps, setWizardCompletedSteps] = useState([]);
    const [wizardEditingRows, setWizardEditingRows] = useState({
        academic: new Set(),
        skills: new Set(),
        certificates: new Set(),
    });
    const [managers, setManagers] = useState([]);
    const [showBirthdayPopup, setShowBirthdayPopup] = useState(false);
    const [config, setConfig] = useState({
        boards: ['CBSE', 'ICSE', 'State Board', 'IB'],
        degrees: [],
        degrees_map: {
            'UG': ['B.E', 'B.Tech', 'B.Sc', 'B.Com', 'B.A', 'BCA', 'BBA', 'B.Arch', 'B.Pharma', 'B.Des', 'LLB'],
            'PG': ['M.E', 'M.Tech', 'M.Sc', 'M.Com', 'M.A', 'MCA', 'MBA', 'M.Arch', 'M.Pharma', 'LLM'],
            'Diploma': ['Diploma in Engineering', 'Polytechnic', 'D.Pharma', 'D.Ed'],
            'PhD': ['PhD', 'Doctorate'],
            '10th': ['All Subjects', 'General'],
            '12th': ['Science', 'Commerce', 'Arts', 'Vocational']
        },
        colleges: [],
        banks: [],
        religions: [],
        communities: [],
        castes: [],
        skill_categories: []
    });

    const getQueryParam = (name) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(name);
    };

    const isReadOnly = useMemo(() => {
        if (!globalUser) return true;
        const roleId = Number(globalUser.role_id || globalUser.user_role_id || 0);
        const currentUserId = String(globalUser.user_id || globalUser.sub || globalUser.id);
        const targetId = getQueryParam('user_id');

        if (roleId === 1) return false;
        if (!targetId || String(targetId) === currentUserId) return false;
        return true;
    }, [globalUser]);

    const [focusedScore, setFocusedScore] = useState(null); // Track focused score input index
    const [cropperImage, setCropperImage] = useState(null); // State for image to be cropped
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null); // State for full photo preview modal
    const [showRelievingReasonModal, setShowRelievingReasonModal] = useState(false); // State for relieving reason modal

    // Parse query params manually since we are using functional component patterns or native window
    const getProfileUserId = () => getQueryParam('user_id') || data?.user_id || globalUser?.user_id || globalUser?.id || globalUser?.sub;

    const getAttendanceTargetUserId = () => {
        if (attendanceRow?.user_id != null && attendanceRow.user_id !== '') {
            return attendanceRow.user_id;
        }
        if (data?.user_id != null && data.user_id !== '') {
            return data.user_id;
        }
        const queryId = getQueryParam('user_id');
        if (queryId) return queryId;
        return globalUser?.user_id ?? globalUser?.id ?? null;
    };

    const isAttendanceApiSuccess = (res) => res?.data?.status === true || res?.data?.status === 1;

    const isProfileSelf = () => {
        const profileUserId = getProfileUserId();
        const selfId = globalUser?.user_id || globalUser?.id || globalUser?.sub;
        return profileUserId && selfId && String(profileUserId) === String(selfId);
    };

    const getMonthDateRange = (year, month) => {
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        return { startDate, endDate };
    };

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return '—';
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}/${y}`;
    };

    const getGradeBadgeClass = (grade) => {
        const map = {
            Excellent: 'border-emerald-200 bg-emerald-50 text-emerald-700',
            Good: 'border-green-200 bg-green-50 text-green-700',
            Average: 'border-amber-200 bg-amber-50 text-amber-700',
            Poor: 'border-red-200 bg-red-50 text-red-700',
        };
        return map[grade] || 'border-gray-200 bg-gray-50 text-gray-700';
    };

    const buildLeaveDonutStyle = (chart) => {
        if (!Array.isArray(chart) || chart.length === 0) return 'conic-gradient(#f3f4f6 0 360deg)';
        const total = chart.reduce((sum, item) => sum + Number(item.y || 0), 0);
        if (total <= 0) return 'conic-gradient(#f3f4f6 0 360deg)';
        let current = 0;
        const stops = chart.map((item) => {
            const pct = (Number(item.y || 0) / total) * 100;
            const start = current;
            current += pct;
            return `${item.color || '#10b981'} ${start}% ${current}%`;
        });
        return `conic-gradient(${stops.join(', ')})`;
    };

    const getLeaveLegendItems = (chart) => {
        if (!Array.isArray(chart)) return [];
        return chart
            .filter((item) => Number(item.y || 0) > 0)
            .map((item) => ({
                label: item.name || 'Value',
                value: Number(item.y || 0),
                color: item.color || '#10b981',
            }));
    };

    const getLeaveChartLabels = (chart) => {
        const items = getLeaveLegendItems(chart);
        const total = items.reduce((sum, item) => sum + item.value, 0);
        if (total <= 0) return [];

        let currentAngle = -90;
        return items.map((item) => {
            const sweep = (item.value / total) * 360;
            const midAngle = currentAngle + sweep / 2;
            currentAngle += sweep;

            const angleRad = (midAngle * Math.PI) / 180;
            const anchorRadius = 102;
            const connectorRadius = 126;
            const labelRadius = 156;

            const anchorX = Math.cos(angleRad) * anchorRadius;
            const anchorY = Math.sin(angleRad) * anchorRadius;
            const connectorX = Math.cos(angleRad) * connectorRadius;
            const connectorY = Math.sin(angleRad) * connectorRadius;
            const labelX = Math.cos(angleRad) * labelRadius;
            const labelY = Math.sin(angleRad) * labelRadius;
            const isRight = labelX >= 0;

            return {
                ...item,
                percentage: ((item.value / total) * 100).toFixed(1),
                anchorX,
                anchorY,
                connectorX,
                connectorY,
                labelX,
                labelY,
                isRight,
            };
        });
    };

    const processPerformanceChartData = (res) => {
        const tickets = res?.data?.data?.data;
        if (!tickets) return { categories: [], series: [] };

        const months = Object.keys(tickets);
        const ticketArray = Object.values(tickets);
        const statusColors = {
            Tickets: '#10b981',
            'Late Punches': '#f59e0b',
            'Leaves Taken': '#ef4444',
            'PENDING VERIFICATION & CLOSURE': '#1f77b4',
            'IN PROCESS': '#ff7f0e',
            REOPEN: '#d62728',
            CLOSED: '#10b981',
            NEW: '#facc15',
            TESTING: '#20c997',
            'TESTING APPROVED': '#0ea5e9',
            'TESTING REJECTED': '#f43f5e',
            'PENDING CUSTOMER VERIFICATION & CONFIRMATION': '#8b5cf6',
        };
        const statuses = [...new Set(ticketArray.flatMap((obj) => Object.keys(obj).filter((k) => k !== 'month' && k !== 'year')))];
        const series = statuses.map((status) => ({
            name: status,
            data: months.map((m) => {
                const statusData = tickets[m] && tickets[m][status];
                if (!statusData) return 0;
                return Object.values(statusData || {}).reduce((sum, v) => sum + (v.count ?? 0), 0);
            }),
            stack: 'Ticket',
            color: statusColors[status],
        }));
        return { categories: months, series };
    };

    const buildTicketSummaryFromChart = (res) => {
        const tickets = res?.data?.data?.data;
        if (!tickets) return [];
        const statusTotals = {};
        Object.values(tickets).forEach((monthData) => {
            Object.entries(monthData).forEach(([status, statusData]) => {
                if (status === 'month' || status === 'year') return;
                const count = Object.values(statusData || {}).reduce((sum, v) => sum + Number(v.count ?? 0), 0);
                statusTotals[status] = (statusTotals[status] || 0) + count;
            });
        });
        return Object.entries(statusTotals)
            .filter(([, count]) => count > 0)
            .map(([label, count]) => ({ label, status_name: label, count }));
    };

    const todayDate = format(new Date(), 'dd/MM/yyyy');

    const handleVerifyBank = async (idx) => {
        const bank = tempData.bank_details[idx];
        if (!bank.account_number || !bank.ifsc) {
            toast.error("Enter Account Number & IFSC first");
            return;
        }

        const loadToast = toast.loading("Please wait, bank validation underway...");
        try {
            // Call the new backend endpoint
            const res = await api.post('/employee/verify-bank', {
                account_number: bank.account_number,
                ifsc: bank.ifsc,
                name: bank.holder_name
            });

            if (res.data.status) {
                if (res.data.verified) {
                    handleDeepChange('bank_details', idx, 'verified_name', res.data.registered_name);
                    handleDeepChange('bank_details', idx, 'verification_status', 'Verified');
                    // Auto-fetch/update name as requested
                    handleDeepChange('bank_details', idx, 'holder_name', res.data.registered_name);

                    toast.success(`Verified Successfully! Name: ${res.data.registered_name}`, { id: loadToast });
                } else {
                    handleDeepChange('bank_details', idx, 'verification_status', 'Failed');
                    toast.error("Bank Verification Failed (Penny Drop Rejected)", { id: loadToast });
                }
            } else {
                toast.error(res.data.message || "Verification Request Failed", { id: loadToast });
            }
        } catch (e) {
            console.error(e);
            toast.error("Verification Error: " + (e.response?.data?.messages?.error || e.message), { id: loadToast });
        }
    };

    // Persistence: Load unsaved data on mount
    useEffect(() => {
        const saved = localStorage.getItem('emp_unsaved_data');
        if (saved && !isReadOnly) {
            localStorage.removeItem('emp_unsaved_data');
        }
        fetchConfig();
        fetchDetails();
        fetchHrNotes();
        fetchLmsNotifications();
        fetchManagers();
        // Keep sidebar badge fresh for newly published notes
        const hrNotesInterval = setInterval(() => fetchHrNotes({ silent: true }), 60000);
        const lmsInterval = setInterval(() => fetchLmsNotifications({ silent: true }), 60000);
        return () => { clearInterval(hrNotesInterval); clearInterval(lmsInterval); };
    }, []);

    useEffect(() => {
        if (globalUser && activeView === 'attendance') {
            fetchAttendanceReport();
            fetchLeaveAnalytics();
        }
    }, [globalUser, activeView, attendanceYear, attendanceMonth, leaveYear, leaveMonth, data.user_id]);

    useEffect(() => {
        if (attendanceDetailEmp) {
            fetchAttendanceDetailLogs(attendanceDetailEmp);
        }
    }, [attendanceDetailEmp, attendanceYear, attendanceMonth]);

    useEffect(() => {
        if (globalUser && activeView === 'my-performance') {
            fetchPerformanceChart();
            fetchTicketOverview();
        }
    }, [globalUser, activeView, performanceYear, data.user_id]);

    useEffect(() => {
        if (globalUser && activeView === 'git-code-performance') {
            resolveProfileEmail();
        }
    }, [globalUser, activeView, data.user_id]);

    useEffect(() => {
        const unread = Number(data.performance_revision_unread) || 0;
        if (!globalUser || activeView !== 'performance-revision' || unread <= 0) return;

        const markSeen = async () => {
            try {
                const profileUserId = data.user_id || getQueryParam('user_id') || globalUser?.user_id || globalUser?.id;
                await api.post('/employee/performance-revision-mark-seen', { user_id: profileUserId });
                setData((prev) => ({ ...prev, performance_revision_unread: 0 }));
                window.dispatchEvent(new CustomEvent('perf-revision-alerts-changed'));
            } catch (e) {
                console.error('Failed to mark performance revision as seen', e);
            }
        };
        markSeen();
    }, [globalUser, activeView, data.user_id, data.performance_revision_unread]);

    // Keep published, applicable HR notes in sync for the profile sidebar view
    useEffect(() => {
        if (!globalUser) return;
        fetchHrNotes();
        const onHrNotesChanged = () => fetchHrNotes();
        window.addEventListener('hr-notes-changed', onHrNotesChanged);
        const interval = setInterval(() => fetchHrNotes({ silent: true }), 30000);
        return () => {
            window.removeEventListener('hr-notes-changed', onHrNotesChanged);
            clearInterval(interval);
        };
    }, [globalUser]);

    // Keep LMS test assignment notifications in sync
    useEffect(() => {
        if (!globalUser) return;
        fetchLmsNotifications();
        const onLmsNotifsChanged = () => fetchLmsNotifications();
        window.addEventListener('lms-notifications-changed', onLmsNotifsChanged);
        const interval = setInterval(() => fetchLmsNotifications({ silent: true }), 30000);
        return () => {
            window.removeEventListener('lms-notifications-changed', onLmsNotifsChanged);
            clearInterval(interval);
        };
    }, [globalUser]);

    const fetchManagers = async () => {
        try {
            const res = await api.get('/hrms/employee-names');
            if (res.data.status) {
                const names = res.data.data.map(u => `${u.first_name} ${u.last_name}`);
                setManagers(names);
            }
        } catch (error) {
            console.error("Failed to fetch employee list for managers", error);
        }
    };

    const fetchConfig = async () => {
        try {
            const res = await api.get('/employee/config');
            if (res.data.status) setConfig(res.data.data);
        } catch (error) {
            console.error("Failed to fetch config", error);
        }
    };

    // Click outside handler for Job Role Edit Popover
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (jobRolePopoverRef.current && !jobRolePopoverRef.current.contains(event.target)) {
                setShowJobRoleEditPopover(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Save handler for quick job role edit
    const handleSaveJobRoleQuickEdit = async () => {
        const loadingToast = toast.loading('Saving official job role...');
        try {
            const payload = {
                user_id: data.user_id,
                official_job_role: editedJobRole
            };
            const res = await api.post('/employee/details', payload);
            if (res.data.status) {
                toast.success('Official Job Role updated successfully', { id: loadingToast });
                setShowJobRoleEditPopover(false);
                await fetchDetails(true);
            } else {
                toast.error(res.data.message || 'Failed to save official job role', { id: loadingToast });
            }
        } catch (error) {
            console.error("Save Error", error);
            toast.error('Failed to save official job role', { id: loadingToast });
        }
    };

    // Save handler for quick relieving reason edit
    const handleSaveRelievingReason = async (reasonToSave) => {
        const loadingToast = toast.loading('Saving reason for relieving...');
        try {
            const payload = {
                user_id: data.user_id,
                relieving_reason: reasonToSave !== undefined ? reasonToSave : (data.relieving_reason || '')
            };
            const res = await api.post('/employee/details', payload);
            if (res.data.status) {
                toast.success('Reason for relieving saved successfully', { id: loadingToast });
                await fetchDetails(true);
            } else {
                toast.error(res.data.message || 'Failed to save reason for relieving', { id: loadingToast });
            }
        } catch (error) {
            console.error("Save Error", error);
            toast.error('Failed to save reason for relieving', { id: loadingToast });
        }
    };

    // Persistence: Save unsaved data on change
    useEffect(() => {
        if (isReadOnly) return; // Don't save to local storage in read-only mode
        if (editingSection && Object.keys(tempData).length > 0) {
            localStorage.setItem('emp_unsaved_data', JSON.stringify({ editingSection, tempData }));
        } else {
            localStorage.removeItem('emp_unsaved_data');
        }
    }, [editingSection, tempData, isReadOnly]);

    const fetchDetails = async (forceSyncTempData = false) => {
        setLoading(true);
        try {
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/details?user_id=${targetId}` : `/employee/details`;

            // Use axios directly as imported, or api if preferred. Using axios to match existing patterns in file if any.
            // Actually the file uses axios import.
            const res = await api.get(url);

            if (res.data.status && res.data.data) {
                const fetchedData = res.data.data;

                // Birthday check logic
                if (fetchedData.dob) {
                    const dob = new Date(fetchedData.dob);
                    const today = new Date();
                    if (dob.getMonth() === today.getMonth() && dob.getDate() === today.getDate()) {
                        setShowBirthdayPopup(true);
                    }
                }

                setData(fetchedData);
                // Only overwrite tempData if we don't have unsaved local changes,
                // unless the caller explicitly wants to re-sync after a save.
                if (forceSyncTempData || !localStorage.getItem('emp_unsaved_data')) {
                    setTempData(fetchedData);
                }



            } else {
                if (res.data.messages?.error === 'You are not authorized to view this profile') {
                    toast.error('You are not authorized to view this profile');
                    // perform navigation if needed, but navigate is not defined in this scope? 
                    // Check if useNavigate is hook used? It acts as a component, so yes.
                }
            }
        } catch (error) {
            console.error("Failed to fetch details", error);
            if (error.response && error.response.status === 403) {
                toast.error('You are not authorized to view this profile');
            } else {
                toast.error('Failed to load employee details');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchHrNotes = async ({ silent = false } = {}) => {
        const targetId = getProfileUserId() || globalUser?.user_id || globalUser?.id;
        if (!targetId) {
            setHrNotes([]);
            return;
        }
        if (!silent) setHrNotesLoading(true);
        try {
            const res = await api.get(`/hrms/notes/active?user_id=${targetId}`);
            if (res.data.status && res.data.data) {
                // Same source as HRMS Employee Notes tab: published notes applicable to current user
                const recentNotes = Array.isArray(res.data.data.recent) ? res.data.data.recent : [];
                const popupNote = res.data.data.popup ? [res.data.data.popup] : [];
                const combinedNotes = [...popupNote, ...recentNotes];
                const uniqueNotes = combinedNotes.filter((note, index, allNotes) =>
                    index === allNotes.findIndex((item) => String(item.id) === String(note.id))
                );
                // Newest first for profile readability
                uniqueNotes.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
                setHrNotes(uniqueNotes);
            } else {
                setHrNotes([]);
            }
        } catch (error) {
            console.error('Failed to fetch HR notes', error);
            if (!silent) setHrNotes([]);
        } finally {
            if (!silent) setHrNotesLoading(false);
        }
    };

    const fetchLmsNotifications = async ({ silent = false } = {}) => {
        const targetId = getProfileUserId() || globalUser?.user_id || globalUser?.id;
        if (!targetId) {
            setLmsNotifications([]);
            return;
        }
        if (!silent) setLmsNotificationsLoading(true);
        try {
            const res = await api.get(`/assessment/lms-notifications?user_id=${targetId}`);
            if (res.data.status) {
                const rawNotifs = Array.isArray(res.data.notifications) ? res.data.notifications : [];
                setLmsNotifications(rawNotifs);
            } else {
                setLmsNotifications([]);
            }
        } catch (error) {
            console.error('Failed to fetch LMS notifications', error);
            if (!silent) setLmsNotifications([]);
        } finally {
            if (!silent) setLmsNotificationsLoading(false);
        }
    };

    const handleReadLmsNotification = async (notif) => {
        if (!notif?.id || notif.read_status === 'Read') return;
        try {
            await api.post('/assessment/lms-notifications/read', { id: notif.id });
        } catch (error) {
            console.error('Error marking LMS notification read:', error);
        }
        setLmsNotifications((prev) =>
            prev.map((n) => (String(n.id) === String(notif.id) ? { ...n, read_status: 'Read' } : n))
        );
    };

    const handleAcknowledgeHrNote = async (note) => {
        if (!note?.id) return;
        try {
            const res = await api.post('/hrms/notes/acknowledge', { note_id: note.id });
            if (res.data?.status) {
                toast.success('Note acknowledged successfully');
                setHrNotes((prev) =>
                    prev.map((n) => (String(n.id) === String(note.id) ? { ...n, read_status: 'Acknowledged' } : n))
                );
            }
        } catch (error) {
            console.error('Acknowledge note error:', error);
            toast.error('Error acknowledging note');
        }
    };

    const handleMarkHrNoteRead = async (note) => {
        if (!note?.id) return;
        if (note.read_status !== 'Unread' && note.read_status !== 'Pending') return;
        try {
            await api.post('/hrms/notes/read', { note_id: note.id });
            setHrNotes((prev) =>
                prev.map((n) => (String(n.id) === String(note.id) ? { ...n, read_status: 'Read' } : n))
            );
        } catch (error) {
            console.error('Error marking note read:', error);
        }
    };

    const getHrNoteCategoryClass = (category) => {
        const c = String(category || '').toLowerCase();
        if (c === 'celebration') return 'bg-pink-50 text-pink-700 border-pink-100';
        if (c === 'event') return 'bg-purple-50 text-purple-700 border-purple-100';
        if (c === 'notification') return 'bg-amber-50 text-amber-700 border-amber-100';
        return 'bg-teal-50 text-teal-700 border-teal-100'; // memo / default
    };

    const resolveProfileEmail = async () => {
        const profileUserId = getProfileUserId();
        if (!profileUserId) return;

        if (isProfileSelf()) {
            setProfileEmail(globalUser?.email || '');
            return;
        }

        if (!isHROrAdmin) {
            setProfileEmail('');
            return;
        }

        try {
            const res = await api.get('/hrms/employee-list');
            if (res.data?.status && Array.isArray(res.data.data)) {
                const match = res.data.data.find((emp) => String(emp.user_id) === String(profileUserId));
                setProfileEmail(match?.email || '');
            }
        } catch (error) {
            console.error('Failed to resolve profile email', error);
            setProfileEmail('');
        }
    };

    const fetchAttendanceReport = async () => {
        const profileUserId = getAttendanceTargetUserId();
        if (!profileUserId) return;

        const { startDate, endDate } = getMonthDateRange(attendanceYear, attendanceMonth);
        setAttendanceLoading(true);
        try {
            const res = await api.get('/attendance/consolidated-report', {
                params: { startDate, endDate, search: '', grade: 'All' },
            });
            if (isAttendanceApiSuccess(res)) {
                const rows = Array.isArray(res.data.data) ? res.data.data : [];
                const row = rows.find((item) => String(item.user_id) === String(profileUserId)) || null;
                setAttendanceRow(row);
            } else {
                setAttendanceRow(null);
            }
        } catch (error) {
            console.error('Failed to fetch attendance report', error);
            setAttendanceRow(null);
        } finally {
            setAttendanceLoading(false);
        }
    };

    const fetchAttendanceDetailLogs = async (targetEmp = attendanceDetailEmp) => {
        if (targetEmp?.id == null || targetEmp.id === '') return;

        const { startDate, endDate } = getMonthDateRange(attendanceYear, attendanceMonth);
        const statusFilter = targetEmp?.statusFilter || 'ALL';
        setAttendanceDetailLoading(true);
        try {
            if (ATTENDANCE_STATUS_DATES_API_MAP[statusFilter]) {
                const res = await api.get('/attendance/status-dates', {
                    params: {
                        user_id: targetEmp.id,
                        status: ATTENDANCE_STATUS_DATES_API_MAP[statusFilter],
                        startDate,
                        endDate,
                    },
                });
                if (isAttendanceApiSuccess(res)) {
                    setAttendanceDetailData((Array.isArray(res.data.data) ? res.data.data : []).map(mapStatusDatesApiRow));
                } else {
                    setAttendanceDetailData([]);
                }
            } else {
                const res = await api.get('/attendance/detailed-report', {
                    params: {
                        startDate,
                        endDate,
                        page: 1,
                        limit: 100,
                        search: '',
                        status: 'All',
                        user_id: targetEmp.id,
                    },
                });
                if (isAttendanceApiSuccess(res)) {
                    const rows = Array.isArray(res.data.data) ? res.data.data : [];
                    setAttendanceDetailData(
                        statusFilter === 'ALL'
                            ? rows
                            : getAttendanceDetailRowsForDisplay(rows, statusFilter)
                    );
                } else {
                    setAttendanceDetailData([]);
                }
            }
        } catch (error) {
            console.error('Failed to fetch attendance detail logs', error);
            toast.error('Failed to load attendance logs');
            setAttendanceDetailData([]);
        } finally {
            setAttendanceDetailLoading(false);
        }
    };

    const handleViewAttendanceDetails = () => {
        const targetUserId = getAttendanceTargetUserId();
        if (!targetUserId) return;
        const empName = attendanceRow?.name
            || data.aadhaar_name
            || `${data.first_name || ''} ${data.last_name || ''}`.trim()
            || 'Employee';
        setAttendanceDetailData([]);
        setAttendanceDetailEmp({ id: targetUserId, name: empName, statusFilter: 'ALL' });
    };

    const handleAttendanceStatusClick = (statusFilter) => {
        const targetUserId = data?.user_id || getAttendanceTargetUserId();
        if (!targetUserId) return;

        const empName = attendanceRow?.name
            || data.aadhaar_name
            || `${data.first_name || ''} ${data.last_name || ''}`.trim()
            || 'Employee';
        setAttendanceDetailData([]);
        setAttendanceDetailEmp({ id: targetUserId, name: empName, statusFilter });
    };

    const handleExportAttendanceDetailExcel = async () => {
        const snapEmp = attendanceDetailEmp;
        const snapData = getAttendanceDetailRowsForDisplay(
            attendanceDetailData || [],
            attendanceDetailEmp?.statusFilter || 'ALL'
        );
        const { startDate, endDate } = getMonthDateRange(attendanceYear, attendanceMonth);

        if (!snapEmp || snapData.length === 0) {
            toast.error('No data to export');
            return;
        }

        const exportToken = `${Date.now()}_${snapEmp.id}`;
        attendanceExportTokenRef.current = exportToken;
        const loadingToast = toast.loading('Generating Detailed Excel...');

        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Detailed Logs');

            worksheet.mergeCells('A1:J1');
            const titleCell = worksheet.getCell('A1');
            titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

            const filterMeta = getAttendanceFilterMeta(attendanceDetailEmp?.statusFilter || 'ALL');

            worksheet.mergeCells('A2:J2');
            const subtitleCell = worksheet.getCell('A2');
            subtitleCell.value = `${filterMeta.label}: ${snapEmp.name}`;
            subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            subtitleCell.font = { name: 'Arial', size: 12, bold: true };

            worksheet.mergeCells('A3:J3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Period: ${formatDateDDMMYYYY(startDate)} — ${formatDateDDMMYYYY(endDate)}`;
            dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
            dateCell.font = { name: 'Arial', size: 10, italic: true };

            worksheet.addRow([]);
            const headerRow = worksheet.addRow([
                '#', 'Date', 'In', 'Out', 'Permission', 'Late/Deviation', 'OT', 'Production', 'Punches', 'Status',
            ]);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            snapData.forEach((d, idx) => {
                const prod = calculateAttendanceProduction(d.check_in, d.check_out);
                const late = (d.lateVal !== undefined && d.lateVal !== null) ? d.lateVal : calculateAttendanceLate(d.check_in);
                const ot = (d.otVal !== undefined && d.otVal !== null) ? d.otVal : calculateAttendanceOvertime(d.check_out);
                const per = (d.permission_start_time || d.permission_end_time)
                    ? `${d.permission_start_time || ''} - ${d.permission_end_time || ''}`
                    : '-';

                const row = worksheet.addRow([
                    idx + 1,
                    formatDateDDMMYYYY(d.attendance_date),
                    d.check_in || '--:--',
                    d.check_out || '--:--',
                    per,
                    late,
                    ot,
                    prod,
                    d.punch_records || 'No Logs',
                    (d.status === 'LOP' ? 'ON_LEAVE' : (d.status || 'ABSENT')),
                ]);

                row.eachCell((cell, colNumber) => {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    if (colNumber === 10) {
                        const status = cell.value;
                        if (status === 'PRESENT' || status === 'WORK_FROM_HOME') {
                            cell.font = { color: { argb: 'FF059669' }, bold: true };
                        } else if (status === 'ABSENT' || status === 'NOT MARKED') {
                            cell.font = { color: { argb: 'FFDC2626' }, bold: true };
                        } else if (status === 'ON_LEAVE' || status === 'LOP' || status === 'LEAVE' || status === 'HALF_DAY_LEAVE') {
                            cell.font = { color: { argb: 'FF7C3AED' }, bold: true };
                        } else if (status === 'LATE') {
                            cell.font = { color: { argb: 'FFB45309' }, bold: true };
                        }
                    }
                });
            });

            worksheet.columns.forEach((column) => { column.width = column.header ? column.header.length + 10 : 15; });
            const buffer = await workbook.xlsx.writeBuffer();

            if (attendanceExportTokenRef.current !== exportToken) {
                toast.dismiss(loadingToast);
                return;
            }

            saveAs(new Blob([buffer]), `${snapEmp.name}_Detailed_Logs_${formatDateDDMMYYYY(startDate)}.xlsx`);
            toast.success('Excel Generated!');
        } catch (error) {
            console.error(error);
            toast.error('Failed to generate Excel');
        } finally {
            toast.dismiss(loadingToast);
        }
    };

    const fetchLeaveAnalytics = async () => {
        const profileUserId = data?.user_id || getProfileUserId();
        if (!profileUserId) return;

        setLeaveLoading(true);
        try {
            const res = await api.get('/hrms/leave-analytics', {
                params: {
                    user_id: profileUserId,
                    year: leaveYear,
                    month: leaveMonth === 'all' ? '' : leaveMonth,
                },
            });
            if (res.data?.status) {
                setLeaveAnalytics(res.data.data);
            } else {
                setLeaveAnalytics(null);
            }
        } catch (error) {
            console.error('Failed to fetch leave analytics', error);
            setLeaveAnalytics(null);
        } finally {
            setLeaveLoading(false);
        }
    };

    const handleStatBoxClick = async (category, title) => {
        const profileUserId = data?.user_id || getProfileUserId();
        if (!profileUserId) return;

        setSummaryModalConfig({
            isOpen: true,
            title,
            category,
            data: [],
            loading: true
        });

        try {
            const res = await api.get('/hrms/summary-details', {
                params: {
                    user_id: profileUserId,
                    year: leaveYear,
                    month: leaveMonth === 'all' ? '' : leaveMonth,
                    category
                }
            });

            if (res.data?.status && Array.isArray(res.data.data)) {
                setSummaryModalConfig({
                    isOpen: true,
                    title,
                    category,
                    data: res.data.data,
                    loading: false
                });
            } else {
                setSummaryModalConfig({
                    isOpen: true,
                    title,
                    category,
                    data: [],
                    loading: false
                });
            }
        } catch (err) {
            console.error('Error fetching summary details:', err);
            setSummaryModalConfig({
                isOpen: true,
                title,
                category,
                data: [],
                loading: false
            });
        }
    };

    const handleAccountSummaryExport = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Account Summary');

            worksheet.mergeCells('A1:B1');
            const titleCell = worksheet.getCell('A1');
            titleCell.value = `${data?.first_name || 'Employee'} ${data?.last_name || ''} - Account Summary`.trim();
            titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FF047857' } };
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

            worksheet.mergeCells('A2:B2');
            const periodCell = worksheet.getCell('A2');
            const monthName = leaveMonth === 'all' ? 'All Months' : monthNames[Number(leaveMonth) - 1] || leaveMonth;
            periodCell.value = `Year: ${leaveYear} | Period: ${monthName}`;
            periodCell.alignment = { horizontal: 'center' };
            periodCell.font = { italic: true };

            worksheet.addRow([]);

            const headerRow = worksheet.addRow(['Metric / Category', 'Value']);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF047857' } };
                cell.alignment = { horizontal: 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            const stats = leaveAnalytics?.stats || {};
            const dataRows = [
                ['Total Leave Taken', stats.total_leave_count || 0],
                ['Scheduled Leave Taken', stats.taken || 0],
                ['Unscheduled Leave Taken', stats.unscheduled || 0],
                ['Pending Requests', stats.request || 0],
                ['Late Punches', stats.total_late || 0],
                ['Remaining Balance', stats.current_balance || 0],
                ['Paid Permission Hours', `${stats.paid_permission || 0}h`],
                ['Loss of Pay Days', stats.loss_of_pay || 0],
            ];

            dataRows.forEach((row) => {
                const r = worksheet.addRow(row);
                r.getCell(1).alignment = { horizontal: 'left' };
                r.getCell(2).alignment = { horizontal: 'right' };
            });

            worksheet.columns = [{ width: 30 }, { width: 15 }];

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `${data?.first_name || 'Employee'}_Leave_Summary_${leaveYear}_${monthName}.xlsx`);
            toast.success('Leave Summary Exported!');
        } catch (error) {
            console.error(error);
            toast.error('Failed to export summary');
        }
    };

    const fetchPerformanceChart = async () => {
        const profileUserId = getProfileUserId();
        if (!profileUserId || !globalUser) return;

        setPerformanceLoading(true);
        try {
            const endpoint = isProfileSelf() ? '/ticket/chartData' : '/ticket/employee-self-chart-data';
            const res = await api.get(endpoint, {
                params: { user_id: profileUserId, selectedYears: [performanceYear] },
            });
            setPerformanceChart(processPerformanceChartData(res));
        } catch (error) {
            console.error('Failed to fetch performance chart', error);
            setPerformanceChart({ categories: [], series: [] });
        } finally {
            setPerformanceLoading(false);
        }
    };

    const fetchTicketOverview = async () => {
        const profileUserId = getProfileUserId();
        if (!profileUserId || !globalUser) return;

        const currentYear = new Date().getFullYear();
        const viewerId = globalUser.user_id || globalUser.id || globalUser.sub;
        const chartParams = isProfileSelf()
            ? { user_id: profileUserId, selectedYears: [currentYear] }
            : { user_id: viewerId, selectedYears: [currentYear], selectedEmployees: [profileUserId] };

        setTicketOverviewLoading(true);
        try {
            const requests = [
                api.get('/ticket/chartData', { params: chartParams }),
                isProfileSelf()
                    ? api.get('/ticket/status-summary')
                    : api.get('/ticket/employee-self-status-summary', { params: { user_id: profileUserId } }),
            ];

            if (isProfileSelf()) {
                requests.push(api.get('/hrms/analytics', { params: { year: currentYear } }));
            } else if (isHROrAdmin) {
                requests.push(api.get('/ticket/getAnalyticsData', {
                    params: { selectedEmployees: [profileUserId], selectedYears: [currentYear] },
                }));
            }

            const results = await Promise.allSettled(requests);
            const chartRes = results[0].status === 'fulfilled' ? results[0].value : null;

            if (results[1]?.status === 'fulfilled' && results[1].value.data?.status) {
                setTicketOverview(Array.isArray(results[1].value.data.data) ? results[1].value.data.data : []);
            } else if (chartRes) {
                setTicketOverview(buildTicketSummaryFromChart(chartRes));
            } else {
                setTicketOverview([]);
            }

            if (isProfileSelf() && results[2]?.status === 'fulfilled' && results[2].value.data?.status) {
                const monthlyMy = results[2].value.data.data?.monthly_my || [];
                const currentMonthIdx = new Date().getMonth();
                const currentAvg = monthlyMy[currentMonthIdx] ?? 0;
                const prevAvg = currentMonthIdx > 0 ? (monthlyMy[currentMonthIdx - 1] ?? 0) : 0;
                setHandlingTimeUnit('Hours');
                setTicketClosureAnalytics({
                    current_month_avg: currentAvg,
                    change: Number((currentAvg - prevAvg).toFixed(1)),
                    trends: monthlyMy.slice(0, currentMonthIdx + 1).map((avg, idx) => ({
                        month: idx + 1,
                        month_label: monthNamesShort[idx],
                        avg_days: avg,
                    })),
                });
            } else if (!isProfileSelf() && results[1]?.status === 'fulfilled') {
                setHandlingTimeUnit('Days');
                setTicketClosureAnalytics(results[1].value.data?.closure_analytics || null);
            } else {
                setHandlingTimeUnit('Days');
                setTicketClosureAnalytics(null);
            }
        } catch (error) {
            console.error('Failed to fetch ticket overview', error);
            setTicketOverview([]);
            setTicketClosureAnalytics(null);
        } finally {
            setTicketOverviewLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        const toastId = toast.loading('Generating PDF...');

        try {
            const url = targetId
                ? `/employee/export-profile-pdf?user_id=${targetId}`
                : `/employee/export-profile-pdf`;

            const response = await api.get(url, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `Employee_Profile_${data.first_name || 'User'}.pdf`);
            document.body.appendChild(link);
            link.remove();

            toast.success('PDF Downloaded!', { id: toastId });
        } catch (error) {
            console.error("PDF Generation Error", error);
            toast.error('Failed to generate PDF.', { id: toastId });
        }
    };

    const startEdit = (section) => {
        if (isReadOnly) return;
        setTempData(JSON.parse(JSON.stringify(data)));
        setErrors({});
        setEditingSection(section);
    };

    const cancelEdit = () => {
        setEditingSection(null);
        setTempData({});
        setErrors({});
    };

    const showValidationToast = (validationErrors) => {
        const errorList = Object.values(validationErrors);
        toast((t) => (
            <div className="flex flex-col gap-1.5 min-w-[200px]">
                <div className="flex items-center gap-2 text-red-600 font-bold border-b border-red-100 pb-1 mb-1">
                    <FiAlertTriangle size={16} />
                    <span>Validation Errors</span>
                </div>
                <ul className="list-disc pl-4 text-[11px] text-gray-700 space-y-1">
                    {errorList.slice(0, 5).map((msg, i) => (
                        <li key={i} className="leading-tight">{msg}</li>
                    ))}
                    {errorList.length > 5 && (
                        <li className="text-gray-400 italic">...and {errorList.length - 5} more errors</li>
                    )}
                </ul>
                <div className="mt-2 text-[10px] text-gray-400 bg-gray-50 p-1.5 rounded text-center">
                    Please correct the highlighted fields above
                </div>
            </div>
        ), {
            duration: 6000,
            icon: '⚠️',
            style: {
                borderRadius: '12px',
                background: '#fff',
                color: '#333',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
            }
        });
    };

    const syncIdentityProofState = (profileData) => {
        setSelectedIdType(resolveIdentityProofType(profileData));
        setVerificationStatus(buildIdentityVerificationStatus(profileData));
    };

    const openUpdateWizard = () => {
        if (isReadOnly) return;
        const wizardData = JSON.parse(JSON.stringify(data));
        setTempData(wizardData);
        syncIdentityProofState(wizardData);
        setErrors({});
        setWizardOpen(true);
        setWizardStep(1);
        setWizardCompletedSteps([]);
        setEditingIdentityType(null);
        setShowIdentityAddForm(false);
        setWizardEditingRows({ academic: new Set(), skills: new Set(), certificates: new Set() });
    };

    const openPerformanceRevision = () => {
        if (isReadOnly) return;
        setPerformanceRevisionOpen(true);
    };

    const handlePerformanceRevisionSave = async (form) => {
        const isAdmin = roleId === 1;
        const targetUserId = data.user_id || getProfileUserId();

        // Prepare the payload for individual performance revision save
        const payload = {
            id: form.id,
            user_id: targetUserId,
            revision_mmyy: form.revision_month,
            performance_revision_reason: form.reason,
            performance_revision_employee_reply: form.employee_reply,
            attachment_path: form.attachment_path,
            attachment_names: form.attachment_names,
        };

        if (isAdmin) {
            payload.employee_id = form.employee_id || data.employee_id;
            payload.employee_name = form.employee_name || data.aadhaar_name;
            payload.role = form.role || data.role;
        }

        setPerformanceRevisionSaving(true);
        const loadingToast = toast.loading('Saving performance revision...');
        try {
            await api.post('/employee/performance-revision', payload);
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/details?user_id=${targetId}` : '/employee/details';
            const res = await api.get(url);
            if (res.data?.status && res.data?.data) {
                setData(res.data.data);
            }
            window.dispatchEvent(new CustomEvent('perf-revision-alerts-changed'));
            toast.success('Performance revision saved', { id: loadingToast });
            return true;
        } catch (error) {
            console.error('Performance revision save failed', error);
            toast.error('Save failed. Please try again.', { id: loadingToast });
            return false;
        } finally {
            setPerformanceRevisionSaving(false);
        }
    };

    const handlePerformanceRevisionDelete = async (id) => {
        const targetUserId = data.user_id || getProfileUserId();
        const loadingToast = toast.loading('Deleting performance revision...');
        try {
            await api.post('/employee/delete-performance-revision', {
                id,
                user_id: targetUserId,
            });
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/details?user_id=${targetId}` : '/employee/details';
            const res = await api.get(url);
            if (res.data?.status && res.data?.data) {
                setData(res.data.data);
            }
            window.dispatchEvent(new CustomEvent('perf-revision-alerts-changed'));
            toast.success('Performance revision deleted', { id: loadingToast });
            return true;
        } catch (error) {
            console.error('Delete failed', error);
            toast.error('Delete failed. Please try again.', { id: loadingToast });
            return false;
        }
    };

    const handlePerformanceRevisionUpload = async (file) => {
        if (!file) return null;
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'performance_revision');
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/upload?user_id=${targetId}` : '/employee/upload';
            const res = await api.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                return {
                    path: res.data.path,
                    filename: res.data.filename || file.name,
                };
            }
            toast.error('File upload failed');
            return null;
        } catch (e) {
            console.error('Upload error', e);
            toast.error('File upload error');
            return null;
        }
    };

    const getPerformanceRevisionProfile = () => ({
        employee_id: data.employee_id || data.emp_id || data.unique_id || '',
        employee_name: data.aadhaar_name || '',
        role: data.role || data.official_job_role || data.job_role || '',
        reason: data.performance_revision_reason || '',
        employee_reply: data.performance_revision_employee_reply || '',
        revised_salary: data.revised_rating || '',
        revision_month: data.performance_revision_month || '',
    });

    useEffect(() => {
        if (!wizardOpen || wizardStep !== 2) return;
        const hasIdentityData = Boolean(
            tempData.aadhaar_number || tempData.aadhaar_file || tempData.pan_number || tempData.pan_file
        );
        if (!selectedIdType && hasIdentityData) {
            syncIdentityProofState(tempData);
        }
    }, [
        wizardOpen,
        wizardStep,
        selectedIdType,
        tempData.aadhaar_number,
        tempData.aadhaar_file,
        tempData.pan_number,
        tempData.pan_file,
    ]);

    const closeUpdateWizard = () => {
        setWizardOpen(false);
        setWizardStep(1);
        setWizardCompletedSteps([]);
        setTempData({});
        setErrors({});
        setSelectedIdType('');
        setEditingIdentityType(null);
        setShowIdentityAddForm(false);
        setVerificationStatus({ PAN: 'idle', AADHAAR: 'idle' });
        setWizardEditingRows({ academic: new Set(), skills: new Set(), certificates: new Set() });
    };

    const validateWizardStep = (step, skipSetErrors = false) => {
        const sections = WIZARD_STEP_SECTIONS[step] || [];
        let merged = {};
        sections.forEach((section) => {
            merged = { ...merged, ...validateSection(section, true) };
        });
        if (step === 3) {
            merged = { ...merged, ...validateWizardStep3Extras(tempData) };
        }
        if (!skipSetErrors) setErrors(merged);
        return merged;
    };

    const isWizardStepValid = useCallback((step) => {
        const sections = WIZARD_STEP_SECTIONS[step] || [];
        let merged = {};
        sections.forEach((section) => {
            merged = { ...merged, ...validateSection(section, true) };
        });
        if (step === 3) {
            merged = { ...merged, ...validateWizardStep3Extras(tempData) };
        }
        return Object.keys(merged).length === 0;
    }, [tempData, selectedIdType]);

    const isWizardSaveValid = useCallback(() => {
        return [1, 2, 3].every((step) => {
            const sections = WIZARD_STEP_SECTIONS[step] || [];
            let merged = {};
            sections.forEach((section) => {
                merged = { ...merged, ...validateSection(section, true) };
            });
            if (step === 3) {
                merged = { ...merged, ...validateWizardStep3Extras(tempData) };
            }
            return Object.keys(merged).length === 0;
        });
    }, [tempData, selectedIdType]);

    const handleWizardNext = () => {
        const stepErrors = validateWizardStep(wizardStep);
        if (Object.keys(stepErrors).length > 0) {
            showValidationToast(stepErrors);
            return;
        }
        setWizardCompletedSteps((prev) => [...new Set([...prev, wizardStep])]);
        setWizardStep((s) => Math.min(EMPLOYEE_WIZARD_STEPS.length, s + 1));
    };

    const handleWizardPrevious = () => {
        setWizardStep((s) => Math.max(1, s - 1));
    };

    const handleWizardStepClick = (stepNum) => {
        if (stepNum === wizardStep) return;
        if (stepNum < wizardStep || wizardCompletedSteps.includes(stepNum)) {
            setWizardStep(stepNum);
            return;
        }
        if (stepNum === wizardStep + 1 && isWizardStepValid(wizardStep)) {
            setWizardCompletedSteps((prev) => [...new Set([...prev, wizardStep])]);
            setWizardStep(stepNum);
            return;
        }
        toast.error('Please complete the current step before moving forward.');
    };

    const validateSection = (section, skipSetErrors = false) => {
        const newErrors = {};
        const d = tempData;

        if (section === 'personal') {
            if (!d.profile_image) newErrors.profile_image = 'Profile Image Required';
            if (!d.aadhaar_name) newErrors.aadhaar_name = 'Name (as per Aadhaar) is Required';
            if (!d.dob) newErrors.dob = 'Date of Birth can\'t be empty';
            if (!d.blood_group) newErrors.blood_group = 'Blood Group is Required';
            if (!d.personal_email) newErrors.personal_email = 'Personal Email is Required';
            else if (!validateEmail(d.personal_email)) newErrors.personal_email = 'Invalid Email format';

            if (!d.address_permanent) newErrors.address_permanent = 'Permanent Address is Required';
            if (!d.address_communication) newErrors.address_communication = 'Communication Address is Required';

            // Mobile
            if (!d.mobile) newErrors.mobile = 'Mobile number is Required';
            else if (!validatePhone(d.mobile)) newErrors.mobile = 'Invalid Mobile Number';

            // Family - Specific fields inside family_details require validation
            if (!d.family_details?.father?.name) newErrors['father_name'] = 'Father Name Required';
            if (!d.family_details?.father?.contact) newErrors['father_contact'] = 'Father Contact Required';
            else if (!validatePhone(d.family_details.father.contact)) newErrors['father_contact'] = 'Invalid Father Contact';

            if (!d.family_details?.mother?.name) newErrors['mother_name'] = 'Mother Name Required';
            if (!d.family_details?.mother?.contact) newErrors['mother_contact'] = 'Mother Contact Required';
            else if (!validatePhone(d.family_details.mother.contact)) newErrors['mother_contact'] = 'Invalid Mother Contact';

            if (d.family_details?.spouse?.name && d.family_details?.spouse?.contact) {
                if (!validatePhone(d.family_details.spouse.contact)) newErrors['spouse_contact'] = 'Invalid Spouse Contact';
            }
        }

        if (section === 'identity') {
            const hasCompleteAadhaar = Boolean(
                d.aadhaar_number && d.aadhaar_file && validateAadhaar(d.aadhaar_number)
            );
            const hasCompletePan = Boolean(
                d.pan_number && d.pan_file && validatePAN(d.pan_number)
            );
            // Wizard Next: require at least one saved Identity Proof
            if (!hasCompleteAadhaar && !hasCompletePan) {
                newErrors['identity_required'] = 'At least one Identity Proof is required';
            }

            // Aadhaar Validation: Required if selected or already has data
            if (selectedIdType === 'AADHAAR' || d.aadhaar_number || d.aadhaar_file) {
                if (!d.aadhaar_number) newErrors['aadhaar_number'] = 'Aadhaar Number Required';
                else if (!validateAadhaar(d.aadhaar_number)) newErrors['aadhaar_number'] = 'Invalid Aadhaar Number (12 Digits)';
                if (!d.aadhaar_file) newErrors['aadhaar_file'] = 'Aadhaar Document Required';
            }

            // PAN Validation: Required if selected or already has data
            if (selectedIdType === 'PAN' || d.pan_number || d.pan_file) {
                if (!d.pan_number) newErrors['pan_number'] = 'PAN Number Required';
                else if (!validatePAN(d.pan_number)) newErrors['pan_number'] = 'Invalid PAN Number';
                if (!d.pan_file) newErrors['pan_file'] = 'PAN Document Required';
            }

            // Also check identity_details array if used
            (d.identity_details || []).forEach((item, idx) => {
                if (!item.type) newErrors[`id_${idx}_type`] = 'ID Type Required';
                if (!item.number) newErrors[`id_${idx}_number`] = 'ID Number Required';
                else {
                    if (item.type === 'Aadhaar' && !validateAadhaar(item.number)) newErrors[`id_${idx}_number`] = 'Invalid Aadhaar (12 digits)';
                    if (item.type === 'PAN' && !validatePAN(item.number)) newErrors[`id_${idx}_number`] = 'Invalid PAN';
                }
                if (!item.file) newErrors[`id_${idx}_file`] = 'Document Proof Required';
            });
        }

        if (section === 'experience') {
            const raw = d.role_history || [];
            // Map with original indices for accurate UI error reporting
            const indexed = raw.map((r, i) => ({ ...r, originalIdx: i }));

            // Sort by start_date to check chronological sequence
            const sorted = [...indexed].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

            sorted.forEach((role, sIdx) => {
                const idx = role.originalIdx;
                if (!role.job_role) newErrors[`role_${idx}_name`] = 'Job Role Required';
                if (!role.start_date) newErrors[`role_${idx}_start`] = 'Start Date Required';

                if (sIdx > 0) {
                    const prev = sorted[sIdx - 1];
                    // Overlap Check: Current start date cannot be before previous end date
                    if (prev.end_date && role.start_date && new Date(role.start_date) < new Date(prev.end_date)) {
                        newErrors[`role_${idx}_start`] = `Overlaps with previous role (ended ${format(new Date(prev.end_date), 'dd/MM/yyyy')})`;
                    }
                    // Strict Start Date Sequence
                    if (role.start_date && prev.start_date && new Date(role.start_date) <= new Date(prev.start_date)) {
                        newErrors[`role_${idx}_start`] = 'Role sequence start dates must be strictly increasing';
                    }
                }

                if (role.end_date && role.start_date && new Date(role.end_date) <= new Date(role.start_date)) {
                    newErrors[`role_${idx}_end`] = 'End date must be after start date';
                }
            });
        }

        if (section === 'previous-experience') {
            const raw = d.previous_experience || [];
            const indexed = raw.map((r, i) => ({ ...r, originalIdx: i }));
            const sorted = [...indexed].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

            sorted.forEach((role, sIdx) => {
                const idx = role.originalIdx;
                if (!role.company_name) newErrors[`prev_exp_${idx}_company`] = 'Company Name Required';
                if (!role.job_role) newErrors[`prev_exp_${idx}_role`] = 'Job Role Required';
                if (!role.start_date) newErrors[`prev_exp_${idx}_start`] = 'Start Date Required';
                if (!role.end_date) newErrors[`prev_exp_${idx}_end`] = 'End Date Required';

                if (role.end_date && role.start_date && new Date(role.end_date) <= new Date(role.start_date)) {
                    newErrors[`prev_exp_${idx}_end`] = 'End date must be after start date';
                }

                if (sIdx > 0) {
                    const prev = sorted[sIdx - 1];
                    if (prev.end_date && role.start_date && new Date(role.start_date) < new Date(prev.end_date)) {
                        newErrors[`prev_exp_${idx}_start`] = `Overlaps with previous experience (ended ${format(new Date(prev.end_date), 'dd/MM/yyyy')})`;
                    }
                    if (role.start_date && prev.start_date && new Date(role.start_date) <= new Date(prev.start_date)) {
                        newErrors[`prev_exp_${idx}_start`] = 'Experience start dates must be strictly increasing';
                    }
                }
            });
        }

        if (section === 'academic') {
            (d.academic_details || []).forEach((item, idx) => {
                if (isEmptyAcademicRow(item)) return;
                const score = parseFloat(item.percentage);
                if (!item.percentage) newErrors[`ac_score_${idx}`] = 'Score Required';
                else if (item.score_type === 'CGPA' && score > 10) {
                    newErrors[`ac_score_${idx}`] = 'CGPA cannot be > 10';
                } else if (item.score_type !== 'CGPA' && score > 100) {
                    newErrors[`ac_score_${idx}`] = 'Percentage cannot be > 100';
                }
                if (!item.year) newErrors[`ac_year_${idx}`] = 'Passing Year Required';
                if (!item.file) newErrors[`ac_file_${idx}`] = 'Document Required';
            });
        }

        if (section === 'certificates') {
            (tempData.certificates || []).forEach((cert, idx) => {
                if (isEmptyCertificateRow(cert)) return;
                if (!cert.type) newErrors[`cert_${idx}_type`] = 'Type Required';
                if (!cert.title) newErrors[`cert_${idx}_title`] = 'Title Required';
                if (!cert.issuing_authority) newErrors[`cert_${idx}_authority`] = 'Authority Required';
                if (!cert.issue_date) newErrors[`cert_${idx}_issue`] = 'Issue Date Required';
                else if (cert.issue_date > new Date().toISOString().split('T')[0]) newErrors[`cert_${idx}_issue`] = 'Future Date Invalid';

                if (cert.type !== 'Award') {
                    if (!cert.validity_type) newErrors[`cert_${idx}_validity`] = 'Validity Type Required';

                    if (cert.validity_type === 'Renewable') {
                        if (!cert.expiry_date) newErrors[`cert_${idx}_expiry`] = 'Expiry Date Required';
                        else if (cert.issue_date && cert.expiry_date <= cert.issue_date) newErrors[`cert_${idx}_expiry`] = 'Expiry must be after Issue Date';
                    }
                }

                if (!cert.file) newErrors[`cert_${idx}_file`] = 'Certificate Document Required';
            });
        }

        if (section === 'bank') {
            (d.bank_details || []).forEach((bank, idx) => {
                if (!bank.bank_name) newErrors[`bank_${idx}_name`] = 'Bank Name Required';
                if (!bank.ifsc || !validateIFSC(bank.ifsc)) newErrors[`bank_${idx}_ifsc`] = 'Invalid IFSC';
                if (!bank.branch) newErrors[`bank_${idx}_branch`] = 'Branch Required';
                if (!bank.account_number) newErrors[`bank_${idx}_acc`] = 'Account Number Required';
                else if (!validateBankAccount(bank.account_number)) newErrors[`bank_${idx}_acc`] = 'Invalid Account Number';

                // Expiry Validation (MM/YYYY) - field is optional
                if (bank.expiry_date) {
                    const [m, y] = (bank.expiry_date || '').split('/').map(Number);
                    if (!m || !y || m < 1 || m > 12) {
                        newErrors[`bank_${idx}_expiry`] = 'Invalid Format (MM/YYYY)';
                    } else {
                        const now = new Date();
                        const currM = now.getMonth() + 1;
                        const currY = now.getFullYear();
                        if (y < currY || (y === currY && m < currM)) {
                            newErrors[`bank_${idx}_expiry`] = 'Account Expired / Card Expired';
                        }
                    }
                }
            });
        }

        if (section === 'health') {
            if (d.insurance_opt === 'OPT-IN') {
                if (!d.nominee_name) newErrors.nominee_name = 'Nominee Name Required';
                if (!d.nominee_relationship) newErrors.nominee_relationship = 'Relationship Required';
                if (!d.nominee_dob) newErrors.nominee_dob = 'Nominee DOB Required';
                if (!d.nominee_phone) newErrors.nominee_phone = 'Nominee Phone Required';
                else if (!validatePhone(d.nominee_phone)) newErrors.nominee_phone = 'Invalid Nominee Phone';
            }
        }

        if (!skipSetErrors) setErrors(newErrors);
        return newErrors;
    };

    const handleSave = async (saveContext = null) => {
        const isWizardSave = saveContext === 'wizard';
        const validationErrors = isWizardSave
            ? (() => {
                let all = {};
                [1, 2, 3].forEach((step) => {
                    all = { ...all, ...validateWizardStep(step, true) };
                });
                setErrors(all);
                return all;
            })()
            : validateSection(editingSection);
        if (Object.keys(validationErrors).length > 0) {
            if (isWizardSave) {
                for (let step = 1; step <= 3; step++) {
                    const stepErrs = validateWizardStep(step, true);
                    if (Object.keys(stepErrs).length > 0) {
                        setWizardStep(step);
                        break;
                    }
                }
            }
            showValidationToast(validationErrors);
            return false;
        }

        let payload = JSON.parse(JSON.stringify(tempData)); // Deep copy to avoid mutation issues

        if (isWizardSave) {
            payload = stripEmptyWizardStep3Rows(payload);
            if (Array.isArray(payload.previous_experience)) {
                payload.previous_experience = payload.previous_experience.filter((row) => !isEmptyPreviousExperienceRow(row));
            }
        }

        // Bank Details Logic
        if (editingSection === 'bank' || isWizardSave) {
            const oldLen = (data.bank_details || []).length;
            const newLen = (payload.bank_details || []).length;

            if (newLen > oldLen && newLen > 0) {
                const isMain = await new Promise(resolve => {
                    setBankConfirmModal({ open: true, resolve });
                });
                const lastIdx = newLen - 1;

                payload.bank_details.forEach((b, i) => {
                    if (isMain) {
                        if (i === lastIdx) {
                            b.is_active = true;
                            // b.expiry_date = null; // Removed: Should not clear user-entered or default expiry
                        } else {
                            b.is_active = false;
                            if (!b.expiry_date) b.expiry_date = new Date().toISOString().split('T')[0];
                        }
                    } else {
                        if (i === lastIdx) {
                            b.is_active = false;
                        }
                        // Keep existing active status for others? Or ensure one is main?
                        // If user clicked NO, we assume previous active account remains active.
                    }
                });
            } else if (newLen === 1) {
                // First account always active
                if (payload.bank_details[0]) payload.bank_details[0].is_active = true;
            }
        }

        const loadingToast = toast.loading('Saving changes...');
        try {
            await api.post('/employee/details', payload);
            localStorage.removeItem('emp_unsaved_data');
            setEditingSection(null);
            if (isWizardSave) {
                closeUpdateWizard();
            }
            await fetchDetails(true);

            // Sync global user state ONLY if own profile image was updated
            const currentGlobalId = globalUser?.user_id || globalUser?.id || globalUser?.sub;
            const targetProfileId = payload.user_id || data.user_id || getProfileUserId();

            if (currentGlobalId && targetProfileId && String(currentGlobalId) === String(targetProfileId) && payload.profile_image) {
                let imgPath = payload.profile_image;
                const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL || '';
                if (imgPath && imgPath.startsWith(IMAGE_BASE_URL)) {
                    imgPath = imgPath.replace(IMAGE_BASE_URL, '').replace(/^\/+/, '');
                }

                setGlobalUser(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        user_img: imgPath,
                        image: imgPath,
                        profile_image: imgPath
                    };
                });

                const localData = JSON.parse(localStorage.getItem('user') || '{}');
                localData.user_img = imgPath;
                localData.image = imgPath;
                localData.profile_image = imgPath;
                localStorage.setItem('user', JSON.stringify(localData));
            }

            toast.success('Saved successfully', { id: loadingToast });
            return true;
        } catch (error) {
            console.error("Save Error", error);
            if (error.response && error.response.status === 422 && error.response.data.errors) {
                setErrors(error.response.data.errors);
                toast.error((t) => (
                    <div className="flex flex-col gap-1">
                        <span className="font-bold text-red-600">Server Validation Failed:</span>
                        <ul className="list-disc pl-4 text-xs text-gray-600 mt-1 space-y-0.5">
                            {Object.values(error.response.data.errors).slice(0, 3).map((msg, i) => <li key={i}>{msg}</li>)}
                        </ul>
                    </div>
                ), { id: loadingToast, duration: 5000 });
            } else {
                toast.error('Save failed. Please try again.', { id: loadingToast });
            }
            return false;
        }
    };

    const handleChange = (e) => {
        setTempData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleJobRoleChange = (e) => {
        const newRole = e.target.value;
        const currentRole = tempData.job_role;

        if (newRole === currentRole) return;

        // If history is empty, it's the first time - no popup
        if (!tempData.role_history || tempData.role_history.length === 0) {
            setTempData({
                ...tempData,
                job_role: newRole,
                role_history: [{
                    job_role: newRole,
                    start_date: tempData.joining_date || format(new Date(), 'yyyy-MM-dd'),
                    status: 'Active'
                }]
            });
        } else {
            // Second time or more - show popup
            // Calculate hypothetical prev role end date (Start Date - 1 day)
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            setPromotionModal({
                open: true,
                newRole: newRole,
                startDate: format(today, 'yyyy-MM-dd'),
                prevRoleEndDate: format(yesterday, 'yyyy-MM-dd'),
                prevRoleStartDate: tempData.role_history[tempData.role_history.length - 1]?.start_date || tempData.joining_date || format(today, 'yyyy-MM-dd')
            });
        }
    };

    const confirmPromotion = () => {
        const { newRole, startDate, prevRoleEndDate, prevRoleStartDate } = promotionModal;

        if (!startDate) {
            toast.error("Start Date is required");
            return;
        }

        const start = new Date(startDate);
        const pStart = new Date(prevRoleStartDate);
        const pEnd = prevRoleEndDate ? new Date(prevRoleEndDate) : null;

        // 1. Basic Sequence Validation
        if (pEnd && pEnd < pStart) {
            toast.error(`The previous role cannot end (${format(pEnd, 'dd/MM/yyyy')}) before it started (${format(pStart, 'dd/MM/yyyy')}). Please correct the Start or End date.`);
            return;
        }

        if (start < pStart) {
            toast.error(`The new role cannot start before the previous role started (${format(pStart, 'dd/MM/yyyy')}).`);
            return;
        }

        // 2. Joining Date Validation
        if (tempData.joining_date && pStart < new Date(tempData.joining_date)) {
            toast.error(`Previous role start date cannot be earlier than Joining Date (${format(new Date(tempData.joining_date), 'dd/MM/yyyy')})`);
            return;
        }

        const history = [...(tempData.role_history || [])];
        let updatedHistory = [];

        // Update Previous Role
        if (history.length > 0) {
            const lastRoleIndex = history.length - 1;
            const lastRole = { ...history[lastRoleIndex] };

            // Update the start date in case the user corrected it in the modal
            lastRole.start_date = prevRoleStartDate;

            if (prevRoleEndDate) {
                lastRole.end_date = prevRoleEndDate;
                lastRole.status = 'Completed';
            }

            updatedHistory = [...history.slice(0, lastRoleIndex), lastRole];
        } else {
            updatedHistory = [...history];
        }

        // Add New Role
        updatedHistory.push({
            job_role: newRole,
            start_date: startDate,
            end_date: '', // Active
            status: 'Active'
        });

        setTempData({
            ...tempData,
            job_role: newRole,
            role_history: updatedHistory,
            // Optimistically update designation for UI if needed immediately, though backend syncs it.
            // But data.designation comes from backend join. We can't easily update 'data' state for that field without refetch.
            // So we just rely on job_role update in UI.
        });
        setPromotionModal({ open: false, newRole: '', startDate: '', prevRoleEndDate: '' });
        toast.success(`Position updated to ${newRole}`);
    };

    const handleFamilyChange = (relation, field, value) => {
        setTempData(prev => ({
            ...prev,
            family_details: {
                ...prev.family_details,
                [relation]: {
                    ...prev.family_details[relation],
                    [field]: value
                }
            }
        }));
    };

    const handleDateChange = (name, date) => {
        setTempData(prev => ({ ...prev, [name]: date ? format(date, 'yyyy-MM-dd') : null }));
    };

    const handleDeepChange = (field, index, subField, value) => {
        setTempData(prev => {
            const list = [...(prev[field] || [])];
            if (!list[index]) list[index] = {};
            list[index] = { ...list[index], [subField]: value };
            return { ...prev, [field]: list };
        });
    };

    const WIZARD_ROW_FIELD_SECTIONS = {
        academic_details: 'academic',
        skills: 'skills',
        certificates: 'certificates',
    };

    const addRow = (field, initialObj = {}) => {
        setTempData(prev => ({ ...prev, [field]: [...(prev[field] || []), initialObj] }));
    };

    const addWizardRow = (field, initialObj, isEmptyRow) => {
        const savedLen = (data[field] || []).length;
        const list = tempData[field] || [];
        const hasEmptyDraft = list.some((item, idx) => idx >= savedLen && isEmptyRow(item));
        if (hasEmptyDraft) return;
        addRow(field, initialObj);
    };

    const getVisibleWizardRows = (field, section, isEmptyRow) =>
        (tempData[field] || [])
            .map((item, idx) => ({ item, idx }))
            .filter(({ item, idx }) => isWizardRowEditing(section, idx) || !isEmptyRow(item));

    const removeRow = (field, index) => {
        setTempData(prev => {
            const list = [...(prev[field] || [])];
            list.splice(index, 1);
            return { ...prev, [field]: list };
        });
        const section = WIZARD_ROW_FIELD_SECTIONS[field];
        if (section) {
            setWizardEditingRows((prev) => {
                const next = new Set();
                (prev[section] || new Set()).forEach((i) => {
                    if (i < index) next.add(i);
                    else if (i > index) next.add(i - 1);
                });
                return { ...prev, [section]: next };
            });
            setErrors((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((key) => {
                    if (isWizardRowErrorKey(section, key, index)) delete next[key];
                });
                return next;
            });
        }
    };

    const handleFileUpload = async (field, file) => {
        if (!file) return;
        const toastId = toast.loading('Uploading...');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/upload?user_id=${targetId}` : '/employee/upload';
            const res = await api.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                setTempData(prev => ({ ...prev, [field]: res.data.path }));
                toast.success('Uploaded', { id: toastId });
            } else toast.error('Check file size/type', { id: toastId });
        } catch (e) { toast.error('Error', { id: toastId }); }
    };

    const handleDeepFileUpload = async (listName, index, subField, file, docType = null) => {
        if (!file) return;
        const toastId = toast.loading('Uploading...');
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (docType) formData.append('type', docType);

            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/upload?user_id=${targetId}` : '/employee/upload';
            const res = await api.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                handleDeepChange(listName, index, subField, res.data.path);
                toast.success('Uploaded', { id: toastId });
            } else toast.error('Failed', { id: toastId });
        } catch (e) { toast.error('Error', { id: toastId }); }
    };

    const getExperience = () => {
        if (!data.joining_date) return '-';
        const start = new Date(data.joining_date);
        const now = new Date();
        const years = differenceInYears(now, start);
        const months = differenceInMonths(now, start) % 12;
        return `${years} Years ${months} Months`;
    };

    const getBankColor = (bankName) => {
        const colors = {
            'HDFC Bank': 'from-blue-800 to-blue-600',
            'ICICI Bank': 'from-orange-700 to-orange-500',
            'State Bank of India': 'from-blue-500 to-cyan-400',
            'Axis Bank': 'from-red-800 to-red-600',
            'Kotak Mahindra Bank': 'from-red-700 to-red-500',
            'Bank of Baroda': 'from-orange-600 to-orange-400',
            'Punjab National Bank': 'from-yellow-700 to-yellow-500',
            'Union Bank of India': 'from-red-600 to-blue-600',
            'Canara Bank': 'from-blue-600 to-yellow-500',
            'IndusInd Bank': 'from-red-900 to-red-700',
            'Yes Bank': 'from-blue-700 to-red-500',
            'City Union Bank': 'from-red-500 to-orange-400',
            'Indian Bank': 'from-blue-900 to-yellow-600'
        };
        return colors[bankName] || 'from-slate-700 to-slate-500';
    };

    const getDurationText = (start, end) => {
        if (!start) return '';
        const endDate = end ? new Date(end) : new Date();
        const years = differenceInYears(endDate, new Date(start));
        const months = differenceInMonths(endDate, new Date(start)) % 12;
        return (years > 0 ? `${years} yrs ` : '') + (months + ' mos');
    };

    const getTotalDurationText = (records = []) => {
        const totalMonths = (records || []).reduce((sum, item) => {
            if (!item?.start_date) return sum;
            const endDate = item.end_date ? new Date(item.end_date) : new Date();
            const startDate = new Date(item.start_date);
            if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) return sum;
            return sum + differenceInMonths(endDate, startDate);
        }, 0);

        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        return totalMonths > 0 ? `${years > 0 ? `${years} Years ` : ''}${months} Months` : '';
    };

    const preprocessImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Upscale 2x for much better OCR accuracy
                    canvas.width = img.width * 2;
                    canvas.height = img.height * 2;

                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const data = imageData.data;

                    // Luminosity-based Binarization
                    for (let i = 0; i < data.length; i += 4) {
                        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
                        // High contrast thresholding (black text < 120)
                        const final = gray < 130 ? 0 : 255;
                        data[i] = final;
                        data[i + 1] = final;
                        data[i + 2] = final;
                    }
                    ctx.putImageData(imageData, 0, 0);
                    resolve(canvas.toDataURL('image/jpeg', 0.95));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const handleTesseractScan = async (type, file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', type);

            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/verify-identity?user_id=${targetId}` : '/employee/verify-identity';
            const uploadRes = await api.post(url, formData);
            if (!uploadRes.data.status) throw new Error("Upload Failed");
            if (!scannerActiveRef.current) return; // Stop if closed

            const fileUrl = uploadRes.data.file_url;
            const processedImage = await preprocessImage(file);
            if (!scannerActiveRef.current) return; // Stop if closed

            const { data: { text } } = await Tesseract.recognize(
                processedImage,
                'eng',
                {
                    logger: m => console.log(m),
                    tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/ '
                }
            );

            if (!scannerActiveRef.current) return; // Stop if closed
            console.log("OCR Raw Extracted Text:", text);

            let extractedNumber = '';
            const words = text.toUpperCase().split(/[\s\n]+/);

            if (type === 'PAN') {
                const panPattern = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]$/;
                for (let word of words) {
                    let cleaned = word.replace(/[^A-Z0-9]/g, '');
                    if (cleaned.length === 10) {
                        // Refined Repair Logic
                        let p1 = cleaned.substring(0, 5).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');
                        let p2 = cleaned.substring(5, 9).replace(/O/g, '0').replace(/I/g, '1').replace(/S/g, '5').replace(/B/g, '8');
                        let p3 = cleaned.substring(9, 10).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');

                        let repaired = p1 + p2 + p3;
                        if (panPattern.test(repaired)) {
                            extractedNumber = repaired;
                            break;
                        }
                    }
                }
            } else if (type === 'AADHAAR') {
                // Search for 12 digits, potentially with spaces
                const mergedText = text.replace(/\s/g, '');
                const aadhaarMatch = mergedText.match(/[0-9]{12}/);
                if (aadhaarMatch) {
                    extractedNumber = aadhaarMatch[0];
                } else {
                    const groupsMatch = text.match(/[0-9]{4}\s[0-9]{4}\s[0-9]{4}/);
                    if (groupsMatch) extractedNumber = groupsMatch[0].replace(/\s/g, '');
                }
            }

            // Fallback: Search in collapsed text (handles spaces/breaks in PAN)
            if (type === 'PAN' && !extractedNumber) {
                const panPattern = /^[A-Z]{3}[PCHFATBLJG][A-Z][0-9]{4}[A-Z]$/;
                const collapsed = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
                for (let i = 0; i <= collapsed.length - 10; i++) {
                    let potential = collapsed.substring(i, i + 10);
                    let p1 = potential.substring(0, 5).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');
                    let p2 = potential.substring(5, 9).replace(/O/g, '0').replace(/I/g, '1').replace(/S/g, '5').replace(/B/g, '8');
                    let p3 = potential.substring(9, 10).replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S');
                    let repaired = p1 + p2 + p3;
                    if (panPattern.test(repaired)) {
                        extractedNumber = repaired;
                        break;
                    }
                }
            }

            if (extractedNumber && scannerActiveRef.current) {
                setTempData(prev => ({
                    ...prev,
                    [type === 'PAN' ? 'pan_number' : 'aadhaar_number']: extractedNumber,
                    [type === 'PAN' ? 'pan_file' : 'aadhaar_file']: fileUrl,
                    [type === 'PAN' ? 'pan_local_filename' : 'aadhaar_local_filename']: file.name
                }));

                setScannedData(prev => ({
                    ...prev,
                    [type]: { number: extractedNumber }
                }));

                setVerificationStatus(prev => ({ ...prev, [type]: 'verified' }));

                setTimeout(() => {
                    if (scannerActiveRef.current) {
                        setIdentityModal({ open: false, type: null, file: null, preview: null });
                        scannerActiveRef.current = false;
                        toast.success(`${type} Number extracted!`, { icon: '✅' });
                    }
                }, 2000);
            } else if (scannerActiveRef.current) {
                setVerificationStatus(prev => ({ ...prev, [type]: 'error' }));
                toast.error(`Could not detect valid ${type} number. Please try a clearer image.`);
            }

        } catch (e) {
            console.error("OCR Error:", e);
            if (scannerActiveRef.current) {
                setVerificationStatus(prev => ({ ...prev, [type]: 'error' }));
                toast.error("Extraction failed. Please try again.");
            }
        }
    };

    const handleIdentityUpload = (type, file) => {
        if (!file) return;
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            toast.error("Only JPG or PNG images are allowed for Identity verification.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setIdentityModal({ open: true, type, file, preview: e.target.result });
            setVerificationStatus(prev => ({ ...prev, [type]: 'scanning' }));
            scannerActiveRef.current = true;
            handleTesseractScan(type, file);
        };
        reader.readAsDataURL(file);
    };

    const renderPersonalEditContent = (wizardLayout = false) => (
        <div className="space-y-6">
            {/* Profile photo + existing personal fields */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Column: Photo Uploader */}
                <div className="lg:col-span-3 flex flex-col items-center justify-center p-6 bg-gray-50/50 rounded-2xl border border-gray-150 shadow-inner min-h-[350px]">
                    <div 
                        className={`w-36 h-36 rounded-full bg-gray-100 border-2 border-dashed ${errors.profile_image ? 'border-red-500 ring-2 ring-red-100 animate-pulse' : 'border-gray-300'} flex items-center justify-center overflow-hidden relative group shadow-inner ${tempData.profile_image ? 'cursor-pointer' : ''}`}
                        onDoubleClick={(e) => {
                            if (tempData.profile_image) {
                                e.stopPropagation();
                                setPhotoPreviewUrl(tempData.profile_image);
                            }
                        }}
                        title={tempData.profile_image ? "Double click to view full image" : ""}
                    >
                        {tempData.profile_image ? (
                            <img src={tempData.profile_image} className="w-full h-full object-cover animate-fade-in" alt="Profile" />
                        ) : (
                            <FiUser size={48} className="text-gray-300" />
                        )}
                        <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-300">
                            <FiUpload className="text-white transform scale-95 group-hover:scale-100 transition-transform" size={24} />
                            <input
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onload = () => setCropperImage(reader.result);
                                        reader.readAsDataURL(file);
                                        e.target.value = null; // Reset input
                                    }
                                }}
                                accept="image/*"
                            />
                        </label>
                    </div>
                    <span className="text-[10px] bg-red-50 text-[#C30010] px-3 py-1 rounded-full mt-4 font-bold uppercase tracking-wider shadow-sm">Upload Photo *</span>
                    {errors.profile_image && <span className="text-[10px] text-red-500 font-bold mt-2 uppercase tracking-tighter animate-pulse">{errors.profile_image}</span>}
                </div>

                {/* Right Column: existing Support System fields only */}
                <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField
                        label="Employee Name (As per Aadhaar)"
                        name="aadhaar_name"
                        value={tempData.aadhaar_name || ''}
                        onChange={handleChange}
                        required
                        error={errors.aadhaar_name}
                    />

                    <InputField
                        label="Job Role (Admin Only)"
                        name="job_role"
                        value={tempData.job_role || ''}
                        onChange={handleJobRoleChange}
                        as="select"
                        options={config.designations || []}
                        required
                        error={errors.job_role}
                        disabled={!(globalUser && [1, 9].includes(Number(globalUser.role_id || globalUser.user_role_id)))}
                    />

                    <DatePickerField
                        label="Date of Birth"
                        selected={tempData.dob}
                        onChange={(d) => handleDateChange('dob', d)}
                        required
                        error={errors.dob}
                    />

                    <InputField
                        label="Blood Group"
                        name="blood_group"
                        value={tempData.blood_group || ''}
                        onChange={handleChange}
                        as="select"
                        options={['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']}
                        required
                        error={errors.blood_group}
                    />

                    <PhoneInput
                        label="Personal Mobile"
                        name="mobile"
                        value={tempData.mobile || ''}
                        onChange={handleChange}
                        required
                        error={errors.mobile}
                    />

                    <InputField
                        label="Personal Email"
                        name="personal_email"
                        value={tempData.personal_email || ''}
                        onChange={handleChange}
                        required
                        error={errors.personal_email}
                    />

                    <DatePickerField
                        label="Joining Date (Enova)"
                        selected={tempData.joining_date}
                        onChange={(d) => handleDateChange('joining_date', d)}
                    />

                    {globalUser && [1, 9].includes(Number(globalUser.role_id || globalUser.user_role_id)) && (
                        <DatePickerField
                            label="Relieving Date"
                            selected={tempData.relieving_date}
                            onChange={(d) => handleDateChange('relieving_date', d)}
                        />
                    )}

                    <InputField
                        label="PF Number"
                        name="pf_number"
                        value={tempData.pf_number || ''}
                        onChange={handleChange}
                        error={errors.pf_number}
                    />

                    <InputField
                        label="ESI Number"
                        name="esi_number"
                        value={tempData.esi_number || ''}
                        onChange={handleChange}
                        error={errors.esi_number}
                    />

                    {globalUser && [1, 9].includes(Number(globalUser.role_id || globalUser.user_role_id)) && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Reason for Relieving
                            </label>
                            <textarea
                                name="relieving_reason"
                                rows={2}
                                value={tempData.relieving_reason || ''}
                                onChange={handleChange}
                                placeholder="Enter reason for relieving..."
                                className="w-full p-2.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C30010]/20 focus:border-[#C30010] transition-all resize-y shadow-sm"
                            />
                        </div>
                    )}

                    {wizardLayout && globalUser && (Number(globalUser.role_id || globalUser.user_role_id) === 1) && (
                        <div className="relative p-3 pt-4 bg-red-50/50 border border-red-100 rounded-xl">
                            <div className="absolute -top-2 left-3 bg-[#C30010] text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest shadow-sm">
                                Admin Only
                            </div>
                            <SearchableSelect
                                label="Official / Admin Assigned Role"
                                value={tempData.official_job_role}
                                onChange={(val) => setTempData(prev => ({ ...prev, official_job_role: val }))}
                                options={config.designations || []}
                                placeholder="Select or Type Official Role"
                            />
                            <p className="text-[9px] text-gray-400 mt-1.5 italic font-semibold leading-tight">
                                * This role will take precedence in Reports & ID Cards if set.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Admin-Only Official Job Role (inline edit layout) */}
            {!wizardLayout && globalUser && (Number(globalUser.role_id || globalUser.user_role_id) === 1) && (
                <div className="p-4 bg-red-50/50 border border-red-150 rounded-2xl relative mt-4">
                    <div className="absolute -top-2.5 left-4 bg-[#C30010] text-white text-[9px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-widest shadow-sm">
                        Admin Only
                    </div>
                    <SearchableSelect
                        label="Official / Admin Assigned Role"
                        value={tempData.official_job_role}
                        onChange={(val) => setTempData(prev => ({ ...prev, official_job_role: val }))}
                        options={config.designations || []}
                        placeholder="Select or Type Official Role"
                    />
                    <p className="text-[10px] text-gray-400 mt-2 italic font-semibold">
                        * This role will take precedence in Reports & ID Cards if set.
                    </p>
                </div>
            )}

            {/* Address Inputs */}
            <div className="border-t pt-3 mt-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase mb-2 pl-1">Address Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 md:gap-y-0">
                    <div>
                        <div className="hidden md:block h-5 mb-1" aria-hidden="true" />
                        <InputField
                            label="Permanent Address"
                            name="address_permanent"
                            as="textarea"
                            rows={2}
                            compact
                            value={tempData.address_permanent}
                            onChange={handleChange}
                            required
                            error={errors.address_permanent}
                        />
                    </div>
                    <div>
                        <div className="flex items-center gap-1.5 pl-1 h-5 mb-1">
                            <input
                                type="checkbox"
                                id="sameAsPerm"
                                className="w-4 h-4 text-[#C30010] border-gray-300 rounded focus:ring-[#C30010]"
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setTempData(prev => ({ ...prev, address_communication: prev.address_permanent }));
                                    }
                                }}
                            />
                            <label htmlFor="sameAsPerm" className="text-[11px] font-bold text-gray-500 uppercase cursor-pointer select-none">
                                Same as Permanent Address
                            </label>
                        </div>
                        <InputField
                            label="Communication Address"
                            name="address_communication"
                            as="textarea"
                            rows={2}
                            compact
                            value={tempData.address_communication}
                            onChange={handleChange}
                            required
                            error={errors.address_communication}
                        />
                    </div>
                </div>
            </div>

            {/* Family Details Input Grid */}
            <div className="border-t pt-4 mt-6">
                <h4 className="text-xs font-bold text-gray-500 uppercase mb-4 pl-1">Family Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {['father', 'mother', 'spouse'].map(relation => (
                        <div key={relation} className="bg-gray-50/50 p-4 rounded-2xl border border-gray-150 animate-fade-in">
                            <h5 className="text-[11px] font-black uppercase text-[#C30010] mb-4 tracking-wider flex items-center gap-2">
                                <div className="w-1.5 h-3.5 bg-[#C30010] rounded-full" />
                                {relation} Details
                            </h5>
                            <div className="space-y-4">
                                <InputField
                                    label="Name"
                                    value={tempData.family_details?.[relation]?.name}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                        handleFamilyChange(relation, 'name', val);
                                    }}
                                    required={relation !== 'spouse'}
                                    error={relation !== 'spouse' && errors[`${relation}_name`]}
                                />
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">
                                        Contact {relation !== 'spouse' && <span className="text-red-500 font-bold">*</span>}
                                    </label>
                                    <div className={`flex items-center border ${errors[relation + '_contact'] ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl bg-white focus-within:ring-2 focus-within:ring-[#C30010]/10 focus-within:border-[#C30010] overflow-hidden transition-all`}>
                                        <div className="bg-gray-50 px-3 py-2 border-r border-gray-200 text-gray-700 text-[12.5px] font-bold flex items-center gap-2">
                                            <span>🇮🇳</span> +91
                                        </div>
                                        <input
                                            className="w-full px-3 py-2 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0 text-[12.5px] font-bold text-gray-800 placeholder-gray-400 font-mono tracking-widest"
                                            placeholder="Mobile Number"
                                            value={tempData.family_details?.[relation]?.contact?.replace('+91 ', '') || ''}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length <= 10) {
                                                    if (val.length === 1 && !/^[6-9]/.test(val)) return;
                                                    handleFamilyChange(relation, 'contact', val ? `+91 ${val}` : '');
                                                }
                                            }}
                                            maxLength={10}
                                        />
                                    </div>
                                    {errors[relation + '_contact'] && (
                                        <span className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-tighter animate-pulse">
                                            {errors[relation + '_contact']}
                                        </span>
                                    )}
                                </div>
                                <InputField
                                    label="Profession"
                                    value={tempData.family_details?.[relation]?.profession}
                                    onChange={(e) => handleFamilyChange(relation, 'profession', e.target.value)}
                                />
                                <InputField
                                    label="Position"
                                    value={tempData.family_details?.[relation]?.position}
                                    onChange={(e) => handleFamilyChange(relation, 'position', e.target.value)}
                                />
                                <InputField
                                    label="Organisation"
                                    value={tempData.family_details?.[relation]?.organisation}
                                    onChange={(e) => handleFamilyChange(relation, 'organisation', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderCommunityEditContent = () => (
        <div className="space-y-4">
            <InputField label="Community" name="community" value={tempData.community} onChange={handleChange} options={config.communities} as="select" />
            <SearchableSelect label="Caste" value={tempData.caste} onChange={(val) => setTempData({ ...tempData, caste: val })} options={config.castes} placeholder="Select Caste" />
            <InputField label="Religion" name="religion" value={tempData.religion} onChange={handleChange} options={config.religions} as="select" />

            <div className="mt-4">
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">Languages Known (Speak)</label>
                <div className="flex flex-wrap gap-2 mt-2">
                    <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-[12.5px] font-medium focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] transition-all resize-none" rows={3} placeholder="Tamil,English,Hindi..." value={(tempData.languages_speak || []).join(',')} onChange={(e) => setTempData({ ...tempData, languages_speak: e.target.value.split(',') })} />
                </div>
            </div>
        </div>
    );

    const renderHealthEditContent = () => (
        <div className="space-y-4">
            <InputField label="Health Conditions (if any & remedies)" name="health_conditions" value={tempData.health_conditions} onChange={handleChange} as="textarea" />
            <div className="p-5 border border-gray-100 rounded-xl bg-gray-50/50 shadow-inner">
                <div className="flex justify-between items-center mb-5">
                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1">Employee Insurance</span>
                    <div className="flex bg-white rounded-xl overflow-hidden border border-gray-200 p-1 shadow-sm">
                        <button type="button" className={`px-5 py-2 text-[10px] font-bold rounded-lg transition-all ${tempData.insurance_opt === 'OPT-IN' ? 'bg-[#C30010] text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`} onClick={() => setTempData({ ...tempData, insurance_opt: 'OPT-IN' })}>OPT-IN</button>
                        <button type="button" className={`px-5 py-2 text-[10px] font-bold rounded-lg transition-all ${tempData.insurance_opt === 'OPT-OUT' ? 'bg-gray-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`} onClick={() => setTempData({ ...tempData, insurance_opt: 'OPT-OUT' })}>OPT-OUT</button>
                    </div>
                </div>
                {tempData.insurance_opt === 'OPT-IN' && (
                    <div className="space-y-3 animate-fade-in pl-2 border-l-2 border-[#C30010]">
                        <InputField label="Nominee Name" name="nominee_name" value={tempData.nominee_name} onChange={handleChange} required error={errors.nominee_name} />
                        <InputField label="Relationship" name="nominee_relationship" value={tempData.nominee_relationship} onChange={handleChange} required error={errors.nominee_relationship} />
                        <DatePickerField label="Nominee Date of Birth" selected={tempData.nominee_dob} onChange={(d) => handleDateChange('nominee_dob', d)} required error={errors.nominee_dob} />
                        <PhoneInput label="Nominee Phone" name="nominee_phone" value={tempData.nominee_phone} onChange={handleChange} required error={errors.nominee_phone} />
                    </div>
                )}
            </div>
        </div>
    );

    const renderExperienceAnalysisContent = () => {
        const experiences = tempData.previous_experience || [];
        const rowsToRender = experiences.length > 0
            ? experiences.map((item, idx) => ({ item, idx }))
            : [{ item: { company_name: '', job_role: '', start_date: '', end_date: '' }, idx: 0, isPlaceholder: true }];

        const updatePreviousExperience = (idx, field, value) => {
            setTempData((prev) => {
                const list = [...(prev.previous_experience || [])];
                if (!list.length) {
                    list.push({ company_name: '', job_role: '', start_date: '', end_date: '' });
                }
                list[idx] = { ...list[idx], [field]: value };
                return { ...prev, previous_experience: list };
            });
        };

        const removePreviousExperience = (idx) => {
            setTempData((prev) => {
                const list = [...(prev.previous_experience || [])];
                list.splice(idx, 1);
                return { ...prev, previous_experience: list };
            });
            setErrors((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((key) => {
                    if (key.startsWith(`prev_exp_${idx}_`)) delete next[key];
                });
                return next;
            });
        };

        return (
            <div className="space-y-2 h-full">
                <div className="space-y-2">
                    {rowsToRender.map(({ item, idx, isPlaceholder }) => (
                        <div
                            key={`prev-exp-row-${idx}`}
                            className="p-2.5 rounded-lg border border-gray-100 bg-gray-50/80 space-y-2 relative"
                        >
                            {!isPlaceholder && (
                                <button
                                    type="button"
                                    onClick={() => removePreviousExperience(idx)}
                                    className="absolute top-2 right-2 text-red-400 hover:text-red-600 transition"
                                    title="Remove"
                                >
                                    <FiTrash2 size={14} />
                                </button>
                            )}
                            <InputField
                                compact
                                label="Company Name"
                                value={item.company_name || ''}
                                onChange={(e) => updatePreviousExperience(idx, 'company_name', e.target.value)}
                                error={errors[`prev_exp_${idx}_company`]}
                            />
                            <InputField
                                compact
                                label="Job Role / Designation"
                                value={item.job_role || ''}
                                onChange={(e) => updatePreviousExperience(idx, 'job_role', e.target.value)}
                                error={errors[`prev_exp_${idx}_role`]}
                            />
                            <InputField
                                compact
                                label="From"
                                type="date"
                                value={item.start_date || ''}
                                onChange={(e) => updatePreviousExperience(idx, 'start_date', e.target.value)}
                                error={errors[`prev_exp_${idx}_start`]}
                                maxDate={item.end_date ? new Date(item.end_date) : new Date()}
                            />
                            <InputField
                                compact
                                label="To"
                                type="date"
                                value={item.end_date || ''}
                                onChange={(e) => updatePreviousExperience(idx, 'end_date', e.target.value)}
                                error={errors[`prev_exp_${idx}_end`]}
                                minDate={item.start_date ? new Date(item.start_date) : null}
                                maxDate={new Date()}
                            />
                            <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Total Experience</label>
                                <p className="text-[12px] font-semibold text-gray-800 py-1">
                                    {getDurationText(item.start_date, item.end_date) || '—'}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
                {getTotalDurationText(experiences.filter((row) => !isEmptyPreviousExperienceRow(row))) && (
                    <div className="pt-1.5 border-t border-gray-100 flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Experience</span>
                        <span className="text-[11px] font-bold text-gray-800 bg-white border border-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {getTotalDurationText(experiences.filter((row) => !isEmptyPreviousExperienceRow(row)))}
                        </span>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => addRow('previous_experience', { company_name: '', job_role: '', start_date: '', end_date: '' })}
                    className="px-3 py-1.5 bg-white border-2 border-dashed border-gray-200 rounded-lg text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-1.5 w-fit shadow-sm"
                >
                    <FiPlus size={14} /> Add Experience
                </button>
            </div>
        );
    };

    const renderBankEditContent = () => (
        <div className="space-y-4">
            {(tempData.bank_details || []).map((bank, idx) => (
                <div key={idx} className={`p-4 rounded border relative bg-gradient-to-r ${getBankColor(bank.bank_name)} text-white`}>
                    <button onClick={() => removeRow('bank_details', idx)} className="absolute top-2 right-2 text-white/70 hover:text-white"><FiTrash2 size={14} /></button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                                Account Number {errors[`bank_${idx}_acc`] && <span className="text-yellow-300 ml-1">({errors[`bank_${idx}_acc`]})</span>}
                            </label>
                            <input
                                className={`w-full px-3 py-2 bg-white border ${errors[`bank_${idx}_acc`] ? 'border-yellow-300 ring-1 ring-yellow-300' : 'border-transparent'} rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400`}
                                value={bank.account_number}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 18);
                                    handleDeepChange('bank_details', idx, 'account_number', val);
                                }}
                                onBlur={() => {
                                    if (bank.account_number && bank.ifsc && bank.verification_status !== 'Verified') {
                                        handleVerifyBank(idx);
                                    }
                                }}
                            />
                        </div>

                        {/* IFSC Code - Moved to Position 2 */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                                IFSC Code {errors[`bank_${idx}_ifsc`] && <span className="text-yellow-300 ml-1">({errors[`bank_${idx}_ifsc`]})</span>}
                            </label>
                            <input
                                className={`w-full px-3 py-2 bg-white border ${errors[`bank_${idx}_ifsc`] ? 'border-yellow-300 ring-1 ring-yellow-300' : 'border-transparent'} rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400 uppercase font-mono tracking-widest`}
                                value={bank.ifsc}
                                placeholder="SBIN0001234"
                                onChange={(e) => handleDeepChange('bank_details', idx, 'ifsc', e.target.value.toUpperCase())}
                                onBlur={async (e) => {
                                    const code = e.target.value;
                                    if (code.length === 11) {
                                        const loadToast = toast.loading("Checking IFSC...");
                                        try {
                                            const res = await fetch(`https://ifsc.razorpay.com/${code}`);
                                            if (res.ok) {
                                                const data = await res.json();
                                                handleDeepChange('bank_details', idx, 'bank_name', data.BANK);
                                                handleDeepChange('bank_details', idx, 'branch', data.BRANCH);
                                                handleDeepChange('bank_details', idx, 'bank_address', data.ADDRESS);

                                                toast.success("Bank & Branch Found!", { id: loadToast });

                                                // Automatic Bank Verification Trigger
                                                if (bank.account_number && bank.verification_status !== 'Verified') {
                                                    handleVerifyBank(idx);
                                                }
                                            } else {
                                                toast.error("Invalid IFSC Code", { id: loadToast });
                                            }
                                        } catch (err) {
                                            toast.dismiss(loadToast);
                                        }
                                    }
                                }}
                            />
                        </div>

                        {/* Name as per Passbook - Moved to Position 3 */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">Name as per Passbook</label>
                            <input className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400 uppercase" value={bank.holder_name} onChange={(e) => handleDeepChange('bank_details', idx, 'holder_name', e.target.value)} />
                        </div>

                        {/* Bank Name - Moved to Position 4 */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">Bank Name</label>
                            <input
                                className={`w-full px-3 py-2 bg-white border ${errors[`bank_${idx}_name`] ? 'border-yellow-300 ring-1 ring-yellow-300' : 'border-transparent'} rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900`}
                                value={bank.bank_name}
                                placeholder="Bank Name"
                                onChange={(e) => handleDeepChange('bank_details', idx, 'bank_name', e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">Branch</label>
                            <input
                                className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 placeholder-gray-400"
                                value={bank.branch}
                                placeholder="Auto-filled from IFSC"
                                onChange={(e) => handleDeepChange('bank_details', idx, 'branch', e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] font-bold text-white/80 uppercase tracking-wide">
                                Expiry / Valid {errors[`bank_${idx}_expiry`] && <span className="text-yellow-300 ml-1">({errors[`bank_${idx}_expiry`]})</span>}
                            </label>
                            <div className="flex gap-2">
                                <select
                                    className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 appearance-none"
                                    value={(bank.expiry_date || '').split('/')[0] || ''}
                                    onChange={(e) => {
                                        const parts = (bank.expiry_date || '').split('/');
                                        const mm = e.target.value;
                                        const yyyy = parts[1] || '';
                                        handleDeepChange('bank_details', idx, 'expiry_date', mm && yyyy ? `${mm}/${yyyy}` : (mm ? `${mm}/` : ''));
                                    }}
                                >
                                    <option value="">Month</option>
                                    {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                                        <option key={m} value={m}>{new Date(2000, parseInt(m) - 1).toLocaleString('default', { month: 'short' })}</option>
                                    ))}
                                </select>
                                <select
                                    className="w-full px-3 py-2 bg-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 text-[12.5px] font-bold text-gray-900 appearance-none"
                                    value={(bank.expiry_date || '').split('/')[1] || ''}
                                    onChange={(e) => {
                                        const parts = (bank.expiry_date || '').split('/');
                                        const mm = parts[0] || '';
                                        const yyyy = e.target.value;
                                        handleDeepChange('bank_details', idx, 'expiry_date', mm && yyyy ? `${mm}/${yyyy}` : (yyyy ? `/${yyyy}` : ''));
                                    }}
                                >
                                    <option value="">Year</option>
                                    {Array.from({ length: 20 }, (_, i) => String(2026 + i)).map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3 col-span-full border-t border-white/10 pt-3 mt-1">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    {bank.is_active ? (
                                        <span className="bg-green-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg w-fit uppercase shadow-sm">Active Account</span>
                                    ) : (
                                        <span className="bg-black/30 text-white/80 text-[10px] font-bold px-3 py-1.5 rounded-lg w-fit uppercase border border-white/20">Inactive</span>
                                    )}
                                    {bank.bank_address && (
                                        <span className="text-[9px] text-white/70 max-w-full sm:max-w-[200px] truncate bg-black/10 px-2 py-1 rounded" title={bank.bank_address}>
                                            {bank.bank_address}
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {bank.verification_status === 'Verified' ? (
                                        <div className="flex items-center gap-1.5 bg-green-500/30 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-green-400/50 shadow-sm">
                                            <FiCheckCircle className="shrink-0" /> <span className="whitespace-nowrap">Identity Verified</span>
                                        </div>
                                    ) : bank.verification_status === 'Failed' ? (
                                        <div className="flex items-center gap-1.5 bg-red-500/30 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-red-400/50 shadow-sm">
                                            <FiShield className="shrink-0" /> <span className="whitespace-nowrap">Validation Failed</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5 bg-white/10 text-white/70 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-white/20 animate-pulse">
                                            <FiShield className="shrink-0" /> <span className="whitespace-nowrap">Validation Pending</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {bank.verified_name && (
                                <div className="text-[10px] text-white/90 font-mono bg-black/20 px-3 py-2 rounded-xl w-full text-left sm:text-right border border-white/5">
                                    <span className="opacity-70 uppercase tracking-widest mr-2">Registered Name:</span>
                                    <span className="font-bold">{bank.verified_name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            {(tempData.bank_details || []).length < 2 && (
                <button onClick={() => addRow('bank_details')} className="px-5 py-2 bg-[#C30010] text-white rounded-xl text-xs font-bold hover:bg-[#a0000d] transition-all flex items-center gap-2 w-fit shadow-lg shadow-red-100 uppercase tracking-widest">
                    <FiPlus size={16} /> Add New Account
                </button>
            )}
        </div>
    );

    const getIdentityProofSnapshot = (sourceData, type) => {
        if (type === 'PAN') {
            return {
                number: sourceData.pan_number || '',
                fileUrl: sourceData.pan_file || '',
                localName: sourceData.pan_local_filename || sourceData.pan_filename || '',
            };
        }
        return {
            number: sourceData.aadhaar_number || '',
            fileUrl: sourceData.aadhaar_file || '',
            localName: sourceData.aadhaar_local_filename || sourceData.aadhaar_filename || '',
        };
    };

    const hasIdentityProofRecord = (sourceData, type) => {
        const snap = getIdentityProofSnapshot(sourceData, type);
        return Boolean(snap.number || snap.fileUrl);
    };

    const revertIdentityTypeFromSaved = (type) => {
        if (type === 'PAN') {
            setTempData((prev) => ({
                ...prev,
                pan_number: data.pan_number || '',
                pan_file: data.pan_file || '',
                pan_local_filename: data.pan_local_filename || data.pan_filename || '',
                pan_filename: data.pan_filename || '',
            }));
            setVerificationStatus((prev) => ({
                ...prev,
                PAN: data.pan_number && data.pan_file ? 'verified' : 'idle',
            }));
        } else if (type === 'AADHAAR') {
            setTempData((prev) => ({
                ...prev,
                aadhaar_number: data.aadhaar_number || '',
                aadhaar_file: data.aadhaar_file || '',
                aadhaar_local_filename: data.aadhaar_local_filename || data.aadhaar_filename || '',
                aadhaar_filename: data.aadhaar_filename || '',
            }));
            setVerificationStatus((prev) => ({
                ...prev,
                AADHAAR: data.aadhaar_number && data.aadhaar_file ? 'verified' : 'idle',
            }));
        }
    };

    const startIdentityInlineEdit = (type) => {
        setEditingIdentityType(type);
        setSelectedIdType(type);
        setShowIdentityAddForm(false);
        setVerificationStatus((prev) => ({ ...prev, [type]: 'idle' }));
        setErrors((prev) => {
            const next = { ...prev };
            delete next.identity_required;
            delete next.aadhaar_number;
            delete next.aadhaar_file;
            delete next.pan_number;
            delete next.pan_file;
            return next;
        });
    };

    const cancelIdentityInlineEdit = () => {
        if (editingIdentityType) {
            revertIdentityTypeFromSaved(editingIdentityType);
        }
        setEditingIdentityType(null);
        setSelectedIdType(resolveIdentityProofType(data));
        setErrors((prev) => {
            const next = { ...prev };
            delete next.identity_required;
            delete next.aadhaar_number;
            delete next.aadhaar_file;
            delete next.pan_number;
            delete next.pan_file;
            return next;
        });
    };

    const saveIdentityInlineEdit = async (type) => {
        const d = tempData;
        const rowErrors = {};
        if (type === 'AADHAAR') {
            if (!d.aadhaar_number) rowErrors.aadhaar_number = 'Aadhaar Number Required';
            else if (!validateAadhaar(d.aadhaar_number)) rowErrors.aadhaar_number = 'Invalid Aadhaar Number (12 Digits)';
            if (!d.aadhaar_file) rowErrors.aadhaar_file = 'Aadhaar Document Required';
        } else if (type === 'PAN') {
            if (!d.pan_number) rowErrors.pan_number = 'PAN Number Required';
            else if (!validatePAN(d.pan_number)) rowErrors.pan_number = 'Invalid PAN Number';
            if (!d.pan_file) rowErrors.pan_file = 'PAN Document Required';
        }
        if (Object.keys(rowErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...rowErrors }));
            showValidationToast(rowErrors);
            return false;
        }

        if (wizardOpen) {
            setEditingIdentityType(null);
            setShowIdentityAddForm(false);
            setErrors((prev) => {
                const next = { ...prev };
                delete next.identity_required;
                delete next.aadhaar_number;
                delete next.aadhaar_file;
                delete next.pan_number;
                delete next.pan_file;
                return next;
            });
            toast.success('Identity details updated in draft');
            return true;
        }

        const loadingToast = toast.loading('Saving identity proof...');
        try {
            const payload = JSON.parse(JSON.stringify(tempData));
            await api.post('/employee/details', payload);
            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/details?user_id=${targetId}` : '/employee/details';
            const res = await api.get(url);
            if (res.data?.status && res.data?.data) {
                const fetched = res.data.data;
                setData(fetched);
                setTempData((prev) => ({
                    ...prev,
                    aadhaar_number: fetched.aadhaar_number || '',
                    aadhaar_file: fetched.aadhaar_file || '',
                    pan_number: fetched.pan_number || '',
                    pan_file: fetched.pan_file || '',
                }));
                syncIdentityProofState(fetched);
            }
            setEditingIdentityType(null);
            setShowIdentityAddForm(false);
            setErrors((prev) => {
                const next = { ...prev };
                delete next.identity_required;
                delete next.aadhaar_number;
                delete next.aadhaar_file;
                delete next.pan_number;
                delete next.pan_file;
                return next;
            });
            toast.success('Identity proof saved', { id: loadingToast });
            return true;
        } catch (error) {
            console.error('Identity save failed', error);
            toast.error('Save failed. Please try again.', { id: loadingToast });
            return false;
        }
    };

    const renderIdentityInlineFields = (type, options = {}) => {
        const { showTypeSelect = false, onCancel = null, onSave = null } = options;
        const number = type === 'PAN' ? (tempData.pan_number || '') : type === 'AADHAAR' ? (tempData.aadhaar_number || '') : '';
        const fileUrl = type === 'PAN' ? tempData.pan_file : type === 'AADHAAR' ? tempData.aadhaar_file : '';
        const localName = type === 'PAN'
            ? (tempData.pan_local_filename || 'Uploaded Document')
            : (tempData.aadhaar_local_filename || 'Uploaded Document');
        const numberError = type === 'PAN' ? errors.pan_number : errors.aadhaar_number;
        const fileError = type === 'PAN' ? errors.pan_file : errors.aadhaar_file;
        const activeType = type || selectedIdType;

        return (
            <div className="flex flex-col xl:flex-row xl:items-end gap-4 xl:gap-5 w-full p-4 md:p-5 bg-gray-50/50 border border-gray-100 rounded-xl">
                <div className="w-full xl:flex-1 xl:min-w-0">
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-1 pl-1">Proof Type <span className="text-red-500 font-bold">*</span></label>
                    {showTypeSelect ? (
                        <select
                            value={selectedIdType}
                            onChange={(e) => setSelectedIdType(e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-800 outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] appearance-none cursor-pointer transition-all pr-10"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1rem' }}
                        >
                            <option value="">Select ID Type...</option>
                            {!hasIdentityProofRecord(tempData, 'AADHAAR') && <option value="AADHAAR">Aadhaar</option>}
                            {!hasIdentityProofRecord(tempData, 'PAN') && <option value="PAN">PAN</option>}
                        </select>
                    ) : (
                        <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-[12.5px] font-bold text-gray-800">
                            {type === 'PAN' ? 'PAN' : 'Aadhaar'}
                        </div>
                    )}
                </div>

                <div className="w-full xl:flex-1 xl:min-w-0">
                    <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest mb-1.5 flex items-center gap-1 pl-1">Proof Number <span className="text-red-500">*</span></label>
                    <input
                        type="text"
                        value={number}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (activeType === 'PAN') handleChange({ target: { name: 'pan_number', value: val } });
                            if (activeType === 'AADHAAR') handleChange({ target: { name: 'aadhaar_number', value: val } });
                        }}
                        placeholder={activeType ? `Enter ${activeType} Number` : 'Select Type first'}
                        disabled={!activeType}
                        className={`w-full px-4 py-2 bg-white border ${numberError ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-bold uppercase tracking-widest text-gray-800 placeholder-gray-400 transition-all font-mono disabled:bg-gray-50 disabled:text-gray-400`}
                    />
                </div>

                <div className="w-full xl:flex-1 xl:min-w-0 flex items-end justify-start xl:justify-end gap-2 flex-wrap">
                    {activeType && (
                        <>
                            {!fileUrl ? (
                                <label className={`cursor-pointer px-3 sm:px-4 h-[36px] sm:h-[40px] flex items-center justify-center gap-2 bg-red-50 border-2 ${fileError ? 'border-red-500 ring-1 ring-red-50' : 'border-red-100 text-[#C30010] hover:bg-red-100 hover:border-red-200'} rounded-xl transition-all shadow-sm`}>
                                    <FiUpload className={`text-[14px] sm:text-[16px] ${fileError ? 'text-red-500' : 'text-[#C30010]'}`} />
                                    <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-widest ${fileError ? 'text-red-500' : 'text-[#C30010]'}`}>Upload & Scan *</span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/jpeg,image/png,image/jpg"
                                        onChange={(e) => handleIdentityUpload(activeType, e.target.files[0])}
                                    />
                                </label>
                            ) : (
                                <>
                                    <div className="flex items-center gap-2 px-3 h-[38px] bg-red-50 border border-red-100 rounded-xl text-red-600 min-w-0 flex-1 xl:flex-none xl:max-w-[240px]" title={localName}>
                                        <FiFileText className="shrink-0" size={14} />
                                        <span className="text-xs font-semibold truncate">{localName}</span>
                                    </div>
                                    <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-[38px] w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-[#C30010] hover:bg-red-50 transition-all shadow-sm"
                                        title="View Document"
                                    >
                                        <FiEye size={18} />
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (activeType === 'PAN') {
                                                setTempData((prev) => ({ ...prev, pan_file: '', pan_number: '', pan_filename: '', pan_local_filename: '' }));
                                                setVerificationStatus((prev) => ({ ...prev, PAN: 'idle' }));
                                            }
                                            if (activeType === 'AADHAAR') {
                                                setTempData((prev) => ({ ...prev, aadhaar_file: '', aadhaar_number: '', aadhaar_filename: '', aadhaar_local_filename: '' }));
                                                setVerificationStatus((prev) => ({ ...prev, AADHAAR: 'idle' }));
                                            }
                                        }}
                                        className="h-[38px] w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-red-500 hover:bg-red-50 transition-all shadow-sm"
                                        title="Delete"
                                    >
                                        <FiTrash2 size={18} />
                                    </button>
                                </>
                            )}
                        </>
                    )}
                    {(onCancel || onSave) && (
                        <div className="flex items-center gap-2 ml-auto">
                            {onCancel && (
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    className="px-3 h-[38px] text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                            )}
                            {onSave && (
                                <button
                                    type="button"
                                    onClick={onSave}
                                    className="px-3 h-[38px] text-xs font-bold text-white bg-[#C30010] hover:bg-[#a0000d] rounded-xl shadow-sm transition flex items-center gap-1"
                                >
                                    <FiCheck size={14} /> Save
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderIdentityWizardContent = () => {
        const hasAnyProof = ['PAN', 'AADHAAR'].some((type) => hasIdentityProofRecord(tempData, type));
        const canAddMore = ['PAN', 'AADHAAR'].some((type) => !hasIdentityProofRecord(tempData, type));
        const showAddForm = !hasAnyProof || showIdentityAddForm;

        return (
            <div className="w-full space-y-3">
                {errors.identity_required && !hasAnyProof && (
                    <p className="text-[11px] font-semibold text-red-500 px-1">{errors.identity_required}</p>
                )}

                {['PAN', 'AADHAAR'].map((type) => {
                    if (!hasIdentityProofRecord(tempData, type) && editingIdentityType !== type) return null;
                    const snap = getIdentityProofSnapshot(tempData, type);
                    const isVerified = verificationStatus[type] === 'verified';

                    if (editingIdentityType === type) {
                        return (
                            <div key={type} className="space-y-2">
                                {renderIdentityInlineFields(type, {
                                    showTypeSelect: false,
                                    onCancel: cancelIdentityInlineEdit,
                                    onSave: () => saveIdentityInlineEdit(type),
                                })}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={type}
                            className={`relative p-3 rounded-xl border ${isVerified ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50'}`}
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 rounded-lg bg-white border border-gray-100 shadow-sm text-gray-600 shrink-0">
                                        {type === 'PAN' ? <FiCreditCard size={16} /> : <FaIdCard size={16} />}
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wide">{type}</h4>
                                        <p className="text-[11px] font-mono font-semibold text-gray-600 mt-0.5 truncate">{snap.number || '---'}</p>
                                    </div>
                                    {isVerified && (
                                        <div className="bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow-sm shrink-0">
                                            <FiCheckCircle size={12} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {snap.fileUrl && (
                                        <a
                                            href={snap.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="h-8 w-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-[#C30010] hover:bg-red-50 transition-all shadow-sm"
                                            title="View Document"
                                        >
                                            <FiEye size={15} />
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => startIdentityInlineEdit(type)}
                                        className="h-8 px-3 inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg text-[#C30010] hover:bg-[#ffebee] transition-all shadow-sm text-[11px] font-bold"
                                        title="Edit"
                                    >
                                        <FiEdit2 size={13} /> Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {showAddForm && editingIdentityType === null && (
                    <div className="pt-1">
                        {renderIdentityInlineFields(selectedIdType, { showTypeSelect: true })}
                        {hasAnyProof && (
                            <div className="flex justify-end mt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowIdentityAddForm(false);
                                        setSelectedIdType(resolveIdentityProofType(tempData));
                                    }}
                                    className="text-[11px] font-bold text-gray-500 hover:text-gray-700 px-2 py-1"
                                >
                                    Cancel Add
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {!showAddForm && editingIdentityType === null && canAddMore && (
                    <button
                        type="button"
                        onClick={() => {
                            setShowIdentityAddForm(true);
                            setSelectedIdType('');
                        }}
                        className="px-3 py-1.5 bg-white border-2 border-dashed border-gray-200 rounded-lg text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-1.5 w-fit shadow-sm"
                    >
                        <FiPlus size={14} /> Add Identity Proof
                    </button>
                )}
            </div>
        );
    };

    const renderIdentityEditContent = () => renderIdentityWizardContent();

    const clearWizardRowErrors = (section, idx, currentErrors) => {
        const next = { ...currentErrors };
        Object.keys(next).forEach((key) => {
            if (isWizardRowErrorKey(section, key, idx)) delete next[key];
        });
        return next;
    };

    const isWizardRowEditing = (section, idx) => {
        const field = WIZARD_SECTION_FIELDS[section];
        if (!field) return true;
        const savedLen = (data[field] || []).length;
        if (idx >= savedLen) return true;
        return wizardEditingRows[section]?.has(idx) ?? false;
    };

    const revertWizardRowFromSaved = (section, idx) => {
        const field = WIZARD_SECTION_FIELDS[section];
        const saved = (data[field] || [])[idx];
        if (!saved) return;
        setTempData((prev) => {
            const list = [...(prev[field] || [])];
            list[idx] = JSON.parse(JSON.stringify(saved));
            return { ...prev, [field]: list };
        });
        setErrors((prev) => clearWizardRowErrors(section, idx, prev));
    };

    const startWizardRowEdit = (section, idx) => {
        const field = WIZARD_SECTION_FIELDS[section];
        const savedLen = (data[field] || []).length;
        if (idx >= savedLen) return;

        const current = wizardEditingRows[section] || new Set();
        current.forEach((editIdx) => {
            if (editIdx !== idx && editIdx < savedLen) {
                revertWizardRowFromSaved(section, editIdx);
            }
        });

        setWizardEditingRows((prev) => ({ ...prev, [section]: new Set([idx]) }));
    };

    const WIZARD_GRID_ACADEMIC = 'grid grid-cols-1 md:grid-cols-[7rem_6rem_7rem_minmax(8rem,1fr)_5.5rem_6rem_auto_3rem] md:gap-x-2 md:gap-y-1 md:items-end w-full';
    const WIZARD_GRID_SKILLS = 'grid grid-cols-1 md:grid-cols-[11rem_minmax(8rem,1fr)_8rem_auto_3rem] md:gap-x-2 md:gap-y-1 md:items-end w-full';
    const WIZARD_GRID_CERTIFICATES = 'grid grid-cols-1 md:grid-cols-[7rem_minmax(8rem,1fr)_9rem_8rem_7rem_8rem_auto_3rem] md:gap-x-2 md:gap-y-1 md:items-end w-full';

    const renderWizardReadOnlyField = (label, value, className = '') => (
        <div className={`min-w-0 ${className}`}>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-0.5 block truncate" title={label}>{label}</label>
            <p className="text-[12px] font-semibold text-gray-800 min-h-[30px] py-1 leading-snug break-words">{value || '—'}</p>
        </div>
    );

    const renderWizardDocViewCell = (fileUrl) => (
        <div className="min-w-0 flex flex-col justify-end self-end">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-0.5 block invisible h-[14px] md:visible md:h-auto md:opacity-0" aria-hidden="true">Doc</span>
            <div className="flex items-center h-[32px]">
                {fileUrl ? (
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-[32px] w-[32px] flex items-center justify-center bg-white border border-gray-200 rounded-lg text-[#C30010] hover:bg-[#ffebee] transition-all shadow-sm"
                        title="View Document"
                    >
                        <FiEye size={16} />
                    </a>
                ) : (
                    <div className="h-[32px] w-[32px]" aria-hidden="true" />
                )}
            </div>
        </div>
    );

    const renderWizardRecordActions = (section, idx) => (
        <div className="min-w-0 flex flex-col items-center justify-end self-end">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-0.5 block w-full text-center">Actions</label>
            <div className="flex items-center justify-center h-[32px]">
                {isWizardRowEditing(section, idx) ? (
                    <button
                        type="button"
                        onClick={() => handleWizardRowSave(section, idx)}
                        className="h-[30px] sm:h-[32px] w-[30px] sm:w-[32px] flex items-center justify-center bg-[#C30010] text-white rounded-lg hover:bg-[#a0000d] transition-all shadow-sm"
                        title="Save"
                    >
                        <FiCheck size={16} className="sm:size-[18px]" />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => startWizardRowEdit(section, idx)}
                        className="h-[30px] sm:h-[32px] w-[30px] sm:w-[32px] flex items-center justify-center bg-white border border-gray-200 rounded-lg text-[#C30010] hover:bg-[#ffebee] transition-all shadow-sm"
                        title="Edit"
                    >
                        <FiEdit2 size={16} className="sm:size-[18px]" />
                    </button>
                )}
            </div>
        </div>
    );

    const renderAcademicEditContent = () => (
        <div className="space-y-2">
            <div className="space-y-1.5">
                {getVisibleWizardRows('academic_details', 'academic', isEmptyAcademicRow).map(({ item, idx }) => {
                    const editing = isWizardRowEditing('academic', idx);
                    return (
                        <div key={idx} className={`${WIZARD_GRID_ACADEMIC} p-2 bg-gray-50 border border-gray-200 rounded relative`}>
                            {!editing && (
                                <>
                                    {renderWizardReadOnlyField('Level', item.qualification)}
                                    {['10th', '12th'].includes(item.qualification) ? (
                                        renderWizardReadOnlyField('Board / Stream', item.board, 'md:col-span-2')
                                    ) : (
                                        <>
                                            {renderWizardReadOnlyField('Degree', item.degree)}
                                            {renderWizardReadOnlyField('Specialization', item.specialization)}
                                        </>
                                    )}
                                    {renderWizardReadOnlyField(
                                        ['10th', '12th'].includes(item.qualification) ? 'School Name' : 'College / Institute',
                                        item.institute
                                    )}
                                    {renderWizardReadOnlyField('Passed Out', item.year)}
                                    {renderWizardReadOnlyField(
                                        'Score',
                                        item.percentage
                                            ? `${item.percentage}${item.score_type === 'CGPA' ? ' (CGPA)' : '%'}`
                                            : '—'
                                    )}
                                    {renderWizardDocViewCell(item.file)}
                                    {renderWizardRecordActions('academic', idx)}
                                </>
                            )}
                            {editing && (
                                <>
                                    {/* 1. Qualification Level */}
                                    <div className="min-w-0">
                                        <SearchableSelect
                                            label="Level"
                                            value={item.qualification}
                                            onChange={(val) => handleDeepChange('academic_details', idx, 'qualification', val)}
                                            options={['10th', '12th', 'Diploma', 'UG', 'PG', 'PhD']}
                                            required
                                            error={errors[`ac_qual_${idx}`]}
                                        />
                                    </div>

                                    {/* 2. Board/Degree & Specialization */}
                                    {['10th', '12th'].includes(item.qualification) ? (
                                        <div className="min-w-0 md:col-span-2">
                                            <SearchableSelect
                                                label="Board / Stream"
                                                value={item.board}
                                                onChange={(val) => handleDeepChange('academic_details', idx, 'board', val)}
                                                options={config.boards || []}
                                                required
                                                error={errors[`ac_board_${idx}`]}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="min-w-0">
                                                <SearchableSelect
                                                    label="Degree"
                                                    value={item.degree}
                                                    onChange={(val) => handleDeepChange('academic_details', idx, 'degree', val)}
                                                    options={config.degrees_map?.[item.qualification] || config.degrees || []}
                                                    required
                                                    error={errors[`ac_degree_${idx}`]}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <SearchableSelect
                                                    label="Specialization"
                                                    value={item.specialization}
                                                    onChange={(val) => handleDeepChange('academic_details', idx, 'specialization', val)}
                                                    options={[]}
                                                    placeholder="Ex: CSE"
                                                    required
                                                    hideIcon={true}
                                                    error={errors[`ac_spec_${idx}`]}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* 3. Institute (Searchable for College, Text for School) */}
                                    <div className="min-w-0">
                                        {['10th', '12th'].includes(item.qualification) ? (
                                            <InputField
                                                label="School Name"
                                                value={item.institute}
                                                onChange={(e) => handleDeepChange('academic_details', idx, 'institute', e.target.value)}
                                                required
                                                placeholder="Enter School Name"
                                                error={errors[`ac_institute_${idx}`]}
                                            />
                                        ) : (
                                            <SearchableSelect
                                                label="College / Institute"
                                                value={item.institute}
                                                onChange={(val) => handleDeepChange('academic_details', idx, 'institute', val)}
                                                options={config.colleges}
                                                required
                                                placeholder="Select College..."
                                                error={errors[`ac_institute_${idx}`]}
                                            />
                                        )}
                                    </div>

                                    {/* 4. Year (Select Dropdown) */}
                                    <div className="min-w-0">
                                        <InputField
                                            label="Passed Out"
                                            as="select"
                                            value={item.year}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const dobYear = data.dob ? new Date(data.dob).getFullYear() : null;
                                                if (dobYear && val && parseInt(val) < dobYear + 12) {
                                                    toast.error(`Year too early for DOB (${dobYear})`);
                                                }
                                                handleDeepChange('academic_details', idx, 'year', val);
                                            }}
                                            options={Array.from({ length: 60 }, (_, i) => String(new Date().getFullYear() - i))}
                                            required
                                            error={errors[`ac_year_${idx}`]}
                                        />
                                    </div>

                                    <div className="min-w-0 relative">
                                        <label className="text-[11px] font-bold text-gray-600 uppercase tracking-widest pl-1 mb-1.5 block">
                                            Score <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative flex items-center">
                                            <input
                                                type="number"
                                                value={item.percentage}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const max = item.score_type === 'CGPA' ? 10 : 100;

                                                    // Allow empty string to let user clear input
                                                    if (val === '') {
                                                        handleDeepChange('academic_details', idx, 'percentage', val);
                                                        return;
                                                    }

                                                    const num = parseFloat(val);
                                                    // Strict check: must be a number, non-negative, and within range
                                                    if (!isNaN(num) && num >= 0 && num <= max) {
                                                        handleDeepChange('academic_details', idx, 'percentage', val);
                                                    }
                                                }}
                                                onFocus={() => setFocusedScore(idx)}
                                                onBlur={() => setTimeout(() => setFocusedScore(null), 200)} // Delay to allow click
                                                className={`w-full px-3 py-2 bg-white border ${errors[`ac_score_${idx}`] ? 'border-red-500 ring-1 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C30010]/10 focus:border-[#C30010] text-[12.5px] font-bold text-gray-800 transition-all pr-12`}
                                                placeholder={item.score_type === 'CGPA' ? 'Ex: 9.5' : 'Ex: 95'}
                                                min="0"
                                                max={item.score_type === 'CGPA' ? 10 : 100}
                                                step={item.score_type === 'CGPA' ? "0.01" : "0.1"}
                                            />
                                            <div className="absolute right-1 flex items-center">
                                                <button
                                                    onClick={() => handleDeepChange('academic_details', idx, 'score_type', item.score_type === 'CGPA' ? 'Percentage' : 'CGPA')}
                                                    className="flex items-center justify-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-[10px] font-bold text-gray-700 rounded-lg transition border border-gray-200 h-[24px]"
                                                    title="Toggle Unit"
                                                >
                                                    {item.score_type === 'CGPA' ? 'CGPA' : '%'}
                                                </button>
                                            </div>

                                            {/* Tooltip for Toggle */}
                                            {focusedScore === idx && (
                                                <div className="absolute -top-8 right-0 bg-gray-800 text-white text-[10px] px-2 py-1 rounded shadow-lg z-10 animate-fade-in pointer-events-none whitespace-nowrap">
                                                    Select % or CGPA
                                                    <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
                                                </div>
                                            )}
                                        </div>
                                        {errors[`ac_score_${idx}`] && <span className="text-[9px] text-red-500 font-semibold block mt-1 leading-none animate-pulse">{errors[`ac_score_${idx}`]}</span>}
                                    </div>

                                    {/* 6. Actions (Upload, View, Delete) */}
                                    <div className="min-w-0 flex items-end gap-2 self-end pb-0.5">
                                        {/* Upload Button */}
                                        <label className={`cursor-pointer inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-[34px] sm:h-[38px] border-2 rounded-xl transition-all w-full md:w-auto ${item.file ? 'bg-red-50 border-red-100 text-[#C30010]' : 'bg-red-50 border-red-100 text-[#C30010] hover:bg-red-100 hover:border-red-200'} ${errors[`ac_file_${idx}`] ? 'border-red-500 ring-1 ring-red-50' : ''}`} title={item.file ? "Change Document" : "Upload Document"}>
                                            <FiUpload size={14} className={`sm:size-[16px] ${errors[`ac_file_${idx}`] ? 'text-red-500' : 'text-[#C30010]'}`} />
                                            <span className={`font-bold text-[9px] sm:text-[10px] uppercase tracking-widest ${errors[`ac_file_${idx}`] ? 'text-red-600' : 'text-[#C30010]'}`}>
                                                {item.file ? "Uploaded" : "Upload File *"}
                                            </span>
                                            <input type="file" className="hidden" onChange={(e) => handleDeepFileUpload('academic_details', idx, 'file', e.target.files[0])} accept="image/*,application/pdf" />
                                        </label>

                                        {/* View Button */}
                                        {item.file && (
                                            <a
                                                href={item.file}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="h-[30px] sm:h-[32px] w-[30px] sm:w-[32px] flex items-center justify-center bg-white border border-gray-200 rounded-lg text-[#C30010] hover:bg-[#ffebee] transition-all shadow-sm"
                                                title="View Document"
                                            >
                                                <FiEye size={16} className="sm:size-[18px]" />
                                            </a>
                                        )}

                                        {/* Delete Button */}
                                        <button
                                            onClick={() => removeRow('academic_details', idx)}
                                            className="h-[34px] sm:h-[38px] w-[34px] sm:w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-red-400 hover:text-white hover:bg-[#C30010] hover:border-[#C30010] transition-all shadow-sm shrink-0"
                                            title="Remove"
                                        >
                                            <FiTrash2 size={16} className="sm:size-[18px]" />
                                        </button>
                                    </div>
                                    {renderWizardRecordActions('academic', idx)}
                                    <button
                                        onClick={() => removeRow('academic_details', idx)}
                                        className="md:hidden col-span-full w-full py-2 text-red-500 bg-red-50 rounded border border-red-100 flex items-center justify-center gap-2 text-sm font-semibold mt-1"
                                    >
                                        <FiTrash2 /> Remove Qualification
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <button onClick={() => addWizardRow('academic_details', {}, isEmptyAcademicRow)} className="px-3 py-1.5 bg-white border-2 border-dashed border-gray-200 rounded-lg text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-1.5 w-fit shadow-sm">
                <FiPlus size={14} className="sm:size-[16px]" /> Add Qualification
            </button>
        </div>
    );

    const renderSkillsEditContent = () => (
        <div className="space-y-2">
            <div className="space-y-1.5">
                {getVisibleWizardRows('skills', 'skills', isEmptySkillRow).map(({ item, idx }) => {
                    const editing = isWizardRowEditing('skills', idx);
                    return (
                        <div key={idx} className={`${WIZARD_GRID_SKILLS} p-2 bg-gray-50 border border-gray-200 rounded relative`}>
                            {!editing && (
                                <>
                                    {renderWizardReadOnlyField('Category', item.category)}
                                    {renderWizardReadOnlyField('Skill Name', item.skill)}
                                    {renderWizardReadOnlyField('Proficiency', item.proficiency)}
                                    <div className="hidden md:block" aria-hidden="true" />
                                    {renderWizardRecordActions('skills', idx)}
                                </>
                            )}
                            {editing && (
                                <>
                                    <div className="min-w-0">
                                        <InputField
                                            label="Category"
                                            value={item.category}
                                            onChange={(e) => handleDeepChange('skills', idx, 'category', e.target.value)}
                                            options={config.skill_categories && config.skill_categories.length > 0 ? config.skill_categories : ['Technical', 'Non-Technical', 'Tools', 'Languages']}
                                            as="select"
                                            required
                                            error={errors[`skill_${idx}_category`]}
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <InputField
                                            label="Skill Name"
                                            value={item.skill}
                                            onChange={(e) => handleDeepChange('skills', idx, 'skill', e.target.value)}
                                            placeholder="Ex: React, Photoshop, Leadership"
                                            required
                                            error={errors[`skill_${idx}_name`]}
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <InputField
                                            label="Proficiency"
                                            value={item.proficiency}
                                            onChange={(e) => handleDeepChange('skills', idx, 'proficiency', e.target.value)}
                                            options={['Beginner', 'Intermediate', 'Advanced', 'Expert']}
                                            as="select"
                                            required
                                            error={errors[`skill_${idx}_proficiency`]}
                                        />
                                    </div>
                                    <button
                                        onClick={() => removeRow('skills', idx)}
                                        className="flex items-center justify-center h-[38px] w-[38px] self-end text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                                        title="Remove"
                                    >
                                        <FiTrash2 size={16} />
                                    </button>
                                    {renderWizardRecordActions('skills', idx)}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <button onClick={() => addWizardRow('skills', { category: 'Technical', skill: '', proficiency: 'Intermediate' }, isEmptySkillRow)} className="px-3 py-1.5 bg-white border-2 border-dashed border-gray-200 rounded-lg text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-1.5 w-fit shadow-sm">
                <FiPlus size={14} className="sm:size-[16px]" /> Add New Skill
            </button>
        </div>
    );

    const renderCertificatesEditContent = () => (
        <div className="space-y-2">
            <div className="space-y-1.5">
                {getVisibleWizardRows('certificates', 'certificates', isEmptyCertificateRow).map(({ item, idx }) => {
                    const editing = isWizardRowEditing('certificates', idx);
                    return (
                        <div key={idx} className={`${WIZARD_GRID_CERTIFICATES} p-2 bg-gray-50 border border-gray-200 rounded relative`}>
                            {!editing && (
                                <>
                                    {renderWizardReadOnlyField('Type', item.type)}
                                    {renderWizardReadOnlyField('Title / Name', item.title)}
                                    {renderWizardReadOnlyField('Issuing Authority', item.issuing_authority)}
                                    {renderWizardReadOnlyField('Issue Date', item.issue_date)}
                                    {renderWizardReadOnlyField('Validity', item.type === 'Award' ? '—' : item.validity_type)}
                                    {renderWizardReadOnlyField(
                                        'Expiry Date',
                                        item.type !== 'Award' && item.validity_type === 'Renewable' ? item.expiry_date : '—'
                                    )}
                                    {renderWizardDocViewCell(item.file)}
                                    {renderWizardRecordActions('certificates', idx)}
                                </>
                            )}
                            {editing && (
                                <>
                                    {/* 1. Type */}
                                    <div className="min-w-0">
                                        <InputField
                                            label="Type"
                                            value={item.type}
                                            onChange={(e) => handleDeepChange('certificates', idx, 'type', e.target.value)}
                                            options={['Certificate', 'Award']}
                                            as="select"
                                            required
                                            error={errors[`cert_${idx}_type`]}
                                        />
                                    </div>

                                    {/* 2. Title */}
                                    <div className="min-w-0">
                                        <InputField
                                            label="Title / Name"
                                            value={item.title}
                                            onChange={(e) => handleDeepChange('certificates', idx, 'title', e.target.value)}
                                            required
                                            error={errors[`cert_${idx}_title`]}
                                            placeholder="Title"
                                        />
                                    </div>

                                    {/* 3. Issuing Authority (New Field) */}
                                    <div className="min-w-0">
                                        <InputField
                                            label="Issuing Authority"
                                            value={item.issuing_authority}
                                            onChange={(e) => handleDeepChange('certificates', idx, 'issuing_authority', e.target.value)}
                                            required
                                            error={errors[`cert_${idx}_authority`]}
                                            placeholder="Authority"
                                        />
                                    </div>

                                    {/* 4. Issue Date */}
                                    <div className="min-w-0">
                                        <InputField
                                            label="Issue Date"
                                            type="date"
                                            value={item.issue_date}
                                            onChange={(e) => handleDeepChange('certificates', idx, 'issue_date', e.target.value)}
                                            required
                                            error={errors[`cert_${idx}_issue`]}
                                            maxDate={item.expiry_date && item.validity_type === 'Renewable' ? new Date(Math.min(new Date(), new Date(item.expiry_date))) : new Date()}
                                        />
                                    </div>

                                    {/* 5. Validity Type (Hidden for Awards) */}
                                    {item.type !== 'Award' && (
                                        <div className="min-w-0">
                                            <InputField
                                                label="Validity"
                                                value={item.validity_type}
                                                onChange={(e) => {
                                                    handleDeepChange('certificates', idx, 'validity_type', e.target.value);
                                                    if (e.target.value === 'Perpetual') {
                                                        handleDeepChange('certificates', idx, 'expiry_date', '');
                                                    }
                                                }}
                                                options={['Perpetual', 'Renewable']}
                                                as="select"
                                                required
                                                error={errors[`cert_${idx}_validity`]}
                                            />
                                        </div>
                                    )}

                                    {/* 6. Expiry Date (Conditional: Certificate + Renewable) */}
                                    {item.type !== 'Award' && item.validity_type === 'Renewable' && (
                                        <div className="min-w-0">
                                            <InputField
                                                label="Expiry Date"
                                                type="date"
                                                value={item.expiry_date}
                                                onChange={(e) => handleDeepChange('certificates', idx, 'expiry_date', e.target.value)}
                                                required
                                                error={errors[`cert_${idx}_expiry`]}
                                                minDate={item.issue_date ? new Date(item.issue_date) : null}
                                            />
                                        </div>
                                    )}

                                    {item.type !== 'Award' && item.validity_type !== 'Renewable' && (
                                        <div className="hidden md:block" aria-hidden="true" />
                                    )}

                                    {item.type === 'Award' && (
                                        <>
                                            <div className="hidden md:block" aria-hidden="true" />
                                            <div className="hidden md:block" aria-hidden="true" />
                                        </>
                                    )}

                                    {/* 7. Actions: Upload, View, Delete */}
                                    <div className="min-w-0 flex items-end gap-2 self-end pb-0.5">
                                        {/* Upload */}
                                        <div className="flex flex-col gap-1 items-center">
                                            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-widest pl-1 md:invisible h-0 md:h-auto overflow-hidden">ACT</label>
                                            <label className={`cursor-pointer inline-flex items-center justify-center gap-2 px-3 h-[38px] border-2 rounded-xl transition-all max-w-[160px] ${item.file ? 'bg-red-50 border-red-100 text-[#C30010]' : 'bg-red-50 border-red-100 text-[#C30010] hover:bg-red-100 hover:border-red-200'} ${errors[`cert_${idx}_file`] ? 'border-red-500 ring-1 ring-red-50' : ''}`} title={item.file ? (typeof item.file === 'string' ? item.file.split('/').pop() : item.file.name) : "Upload"}>
                                                <FiUpload className={`${item.file ? "text-[#C30010]" : "text-[#C30010]"} ${errors[`cert_${idx}_file`] ? 'text-red-500' : ''}`} size={16} />
                                                <span className={`truncate max-w-[100px] text-[10px] font-bold uppercase tracking-wider ${item.file ? "text-[#C30010]" : "text-[#C30010]"} ${errors[`cert_${idx}_file`] ? 'text-red-600' : ''}`}>
                                                    {item.file ? (typeof item.file === 'string' ? item.file.split('/').pop() : item.file.name) : "Certificate *"}
                                                </span>
                                                <input type="file" className="hidden" onChange={(e) => handleDeepFileUpload('certificates', idx, 'file', e.target.files[0])} accept="image/*,application/pdf" />
                                            </label>
                                        </div>

                                        {/* View (Eye) */}
                                        {item.file && (
                                            <a href={item.file} target="_blank" rel="noopener noreferrer" className="h-[38px] w-[38px] flex items-center justify-center bg-white text-[#C30010] border border-gray-200 rounded-xl hover:bg-[#ffebee] transition-all shadow-sm mt-auto" title="View Document">
                                                <FiEye size={18} />
                                            </a>
                                        )}

                                        {/* Delete */}
                                        <button
                                            onClick={() => removeRow('certificates', idx)}
                                            className="h-[38px] w-[38px] flex items-center justify-center bg-white border border-gray-200 rounded-xl text-red-500 hover:text-white hover:bg-[#C30010] hover:border-[#C30010] transition-all shadow-sm mt-auto"
                                            title="Remove"
                                        >
                                            <FiTrash2 size={18} />
                                        </button>
                                    </div>
                                    {renderWizardRecordActions('certificates', idx)}
                                    <button
                                        onClick={() => removeRow('certificates', idx)}
                                        className="md:hidden col-span-full w-full py-2 text-red-500 bg-red-50 rounded border border-red-100 flex items-center justify-center gap-2 text-sm font-semibold mt-1"
                                    >
                                        <FiTrash2 /> Remove Entry
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <button onClick={() => addWizardRow('certificates', { type: 'Certificate', title: '', issue_date: '', expiry_date: '', file: '' }, isEmptyCertificateRow)} className="px-3 py-1.5 bg-white border-2 border-dashed border-gray-200 rounded-lg text-[10px] sm:text-xs font-bold text-gray-400 hover:border-[#C30010] hover:text-[#C30010] transition-all flex items-center gap-1.5 w-fit shadow-sm">
                <FiPlus size={14} className="sm:size-[16px]" /> Add New Entry
            </button>
        </div>
    );

    const handleWizardSave = async () => {
        const saved = await handleSave('wizard');
        if (saved && (tempData.certificates || []).length > 2) {
            toast((t) => (
                <div className="flex items-center gap-3">
                    <div className="bg-yellow-100 p-2 rounded-full"><FiStar className="text-yellow-600" size={24} fill="currentColor" /></div>
                    <div>
                        <b className="block text-gray-800">Well Done {data.aadhaar_name?.split(' ')[0]}!</b>
                        <span className="text-xs text-text-500">You&apos;ve added {(tempData.certificates || []).length} achievements. Keep it up!</span>
                    </div>
                    <button onClick={() => toast.dismiss(t.id)} className="text-gray-400 hover:text-gray-600"><FiX /></button>
                </div>
            ), { duration: 5000, position: 'top-center' });
        }
    };

    const clearWizardSectionErrors = (section, currentErrors) => {
        const next = { ...currentErrors };
        const filter = WIZARD_SECTION_EXTRA_ERROR_FILTER[section];
        Object.keys(next).forEach((key) => {
            if (filter?.(key)) delete next[key];
        });
        return next;
    };

    const validateWizardRow = (section, idx, skipSetErrors = false) => {
        const baseErrors = validateSection(section, true);
        const filter = WIZARD_SECTION_EXTRA_ERROR_FILTER[section];
        const extraErrors = filter
            ? Object.fromEntries(
                Object.entries(validateWizardStep3Extras(tempData)).filter(([key]) => filter(key))
            )
            : {};
        const allErrors = { ...baseErrors, ...extraErrors };
        const rowErrors = Object.fromEntries(
            Object.entries(allErrors).filter(([key]) => isWizardRowErrorKey(section, key, idx))
        );
        if (!skipSetErrors) {
            setErrors((prev) => {
                const cleared = clearWizardSectionErrors(section, prev);
                return { ...cleared, ...rowErrors };
            });
        }
        return rowErrors;
    };

    const validateWizardSection = (section) => {
        const baseErrors = validateSection(section, true);
        const filter = WIZARD_SECTION_EXTRA_ERROR_FILTER[section];
        const extraErrors = filter
            ? Object.fromEntries(
                Object.entries(validateWizardStep3Extras(tempData)).filter(([key]) => filter(key))
            )
            : {};
        const merged = { ...baseErrors, ...extraErrors };
        setErrors(merged);
        return merged;
    };

    const handleWizardSectionCancel = (section) => {
        const field = WIZARD_SECTION_FIELDS[section];
        if (!field) return;
        setTempData((prev) => ({
            ...prev,
            [field]: JSON.parse(JSON.stringify(data[field] || [])),
        }));
        setErrors((prev) => clearWizardSectionErrors(section, prev));
    };

    const handleWizardSectionSave = async (section, options = {}) => {
        const { skipValidation = false } = options;
        if (!skipValidation) {
            const validationErrors = validateWizardSection(section);
            if (Object.keys(validationErrors).length > 0) {
                showValidationToast(validationErrors);
                return false;
            }
        }

        const field = WIZARD_SECTION_FIELDS[section];

        if (wizardOpen) {
            setErrors((prev) => clearWizardSectionErrors(section, prev));
            return true;
        }

        const payload = stripEmptyWizardStep3Rows(JSON.parse(JSON.stringify(tempData)));
        const loadingToast = toast.loading('Saving changes...');

        try {
            await api.post('/employee/details', payload);

            const targetId = getQueryParam('user_id');
            const url = targetId ? `/employee/details?user_id=${targetId}` : `/employee/details`;
            const res = await api.get(url);

            if (res.data.status && res.data.data) {
                const fetchedData = res.data.data;
                setData(fetchedData);
                if (field) {
                    setTempData((prev) => ({
                        ...prev,
                        [field]: JSON.parse(JSON.stringify(fetchedData[field] || [])),
                    }));
                }
            }

            setErrors((prev) => clearWizardSectionErrors(section, prev));
            toast.success('Saved successfully', { id: loadingToast });

            if (section === 'certificates' && (tempData.certificates || []).length > 2) {
                toast((t) => (
                    <div className="flex items-center gap-3">
                        <div className="bg-yellow-100 p-2 rounded-full"><FiStar className="text-yellow-600" size={24} fill="currentColor" /></div>
                        <div>
                            <b className="block text-gray-800">Well Done {data.aadhaar_name?.split(' ')[0]}!</b>
                            <span className="text-xs text-text-500">You&apos;ve added {(tempData.certificates || []).length} achievements. Keep it up!</span>
                        </div>
                        <button onClick={() => toast.dismiss(t.id)} className="text-gray-400 hover:text-gray-600"><FiX /></button>
                    </div>
                ), { duration: 5000, position: 'top-center' });
            }

            return true;
        } catch (error) {
            console.error('Wizard section save error', error);
            if (error.response && error.response.status === 422 && error.response.data.errors) {
                setErrors(error.response.data.errors);
                toast.error((t) => (
                    <div className="flex flex-col gap-1">
                        <span className="font-bold text-red-600">Server Validation Failed:</span>
                        <ul className="list-disc pl-4 text-xs text-gray-600 mt-1 space-y-0.5">
                            {Object.values(error.response.data.errors).slice(0, 3).map((msg, i) => <li key={i}>{msg}</li>)}
                        </ul>
                    </div>
                ), { id: loadingToast, duration: 5000 });
            } else {
                toast.error('Save failed. Please try again.', { id: loadingToast });
            }
            return false;
        }
    };

    const handleWizardRowSave = async (section, idx) => {
        const rowErrors = validateWizardRow(section, idx);
        if (Object.keys(rowErrors).length > 0) {
            showValidationToast(rowErrors);
            return false;
        }

        const success = await handleWizardSectionSave(section, { skipValidation: true });
        if (success) {
            setWizardEditingRows((prev) => {
                const next = new Set(prev[section]);
                next.delete(idx);
                return { ...prev, [section]: next };
            });
        }
        return success;
    };

    if (loading) return (
        <div className="w-full min-h-screen flex items-center justify-center bg-gray-50">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <>
            {cropperImage && (
                <ImageCropper
                    imageSrc={cropperImage}
                    onCancel={() => setCropperImage(null)}
                    onCropComplete={(blob) => {
                        setCropperImage(null);
                        const file = new File([blob], "profile_pic.jpg", { type: "image/jpeg" });
                        handleFileUpload('profile_image', file);
                    }}
                />
            )}
            {/* Shared modals — render above Update Form wizard */}
            {/* Full Profile Image Pop-up Modal */}
            {photoPreviewUrl && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-md z-[600] flex items-center justify-center p-4 overflow-y-auto animate-fade-in"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-2xl max-w-4xl max-h-[90vh] shadow-2xl border border-gray-100 overflow-hidden relative flex flex-col items-center p-4 sm:p-6 my-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="w-full flex items-center justify-between pb-3 mb-3 border-b border-gray-100 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-red-50 text-[#C30010] rounded-xl">
                                    <FiUser size={20} />
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-bold text-gray-900 uppercase tracking-tight">
                                        {data?.aadhaar_name || 'Employee Photo'}
                                    </h3>
                                    <span className="text-[10px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-100 uppercase tracking-widest inline-block mt-0.5">
                                        Full Image Preview
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setPhotoPreviewUrl(null)}
                                className="p-2 bg-gray-100 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all shadow-sm shrink-0"
                                title="Close Preview"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Centered Full Image Display */}
                        <div className="relative flex items-center justify-center w-full max-h-[72vh] overflow-hidden rounded-xl bg-slate-900/5 p-2 border border-slate-100">
                            <img
                                src={photoPreviewUrl}
                                alt="Employee Profile Full View"
                                className="max-w-full max-h-[68vh] w-auto h-auto object-contain rounded-lg shadow-md"
                            />
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Relieving Reason Pop-up Modal */}
            {showRelievingReasonModal && (
                <div 
                    className="fixed inset-0 bg-black/70 backdrop-blur-md z-[600] flex items-center justify-center p-4 overflow-y-auto animate-fade-in"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-gray-100 flex flex-col gap-4 relative my-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-red-50 text-[#C30010] rounded-xl">
                                    <FiFileText size={20} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-900 uppercase tracking-tight">
                                        Reason for Relieving
                                    </h3>
                                    <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded border border-gray-200 uppercase tracking-widest inline-block mt-0.5">
                                        {data?.aadhaar_name || 'Offboarding Reason'}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowRelievingReasonModal(false)}
                                className="p-2 bg-gray-100 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all shadow-sm shrink-0"
                                title="Close"
                            >
                                <FiX size={18} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="bg-gray-50/90 p-4 rounded-xl border border-gray-200/80 max-h-[60vh] overflow-y-auto shadow-inner">
                            <p className="text-xs sm:text-sm text-gray-800 leading-relaxed font-medium whitespace-pre-wrap">
                                {data.relieving_reason || 'No reason provided.'}
                            </p>
                        </div>


                    </motion.div>
                </div>
            )}

            {/* Promotion Confirmation Modal */}
            {promotionModal.open && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-auto overflow-hidden border border-gray-100"
                    >
                        <div className="p-4 sm:p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                    <FiActivity size={20} />
                                </div>
                                <h3 className="text-base sm:text-lg font-bold text-gray-800 uppercase tracking-tight">Confirm Promotion</h3>
                            </div>
                            <button onClick={() => setPromotionModal({ ...promotionModal, open: false })} className="text-gray-400 hover:text-gray-600 transition">
                                <FiX size={20} />
                            </button>
                        </div>
                        <div className="p-5 sm:p-8 space-y-4 sm:space-y-6">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">New Designation</span>
                                <div className="text-lg sm:text-xl font-extrabold text-[#C30010] tracking-tight truncate">{promotionModal.newRole}</div>
                            </div>
                            <div className="bg-blue-50 border border-blue-100 p-3 sm:p-4 rounded-xl flex gap-3">
                                <FiCalendar className="text-blue-500 shrink-0 mt-0.5" size={16} />
                                <div className="text-[10px] sm:text-xs text-blue-700 leading-relaxed font-bold">
                                    The previous role will be marked as "Completed". Verify the end date below.
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <InputField
                                    label="Prev Role Start"
                                    type="date"
                                    value={promotionModal.prevRoleStartDate}
                                    onChange={(e) => setPromotionModal({ ...promotionModal, prevRoleStartDate: e.target.value })}
                                    required
                                />
                                <InputField
                                    label="Prev Role End"
                                    type="date"
                                    value={promotionModal.prevRoleEndDate}
                                    onChange={(e) => setPromotionModal({ ...promotionModal, prevRoleEndDate: e.target.value })}
                                    required
                                />
                                <div className="col-span-full">
                                    <InputField
                                        label="New Role Start Date"
                                        type="date"
                                        value={promotionModal.startDate}
                                        onChange={(e) => setPromotionModal({ ...promotionModal, startDate: e.target.value })}
                                        required
                                        minDate={tempData.joining_date ? new Date(tempData.joining_date) : null}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100 flex gap-2 sm:gap-3">
                            <button
                                onClick={() => setPromotionModal({ ...promotionModal, open: false })}
                                className="flex-1 px-3 py-2 sm:py-2.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmPromotion}
                                className="flex-1 px-3 py-2 sm:py-2.5 text-xs font-bold text-white bg-[#C30010] rounded-xl hover:bg-[#a0000d] shadow-lg shadow-red-100 transition flex items-center justify-center gap-1.5"
                            >
                                Update Role <FiChevronRight className="hidden sm:block" />
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Bank Activation Modal */}
            {bankConfirmModal.open && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl w-full max-w-sm my-auto shadow-2xl overflow-hidden border border-gray-100"
                    >
                        <div className="p-6 text-center">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-50 text-[#C30010] rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiCreditCard size={28} />
                            </div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 uppercase tracking-tight">Activate Bank Account?</h3>
                            <p className="text-[11px] sm:text-sm text-gray-500 leading-relaxed mb-6 font-medium">
                                Is this new bank account the <strong>CURRENT ACTIVE</strong> account? Only one account can be active at a time.
                            </p>
                            <div className="flex flex-col gap-2.5">
                                <button
                                    onClick={() => {
                                        const resolve = bankConfirmModal.resolve;
                                        setBankConfirmModal({ open: false, resolve: null });
                                        resolve(true);
                                    }}
                                    className="w-full py-2.5 sm:py-3 bg-[#C30010] text-sm text-white rounded-xl font-bold shadow-lg shadow-red-100 hover:bg-[#a0000d] transition-all uppercase tracking-widest"
                                >
                                    Set as Active
                                </button>
                                <button
                                    onClick={() => {
                                        const resolve = bankConfirmModal.resolve;
                                        setBankConfirmModal({ open: false, resolve: null });
                                        resolve(false);
                                    }}
                                    className="w-full py-2.5 sm:py-3 bg-gray-100 text-sm text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-all uppercase tracking-widest"
                                >
                                    Keep Inactive
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Identity Verification Pop-up Modal */}
            {identityModal.open && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white rounded-2xl w-full max-w-md my-auto shadow-2xl border border-gray-100 overflow-hidden relative"
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => {
                                setIdentityModal({ open: false, type: null, file: null, preview: null });
                                scannerActiveRef.current = false;
                                if (identityModal.type) {
                                    setVerificationStatus(prev => ({ ...prev, [identityModal.type]: 'idle' }));
                                }
                            }}
                            className="absolute top-4 right-4 z-20 p-2 bg-white/80 backdrop-blur-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all shadow-sm border border-gray-100"
                            title="Close Scanner"
                        >
                            <FiX size={18} />
                        </button>
                        <div className="p-5 sm:p-6">
                            <div className="flex items-center gap-3 mb-6 pr-8">
                                <div className="p-2 sm:p-2.5 bg-red-50 text-[#C30010] rounded-lg">
                                    {identityModal.type === 'PAN' ? <FiCreditCard size={20} /> : <FaIdCard size={20} />}
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-bold text-gray-900 uppercase tracking-tight">{identityModal.type} Verification</h3>
                                    <div className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded inline-block font-black tracking-widest mt-0.5">GOVT. ISSUED ID</div>
                                </div>
                            </div>
                            {/* Scanning Area */}
                            <div className="relative rounded-xl overflow-hidden border border-slate-100 mb-6 bg-slate-50 min-h-[160px] max-h-[300px] shadow-inner">
                                <img src={identityModal.preview} alt="ID Preview" className="w-full h-full object-contain" />
                                {/* Scan Line Animation */}
                                {verificationStatus[identityModal.type] === 'scanning' && (
                                    <div className="absolute inset-0 z-10 w-full h-full">
                                        <motion.div
                                            className="w-full h-1 bg-gradient-to-r from-transparent via-[#C30010] to-transparent shadow-[0_0_15px_rgba(195,0,16,0.6)]"
                                            animate={{ top: ['0%', '100%', '0%'] }}
                                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                            style={{ position: 'absolute' }}
                                        />
                                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/80 text-white text-[8px] font-black px-3 py-1.5 rounded-full backdrop-blur-md flex items-center gap-2 tracking-widest uppercase">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                            Reading Document...
                                        </div>
                                    </div>
                                )}
                                {verificationStatus[identityModal.type] === 'verified' && (
                                    <div className="absolute inset-0 bg-green-500/10 backdrop-blur-[1px] flex items-center justify-center">
                                        <div className="bg-white/90 px-4 py-2 rounded-xl shadow-xl flex items-center gap-2 border border-green-100 animate-bounce">
                                            <FiCheckCircle className="text-green-500" size={20} />
                                            <span className="text-[10px] font-black text-green-600 uppercase tracking-widest">Extraction Successful</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Extracted Data Fields */}
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase block mb-1.5 tracking-widest pl-1">
                                        Extracted {identityModal.type} Number
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            value={scannedData[identityModal.type]?.number || ''}
                                            readOnly
                                            className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-slate-700 font-mono font-black tracking-[0.2em] text-sm sm:text-lg group-hover:border-red-100 transition-all outline-none"
                                            placeholder="Scanning ID..."
                                        />
                                        {verificationStatus[identityModal.type] === 'verified' && <FiLock className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}


            {!wizardOpen && (
                <div className="w-full bg-[#F8FAFC] px-4 py-4 font-sans text-sm text-gray-700">
                    <div className="w-full">

                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-6 border-b pb-4 px-4 lg:px-0 gap-4">
                            <div className="w-full sm:w-auto">
                                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-800 tracking-tight flex items-center gap-3">
                                    <span className="bg-gradient-to-r from-red-600 to-red-800 text-transparent bg-clip-text">Employee Profile</span>
                                    {globalUser && isReadOnly && <span className="text-[10px] sm:text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded border border-gray-300">Read Only</span>}
                                </h1>
                                <p className="text-gray-500 mt-1 flex items-center gap-2 text-xs sm:text-sm"> Manage and view detailed employee information safely. </p>
                            </div>
                            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                                {getQueryParam('user_id') && (
                                    <button onClick={() => navigate('/home?tab=employees')} className="flex-1 sm:flex-none flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 transition font-bold text-xs sm:text-sm px-3 py-2 rounded-lg bg-white border border-gray-200 sm:border-transparent hover:bg-gray-100">
                                        <FiChevronLeft /> Back
                                    </button>
                                )}
                                {roleId !== 16 && (
                                    <button onClick={() => navigate('/org')} className="flex items-center gap-2 text-white bg-red-600 hover:bg-red-700 transition font-bold px-4 py-2 rounded-lg shadow-md shadow-purple-100">
                                        <FiUsers /> Org Chart
                                    </button>
                                )}
                            </div>
                        </div>


                        <div id="employee-details-container">
                            <>
                                <div className="space-y-4 mt-4">

                                    {/* ROW 1: Personal Information Card (View Mode) */}
                                    <SectionCard
                                        title="Personal Information"
                                        icon={FiUser}
                                        isEditing={false}
                                        showUpdateOnView={!isReadOnly}
                                        onOpenWizard={openUpdateWizard}
                                        compact
                                    >
                                        <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
                                            {/* Profile sidebar */}
                                            <div className="lg:w-36 shrink-0 flex flex-col items-center text-center">
                                                <div 
                                                    className={`w-32 h-32 rounded-full bg-red-50 border-2 border-red-100 flex items-center justify-center overflow-hidden shadow-inner mb-2 group relative ${data.profile_image ? 'cursor-pointer hover:border-red-400 hover:shadow-md transition-all' : ''}`}
                                                    onDoubleClick={() => data.profile_image && setPhotoPreviewUrl(data.profile_image)}
                                                    title={data.profile_image ? "Double click to view full image" : ""}
                                                >
                                                    {data.profile_image ? (
                                                        <img src={data.profile_image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" alt="Profile" />
                                                    ) : (
                                                        <span className="text-2xl font-black text-[#C30010]">
                                                            {data.aadhaar_name ? data.aadhaar_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'EM'}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide leading-snug">
                                                    {globalUser && Number(globalUser.role_id || globalUser.user_role_id) === 1
                                                        ? (data.official_job_role || data.job_role || '—')
                                                        : (data.job_role || '—')}
                                                </div>
                                                {globalUser && Number(globalUser.role_id || globalUser.user_role_id) === 1 && data.official_job_role && data.job_role && data.job_role !== data.official_job_role && (
                                                    <span className="text-[10px] text-gray-400 line-through decoration-gray-300 mt-0.5">{data.job_role}</span>
                                                )}

                                                {data.reporting_manager && (
                                                    <div className="text-[9px] text-gray-400 font-bold uppercase mt-1.5 tracking-wide leading-relaxed">
                                                        Reports to:
                                                        <span className="block text-gray-600 font-bold normal-case mt-0.5">{data.reporting_manager}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Main details */}
                                            <div className="flex-1 min-w-0">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2">
                                                    <Field compact label="Full Name" value={data.aadhaar_name || '—'} />
                                                    <div>
                                                        {!showJobRoleEditPopover ? (
                                                            <Field
                                                                compact
                                                                label="Job Role"
                                                                value={
                                                                    globalUser && Number(globalUser.role_id || globalUser.user_role_id) === 1
                                                                        ? (data.official_job_role || data.job_role || '—')
                                                                        : (data.job_role || '—')
                                                                }
                                                                onEditClick={
                                                                    globalUser && Number(globalUser.role_id || globalUser.user_role_id) === 1
                                                                        ? () => {
                                                                            setEditedJobRole(data.official_job_role || data.job_role || '');
                                                                            setShowJobRoleEditPopover(true);
                                                                        }
                                                                        : null
                                                                }
                                                            />
                                                        ) : (
                                                            <div
                                                                ref={jobRolePopoverRef}
                                                                className="p-3 bg-red-50/50 border border-red-100 rounded-xl relative w-full"
                                                            >
                                                                <div className="absolute -top-2 left-3 bg-[#C30010] text-white text-[7px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-widest shadow-sm">
                                                                    Admin Only
                                                                </div>
                                                                <div className="flex gap-3 items-end">
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="block text-[8px] font-black text-gray-500 uppercase tracking-widest mb-1 mt-1">
                                                                            Official / Admin Assigned Role
                                                                        </span>
                                                                        <SearchableSelect
                                                                            value={editedJobRole}
                                                                            onChange={(val) => setEditedJobRole(val)}
                                                                            options={config.designations || []}
                                                                            placeholder="Select or Type Official Role"
                                                                        />
                                                                        <p className="text-[8.5px] text-gray-400 mt-1 italic font-semibold leading-tight">
                                                                            * This role will take precedence in reports & ID Cards if set.
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-1.5 shrink-0 mb-1">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setShowJobRoleEditPopover(false)}
                                                                            className="px-2.5 py-1 text-[10px] font-bold text-gray-600 hover:bg-gray-50 border border-gray-200 rounded-lg transition-all bg-white shadow-sm"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={handleSaveJobRoleQuickEdit}
                                                                            className="px-3 py-1 text-[10px] font-bold text-white bg-[#C30010] hover:bg-[#9a000c] rounded-lg shadow-sm transition-all"
                                                                        >
                                                                            Save
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Field compact label="DOB" value={data.dob ? format(new Date(data.dob), 'dd/MM/yyyy') : '—'} />
                                                    <Field compact label="Blood Group" value={data.blood_group || '—'} />
                                                    <Field compact label="Mobile" value={data.mobile ? (data.mobile.startsWith('+') ? data.mobile : `+91 ${data.mobile}`) : '—'} type="tel" />
                                                    <Field compact label="Email" value={data.personal_email || '—'} />
                                                    <Field compact label="Joining Date" value={data.joining_date ? format(new Date(data.joining_date), 'dd/MM/yyyy') : '—'} />
                                                    <Field compact label="Relieving Date" value={data.relieving_date ? format(new Date(data.relieving_date), 'dd/MM/yyyy') : '—'} />
                                                    <Field compact label="Experience (eNova)" value={getExperience() || '—'} />

                                                    <div 
                                                        onDoubleClick={() => data.relieving_reason && setShowRelievingReasonModal(true)}
                                                        title={data.relieving_reason ? "Double click to view full reason" : ""}
                                                        className={data.relieving_reason ? "cursor-pointer group" : ""}
                                                    >
                                                        <Field 
                                                            compact 
                                                            label={
                                                                <span>
                                                                    Reason for Relieving{' '}
                                                                    <span className="text-[8px] text-[#C30010] font-bold normal-case tracking-normal italic">
                                                                        (Note: Double click to view full reason)
                                                                    </span>
                                                                </span>
                                                            } 
                                                            value={data.relieving_reason || '—'} 
                                                        />
                                                    </div>
                                                    <Field compact label="PF Number" value={data.pf_number || '—'} />
                                                    <Field compact label="ESI Number" value={data.esi_number || '—'} />
                                                </div>

                                                <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                                                    <Field compact wrap label="Permanent Address" value={data.address_permanent || '—'} />
                                                    <Field compact wrap label="Communication Address" value={data.address_communication || '—'} />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                                                    {[
                                                        { key: 'father', label: 'Father' },
                                                        { key: 'mother', label: 'Mother' },
                                                        { key: 'spouse', label: 'Spouse' },
                                                    ].map(({ key, label }) => {
                                                        const member = data.family_details?.[key] || {};
                                                        const contact = member.contact
                                                            ? (member.contact.startsWith('+') ? member.contact : `+91 ${member.contact}`)
                                                            : null;

                                                        return (
                                                            <div key={key} className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
                                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
                                                                <p className="text-sm font-bold text-gray-800 mt-1">{member.name || '—'}</p>
                                                                {contact ? (
                                                                    <a
                                                                        href={`tel:${contact.replace(/\s+/g, '')}`}
                                                                        className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline mt-1"
                                                                    >
                                                                        <FiPhone size={11} />
                                                                        {contact}
                                                                    </a>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1 text-xs text-gray-300 mt-1">
                                                                        <FiPhone size={11} />
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </SectionCard>

                                    {/* Experience card (full width, shown conditionally) */}
                                    {/* Role History / Experience Progression - Show only if 2+ roles exist */}
                                    {((tempData.role_history || []).length > 1 ||
                                        (data.role_history || []).length > 1) && (
                                            <SectionCard
                                                title="Experience & Role Progression"
                                                icon={FiActivity}
                                                isEditing={false}
                                            >
                                                <div className="w-full">
                                                    {(data.role_history || []).length > 0 ? (
                                                        <>
                                                            <div className="w-full overflow-x-auto pb-1 -mx-1 px-1">
                                                                <div
                                                                    className="flex items-stretch w-full min-w-full"
                                                                    style={{ minWidth: `${Math.max((data.role_history || []).length, 1) * 220}px` }}
                                                                >
                                                                    {[...(data.role_history || [])].map((role, idx, arr) => {
                                                                        const isActive = idx === arr.length - 1 && !role.end_date;
                                                                        const isFirst = idx === 0;
                                                                        const isLast = idx === arr.length - 1;

                                                                        return (
                                                                            <div
                                                                                key={`${role.job_role}-${role.start_date}-${idx}`}
                                                                                className="flex flex-1 min-w-[200px] basis-0 flex-col"
                                                                            >
                                                                                <div className="flex items-center w-full px-1">
                                                                                    <div className={`h-0.5 flex-1 transition-colors ${isFirst ? 'bg-transparent' : isActive ? 'bg-[#f3c7cc]' : 'bg-gray-200'}`} />
                                                                                    <div
                                                                                        className={`relative shrink-0 w-7 h-7 rounded-full border-2 bg-white flex items-center justify-center z-10 transition-all ${isActive
                                                                                            ? 'border-[#C30010] shadow-md shadow-red-100 scale-110'
                                                                                            : 'border-gray-300 group-hover:border-gray-400'
                                                                                            }`}
                                                                                    >
                                                                                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-[#C30010] animate-pulse' : 'bg-gray-400'}`} />
                                                                                    </div>
                                                                                    <div className={`h-0.5 flex-1 transition-colors ${isLast ? 'bg-transparent' : 'bg-gray-200'}`} />
                                                                                </div>

                                                                                <div className="px-1 pt-3 flex-1 flex">
                                                                                    <div
                                                                                        className={`w-full flex flex-col p-3 rounded-lg border min-h-[132px] transition-all ${isActive
                                                                                            ? 'bg-[#fff7f8] border-[#C30010] shadow-md ring-2 ring-[#C30010]/15'
                                                                                            : 'bg-gray-50 border-gray-100 shadow-sm hover:border-gray-200'
                                                                                            }`}
                                                                                    >
                                                                                        <div className="flex justify-between items-start gap-2 mb-2">
                                                                                            <div className="min-w-0">
                                                                                                <h4 className={`font-bold text-sm leading-snug truncate ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                                                                                                    {role.job_role}
                                                                                                </h4>
                                                                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 truncate">
                                                                                                    eNova Software and Hardware Solutions
                                                                                                </p>
                                                                                            </div>
                                                                                            {isActive && (
                                                                                                <span className="shrink-0 px-2 py-0.5 bg-[#ffebee] text-[#C30010] text-[9px] font-bold rounded uppercase tracking-tighter border border-red-100 shadow-sm">
                                                                                                    Current
                                                                                                </span>
                                                                                            )}
                                                                                        </div>

                                                                                        <div className="mt-auto space-y-2">
                                                                                            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-bold uppercase tracking-wide">
                                                                                                <FiCalendar className={`shrink-0 ${isActive ? 'text-[#C30010]' : 'text-gray-400'}`} size={12} />
                                                                                                <span className="truncate">
                                                                                                    {role.start_date ? format(new Date(role.start_date), 'MMM yyyy') : '-'} — {role.end_date ? format(new Date(role.end_date), 'MMM yyyy') : 'Present'}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-white/80 text-gray-600 rounded border border-gray-100 text-[10px] font-bold uppercase tracking-wide w-fit">
                                                                                                <FiActivity className="text-gray-400 shrink-0" size={12} />
                                                                                                <span>{role.duration_text || getDurationText(role.start_date, role.end_date)}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>

                                                            {data.total_experience_text && (
                                                                <div className="mt-5 pt-4 border-t flex items-center justify-between gap-3">
                                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Corporate Tenure</span>
                                                                    <span className="text-xs font-bold text-gray-800 bg-white border px-3 py-1 rounded-full shadow-sm whitespace-nowrap">
                                                                        {data.total_experience_text}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="text-gray-400 text-xs italic py-4">No career progression history found.</div>
                                                    )}
                                                </div>
                                            </SectionCard>
                                        )}

                                    {/* ROW 2: Bank | Community | Health | Identity */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4 items-stretch">
                                        {/* 4. Bank Details */}
                                        <SectionCard title="Bank Details" icon={FiCreditCard} summary isEditing={false}>
                                            <div className="space-y-2">
                                                {(data.bank_details || []).map((b, i) => (
                                                    <div key={i} className={`p-3 rounded-lg shadow-sm relative overflow-hidden bg-gradient-to-r ${getBankColor(b.bank_name)} text-white ${b.is_active ? 'ring-2 ring-offset-1 ring-green-500' : 'opacity-80 grayscale-[0.5]'}`}>
                                                        <div className="relative z-10">
                                                            <div className="flex justify-between items-start gap-2">
                                                                <div className="text-[10px] text-white/80 uppercase tracking-widest truncate">{b.bank_name}</div>
                                                                {b.is_active && <span className="bg-white/20 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border border-white/30 backdrop-blur-sm shrink-0">Active</span>}
                                                            </div>
                                                            <div className="text-base font-mono my-1.5 tracking-wider truncate">{b.account_number}</div>
                                                            <div className="flex justify-between gap-2 text-[10px] text-white/90">
                                                                <span className="truncate">{b.holder_name}</span>
                                                                <div className="text-right shrink-0">
                                                                    <div>{b.ifsc}</div>
                                                                    {b.expiry_date && <div className="text-[9px] text-white/70">Valid: {b.expiry_date}</div>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <FiCreditCard className="absolute -bottom-3 -right-3 text-white opacity-20" size={72} />
                                                    </div>
                                                ))}
                                            </div>
                                        </SectionCard>
                                        <SectionCard title="Community & Languages" icon={FiGlobe} summary isEditing={false}>
                                            <div className="space-y-1.5">
                                                <Field compact label="Community" value={data.community} />
                                                <Field compact label="Caste" value={data.caste} />
                                                <Field compact label="Religion" value={data.religion} />
                                                <div className="pt-1.5 border-t border-gray-100">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Languages (Speak)</span>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {(data.languages_speak || []).map((l, i) => <span key={i} className="px-1.5 py-0.5 bg-[#ffebee] text-[#C30010] text-[10px] rounded border border-red-100">{l.trim()}</span>)}
                                                    </div>
                                                </div>
                                            </div>
                                        </SectionCard>
                                        <SectionCard title="Health & Insurance" icon={FiActivity} summary isEditing={false}>
                                            <div className="space-y-2 h-full flex flex-col">
                                                <div>
                                                    <span className="text-[9px] uppercase text-gray-400 font-bold tracking-wide">Health Conditions</span>
                                                    <p className="text-xs text-gray-800 mt-0.5 leading-snug">{data.health_conditions || 'None'}</p>
                                                </div>
                                                <div className="pt-1.5 border-t border-gray-100 mt-auto">
                                                    <span className="text-[9px] uppercase text-gray-400 font-bold tracking-wide block mb-1">Insurance</span>
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${data.insurance_opt === 'OPT-IN' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{data.insurance_opt}</span>
                                                    {data.insurance_opt === 'OPT-IN' && (
                                                        <div className="mt-1.5 space-y-0.5">
                                                            <div className="text-[11px] text-gray-700 font-semibold leading-snug">
                                                                Nominee: <span className="text-gray-900">{data.nominee_name}</span> ({data.nominee_relationship})
                                                            </div>
                                                            {data.nominee_phone && (
                                                                <a href={`tel:${data.nominee_phone.replace(/\s+/g, '')}`} className="inline-flex items-center gap-1 text-[10px] text-blue-600 font-bold hover:text-blue-700 transition">
                                                                    <FiPhone size={10} />
                                                                    <span>{data.nominee_phone}</span>
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </SectionCard>

                                        <SectionCard
                                            title="Identity Proofs (KYC)"
                                            icon={FiShield}
                                            summary
                                            isEditing={false}
                                        >
                                            <div className="space-y-2">
                                                {['PAN', 'AADHAAR'].map((type) => {
                                                    const fileUrl = type === 'PAN' ? data.pan_file : data.aadhaar_file;
                                                    const number = type === 'PAN' ? data.pan_number : data.aadhaar_number;
                                                    const isVerified = verificationStatus[type] === 'verified';

                                                    if (!number && !fileUrl) return null;

                                                    return (
                                                        <div key={type} className={`relative p-2.5 rounded-lg border ${isVerified ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50'}`}>
                                                            <div className="flex justify-between items-start gap-2">
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <div className="p-1.5 rounded-lg bg-white border border-gray-100 shadow-sm text-gray-600 shrink-0">
                                                                        {type === 'PAN' ? <FiCreditCard size={14} /> : <FaIdCard size={14} />}
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <h4 className="font-bold text-gray-800 text-xs">{type}</h4>
                                                                        <p className="text-[10px] font-mono font-semibold text-gray-600 mt-0.5 truncate">{number || '---'}</p>
                                                                    </div>
                                                                </div>
                                                                {isVerified && (
                                                                    <div className="bg-green-500 text-white w-4 h-4 rounded-full flex items-center justify-center shadow-sm shrink-0">
                                                                        <FiCheckCircle size={10} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {fileUrl && (
                                                                <div className="mt-2 flex justify-end">
                                                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-[9px] font-bold text-[#C30010] hover:underline flex items-center gap-1 uppercase tracking-wider">
                                                                        <FiEye size={12} /> View
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                                {(!data.pan_number && !data.aadhaar_number) && (
                                                    <div className="text-gray-400 text-[11px] italic">No verified identity proofs added.</div>
                                                )}
                                            </div>
                                        </SectionCard>
                                    </div>

                                    {/* === SIDEBAR + VIEW PANELS === */}
                                    <div className="flex flex-col lg:flex-row lg:items-stretch gap-5 mt-6">

                                        {/* Sidebar Nav */}
                                        <div className="lg:w-72 shrink-0 flex">
                                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col w-full h-full min-h-full">
                                                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 shrink-0">
                                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Profile Sections</p>
                                                </div>
                                                <div className="flex-1">
                                                    {(() => {
                                                        const navItems = [
                                                            { id: 'attendance', label: 'Attendance', icon: FiCalendar },
                                                            { id: 'my-performance', label: 'Performance Analysis', icon: FiTrendingUp },
                                                            { id: 'experience-analysis', label: 'Previous Experience', icon: FiBriefcase },
                                                            { id: 'hr-notes', label: 'HR Notes', icon: FiBook },
                                                            { id: 'lms-tests', label: 'LMS Tests', icon: FiAward },
                                                            { id: 'educational-qualification', label: 'Educational Qualification', icon: FiBook },
                                                            { id: 'professional-skill-set', label: 'Professional Skill Set', icon: FiCode },
                                                            { id: 'certificates-awards', label: 'Certificates & Awards', icon: FiAward },
                                                            { id: 'git-code-performance', label: 'Git Code Performance', icon: FiActivity },
                                                        ];
                                                        if (!isReadOnly) {
                                                            navItems.push({ id: 'performance-revision', label: 'Performance Revision', icon: FiAward });
                                                        }
                                                        const viewerRoleId = Number(globalUser?.role_id || globalUser?.user_role_id || 0);
                                                        const isViewerAdmin = viewerRoleId === 1;

                                                        const unreadPr = Number(data.performance_revision_unread) || 0;
                                                        const unreadHrNotes = isViewerAdmin ? 0 : hrNotes.filter(
                                                            (n) => n.read_status === 'Unread' || n.read_status === 'Pending'
                                                        ).length;
                                                        const unreadLms = isViewerAdmin ? 0 : lmsNotifications.filter(
                                                            (n) => n.read_status === 'Unread'
                                                        ).length;
                                                        return navItems.map(({ id, label, icon: Icon }) => (
                                                            <button
                                                                key={id}
                                                                onClick={() => setActiveView(id)}
                                                                className={`group w-full flex items-center gap-3 px-5 py-3.5 text-sm font-bold transition-all duration-200 border-l-[3px]
                                                                ${activeView === id
                                                                        ? 'border-[#C30010] bg-[#ffebee] text-[#C30010] shadow-sm'
                                                                        : 'border-transparent text-gray-500 hover:bg-[#fff5f6] hover:text-[#C30010] hover:border-[#f3c7cc] hover:pl-6'}`}
                                                            >
                                                                <Icon size={17} className={`shrink-0 transition-colors duration-200 ${activeView === id ? 'text-[#C30010]' : 'text-gray-400 group-hover:text-[#C30010]'}`} />
                                                                <span className="text-left leading-snug flex-1">{label}</span>
                                                                {id === 'hr-notes' && unreadHrNotes > 0 && (
                                                                    <span className="flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                                                                        {unreadHrNotes > 99 ? '99+' : unreadHrNotes}
                                                                    </span>
                                                                )}
                                                                {id === 'lms-tests' && unreadLms > 0 && (
                                                                    <span className="flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                                                                        {unreadLms > 99 ? '99+' : unreadLms}
                                                                    </span>
                                                                )}
                                                                {id === 'performance-revision' && unreadPr > 0 && (
                                                                    <span className="flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                                                                        {unreadPr > 99 ? '99+' : unreadPr}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        ));
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content Panel */}
                                        <div className="flex-1 min-w-0">

                                            {activeView === 'attendance' && (
                                                <div className="space-y-6">
                                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                                        <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                                            <div>
                                                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Attendance Report</h3>
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    Monthly attendance summary for the selected employee.
                                                                    {(() => {
                                                                        const { startDate, endDate } = getMonthDateRange(attendanceYear, attendanceMonth);
                                                                        return ` ${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
                                                                    })()}
                                                                </p>
                                                            </div>
                                                            <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                                                <select
                                                                    value={attendanceMonth}
                                                                    onChange={(e) => setAttendanceMonth(Number(e.target.value))}
                                                                    className="px-2 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-600 font-medium focus:outline-none focus:ring-2 focus:ring-[#C30010]/20 text-xs"
                                                                >
                                                                    {monthNames.map((name, idx) => (
                                                                        <option key={name} value={idx + 1}>{name}</option>
                                                                    ))}
                                                                </select>
                                                                <select
                                                                    value={attendanceYear}
                                                                    onChange={(e) => setAttendanceYear(Number(e.target.value))}
                                                                    className="px-2 py-1 rounded-md border border-gray-200 bg-gray-50 text-gray-600 font-medium focus:outline-none focus:ring-2 focus:ring-[#C30010]/20 text-xs"
                                                                >
                                                                    {yearOptions.map((year) => (
                                                                        <option key={year} value={year}>{year}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div className="overflow-x-auto">
                                                            {attendanceLoading ? (
                                                                <div className="flex items-center justify-center py-16">
                                                                    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-[#C30010]"></div>
                                                                </div>
                                                            ) : (
                                                                <table className="min-w-full text-sm">
                                                                    <thead className="bg-[#faf7f2] text-[10px] font-black uppercase tracking-wider text-gray-500">
                                                                        <tr>
                                                                            <th className="px-4 py-3 text-center">No.</th>
                                                                            <th className="px-4 py-3 text-left">Employee Details</th>
                                                                            <th className="px-4 py-3 text-center">Work Days</th>
                                                                            <th className="px-4 py-3 text-center">Accrued</th>
                                                                            <th className="px-4 py-3 text-center">Present</th>
                                                                            <th className="px-4 py-3 text-center">Late</th>
                                                                            <th className="px-4 py-3 text-center">LOP</th>
                                                                            <th className="px-4 py-3 text-center">Leave</th>
                                                                            <th className="px-4 py-3 text-center">Perm</th>
                                                                            <th className="px-4 py-3 text-center">OD</th>
                                                                            <th className="px-4 py-3 text-center">Absent</th>
                                                                            <th className="px-4 py-3 text-center">Missing Status</th>
                                                                            <th className="px-4 py-3 text-center">Atten %</th>
                                                                            <th className="px-4 py-3 text-center">Grade</th>
                                                                            <th className="px-4 py-3 text-center">Action</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(() => {
                                                                            const profileUserId = getProfileUserId();
                                                                            const row = attendanceRow;
                                                                            const empName = row?.name || data.aadhaar_name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Employee Name';
                                                                            const empDesignation = row?.designation || data.job_role || data.designation || 'Employee Role';
                                                                            const empImg = row?.img || data.user_img;
                                                                            const percentage = Number(row?.percentage || 0);

                                                                            if (!profileUserId) {
                                                                                return (
                                                                                    <tr>
                                                                                        <td colSpan={15} className="px-4 py-14 text-center text-sm text-gray-400">
                                                                                            No employee selected.
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            }

                                                                            const clickableBadgeClass = "inline-flex min-w-8 justify-center rounded-md px-2.5 py-1 text-xs font-black border transition-all hover:scale-105 active:scale-95";
                                                                            return (
                                                                                <tr className={`border-t border-gray-100 ${row?.has_lop ? 'bg-red-50/30' : ''}`}>
                                                                                    <td className="px-4 py-4 text-center text-gray-500">1</td>
                                                                                    <td className="px-4 py-4 min-w-[240px]">
                                                                                        <div className="flex items-center gap-3">
                                                                                            <div 
                                                                                                className={`h-10 w-10 rounded-xl bg-[#ffebee] text-[#C30010] flex items-center justify-center font-bold text-xs overflow-hidden border-2 border-white shadow-md ${empImg ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
                                                                                                onDoubleClick={() => empImg && setPhotoPreviewUrl(empImg)}
                                                                                                title={empImg ? "Double click to view full image" : ""}
                                                                                            >
                                                                                                {empImg ? (
                                                                                                    <img src={empImg} alt="" className="h-full w-full object-cover" />
                                                                                                ) : (
                                                                                                    String(empName).slice(0, 2).toUpperCase()
                                                                                                )}
                                                                                            </div>
                                                                                            <div>
                                                                                                <p className="font-bold text-gray-800">{empName}</p>
                                                                                                <p className="text-[10px] uppercase tracking-wider text-red-600 font-black">{empDesignation}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center text-gray-700 font-bold">{row?.working_days ?? '—'}</td>
                                                                                    <td className="px-4 py-4 text-center text-blue-600 font-semibold">{row ? Math.max(0, row.accrued_leave || 0) : '—'}</td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <div className="flex flex-col items-center">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleAttendanceStatusClick('PRESENT')}
                                                                                                className={`${clickableBadgeClass} bg-emerald-50 text-emerald-700 border-emerald-100`}
                                                                                            >
                                                                                                {row?.present ?? 0}
                                                                                            </button>
                                                                                            {row?.holiday_worked > 0 && (
                                                                                                <span className="text-[10px] text-purple-600 font-bold mt-1 bg-purple-50 px-1.5 rounded border border-purple-100">
                                                                                                    +{row.holiday_worked} OT
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('LATE')} className={`${clickableBadgeClass} bg-amber-50 text-amber-700 border-amber-100`}>
                                                                                            {row?.late ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('LOP')} className={`${clickableBadgeClass} bg-rose-50 text-rose-700 border-rose-200`}>
                                                                                            {row?.lop ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('LEAVE')} className={`${clickableBadgeClass} bg-purple-50 text-purple-700 border-purple-100`}>
                                                                                            {row?.leave ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('PERMISSION')} className={`${clickableBadgeClass} bg-pink-50 text-pink-700 border-pink-100`}>
                                                                                            {row?.permission ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('OD')} className={`${clickableBadgeClass} bg-indigo-50 text-indigo-700 border-indigo-100`}>
                                                                                            {row?.od ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('ABSENT')} className={`${clickableBadgeClass} bg-rose-50 text-rose-700 border-rose-100`}>
                                                                                            {row?.absent ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button type="button" onClick={() => handleAttendanceStatusClick('MISSING_STATUS')} className={`${clickableBadgeClass} bg-orange-50 text-orange-700 border-orange-100`}>
                                                                                            {row?.missing_status ?? 0}
                                                                                        </button>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center min-w-[120px]">
                                                                                        <div className="flex flex-col items-center gap-1">
                                                                                            <span className="text-xs font-black text-gray-800">{row?.percentage ?? 0}%</span>
                                                                                            <div className="w-full max-w-[80px] h-1.5 rounded-full bg-gray-100 overflow-hidden border border-gray-200">
                                                                                                <div
                                                                                                    className={`h-full ${percentage >= 90 ? 'bg-emerald-500' : percentage >= 80 ? 'bg-blue-500' : percentage >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                                                                                    style={{ width: `${Math.min(100, percentage)}%` }}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <span className={`inline-flex rounded-lg border-2 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-widest ${row?.grade_variant || getGradeBadgeClass(row?.grade)}`}>
                                                                                            {row?.grade || '—'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-4 py-4 text-center">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={handleViewAttendanceDetails}
                                                                                            className="px-4 py-2 bg-white text-[#C30010] border-2 border-[#C30010] rounded-xl text-[11px] font-black uppercase tracking-tight hover:bg-[#C30010] hover:text-white transition-all shadow-sm active:scale-95 whitespace-nowrap"
                                                                                        >
                                                                                            View Details
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })()}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                                        {/* Leave Details - Donut Chart Card */}
                                                        <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition flex flex-col justify-between">
                                                            <div className="flex justify-between items-center mb-1">
                                                                <h3 className="text-base font-bold text-gray-800">Leave Details</h3>
                                                                <div className="flex gap-1.5">
                                                                    <select
                                                                        value={leaveMonth}
                                                                        onChange={(e) => setLeaveMonth(e.target.value)}
                                                                        className="border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-medium bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                                                    >
                                                                        <option value="all">All Months</option>
                                                                        {monthNames.map((m, i) => (
                                                                            <option key={m} value={i + 1}>{m}</option>
                                                                        ))}
                                                                    </select>
                                                                    <select
                                                                        value={leaveYear}
                                                                        onChange={(e) => setLeaveYear(Number(e.target.value))}
                                                                        className="border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-medium bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                                                    >
                                                                        {yearOptions.map((y) => (
                                                                            <option key={y} value={y}>{y}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            {leaveLoading ? (
                                                                <div className="flex items-center justify-center h-[250px]">
                                                                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#C30010]"></div>
                                                                </div>
                                                            ) : leaveAnalytics?.chart?.length > 0 ? (
                                                                <HighchartsReact
                                                                    highcharts={Highcharts}
                                                                    options={{
                                                                        exporting: {
                                                                            enabled: true,
                                                                            sourceWidth: 1000,
                                                                            chartOptions: {
                                                                                chart: { backgroundColor: '#ffffff' }
                                                                            }
                                                                        },
                                                                        chart: { type: 'pie', height: 250, spacing: [5, 5, 5, 5] },
                                                                        title: { text: null },
                                                                        plotOptions: {
                                                                            pie: {
                                                                                size: '88%',
                                                                                innerSize: '60%',
                                                                                center: ['40%', '45%'],
                                                                                dataLabels: { enabled: false },
                                                                                showInLegend: true,
                                                                                borderWidth: 0
                                                                            }
                                                                        },
                                                                        tooltip: { pointFormat: '<b>{point.y}</b>' },
                                                                        legend: {
                                                                            layout: 'vertical',
                                                                            align: 'right',
                                                                            verticalAlign: 'middle',
                                                                            itemStyle: { fontSize: '10px', fontWeight: '500' },
                                                                            labelFormat: '{name}: <b>{y}</b>'
                                                                        },
                                                                        series: [{
                                                                            name: 'Count',
                                                                            colorByPoint: true,
                                                                            data: leaveAnalytics.chart
                                                                        }],
                                                                        credits: { enabled: false }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="flex items-center justify-center h-[230px] text-gray-400">
                                                                    <div className="text-center">
                                                                        <FiClock className="text-3xl mx-auto mb-1.5 text-gray-300" />
                                                                        <p className="text-xs">No activity records</p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Leave Summary Stats - History Card */}
                                                        <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition flex flex-col justify-between">
                                                            <div className="flex justify-between items-center mb-3">
                                                                <h3 className="text-base font-bold text-gray-800">Leave History</h3>
                                                                <button
                                                                    onClick={handleAccountSummaryExport}
                                                                    className="flex items-center gap-1.5 px-3 py-1 bg-[#047857] hover:bg-[#064e3b] text-white rounded-full transition-all text-[10px] font-bold shadow-sm hover:shadow active:scale-95"
                                                                >
                                                                    <FiDownload size={12} />
                                                                    <span>Export</span>
                                                                </button>
                                                            </div>

                                                            {leaveLoading ? (
                                                                <div className="flex items-center justify-center py-10">
                                                                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#C30010]"></div>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 flex-1">
                                                                        <div
                                                                            onClick={() => handleStatBoxClick('total_taken', 'Total Leave Taken')}
                                                                            className="p-2.5 sm:p-3 bg-blue-50 rounded-lg flex flex-col justify-center border border-blue-200 cursor-pointer hover:bg-blue-100/70 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-tight">Total Leave Taken</p>
                                                                            <p className="text-lg sm:text-xl font-black text-blue-900 mt-0.5">{leaveAnalytics?.stats?.total_leave_count ?? 0}</p>
                                                                        </div>

                                                                        <div
                                                                            onClick={() => handleStatBoxClick('taken', 'Scheduled Leave Taken')}
                                                                            className="p-2.5 sm:p-3 bg-green-50/80 rounded-lg flex flex-col justify-center border border-green-100 cursor-pointer hover:bg-green-100 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-green-700 uppercase tracking-tight">Scheduled Leave Taken</p>
                                                                            <p className="text-lg sm:text-xl font-black text-green-950 mt-0.5">{leaveAnalytics?.stats?.taken ?? 0}</p>
                                                                        </div>

                                                                        <div
                                                                            onClick={() => handleStatBoxClick('unscheduled', 'Unscheduled Leave Taken')}
                                                                            className="p-2.5 sm:p-3 bg-orange-50/80 rounded-lg flex flex-col justify-center border border-orange-100 cursor-pointer hover:bg-orange-100 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-orange-700 uppercase tracking-tight">Unscheduled Leave Taken</p>
                                                                            <p className="text-lg sm:text-xl font-black text-orange-950 mt-0.5">{leaveAnalytics?.stats?.unscheduled ?? 0}</p>
                                                                        </div>

                                                                        <div
                                                                            onClick={() => handleStatBoxClick('pending', 'Pending Requests')}
                                                                            className="p-2.5 sm:p-3 bg-purple-50/80 rounded-lg flex flex-col justify-center border border-purple-100 cursor-pointer hover:bg-purple-100 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-purple-700 uppercase tracking-tight">Pending Req.</p>
                                                                            <p className="text-lg sm:text-xl font-black text-purple-950 mt-0.5">{leaveAnalytics?.stats?.request ?? 0}</p>
                                                                        </div>

                                                                        <div
                                                                            onClick={() => handleStatBoxClick('late', 'Late Punches')}
                                                                            className="p-2.5 sm:p-3 bg-emerald-50/80 rounded-lg flex flex-col justify-center border border-emerald-100 cursor-pointer hover:bg-emerald-100 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-tight">Late Punches</p>
                                                                            <p className="text-lg sm:text-xl font-black text-emerald-950 mt-0.5">{leaveAnalytics?.stats?.total_late ?? 0}</p>
                                                                        </div>

                                                                        <div className="p-2.5 sm:p-3 bg-pink-50 rounded-lg flex flex-col justify-center border border-pink-200">
                                                                            <p className="text-[10px] font-bold text-pink-700 uppercase tracking-tight">Balance</p>
                                                                            <p className="text-lg sm:text-xl font-black text-pink-950 mt-0.5">{leaveAnalytics?.stats?.current_balance ?? 0}</p>
                                                                        </div>

                                                                        <div
                                                                            onClick={() => handleStatBoxClick('permission', 'Paid Permission')}
                                                                            className="p-2.5 sm:p-3 bg-cyan-50/80 rounded-lg flex flex-col justify-center border border-cyan-100 cursor-pointer hover:bg-cyan-100 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-cyan-700 uppercase tracking-tight">Paid Permission</p>
                                                                            <p className="text-lg sm:text-xl font-black text-cyan-950 mt-0.5">{leaveAnalytics?.stats?.paid_permission ?? leaveAnalytics?.stats?.permission_hours ?? 0}h</p>
                                                                        </div>

                                                                        <div
                                                                            onClick={() => handleStatBoxClick('lop', 'Loss of Pay')}
                                                                            className="p-2.5 sm:p-3 bg-rose-50/80 rounded-lg flex flex-col justify-center border border-rose-100 cursor-pointer hover:bg-rose-100 transition-all shadow-sm hover:shadow active:scale-95"
                                                                        >
                                                                            <p className="text-[10px] font-bold text-rose-700 uppercase tracking-tight">Loss of Pay</p>
                                                                            <p className="text-lg sm:text-xl font-black text-rose-950 mt-0.5">{leaveAnalytics?.stats?.loss_of_pay ?? 0}</p>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {activeView === 'my-performance' && (
                                                <div className="space-y-6">
                                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                                        <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                            <div>
                                                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Overall Performance</h3>
                                                                <p className="text-xs text-gray-400 mt-1">Ticket, late punch, and leave trends for this employee.</p>
                                                            </div>
                                                            <select
                                                                value={performanceYear}
                                                                onChange={(e) => setPerformanceYear(Number(e.target.value))}
                                                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-[11px] text-gray-600 font-semibold focus:outline-none focus:ring-2 focus:ring-[#C30010]/20"
                                                            >
                                                                {yearOptions.map((year) => (
                                                                    <option key={year} value={year}>{year}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="p-5 lg:p-6">
                                                            {performanceLoading ? (
                                                                <div className="flex items-center justify-center py-16">
                                                                    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-[#C30010]"></div>
                                                                </div>
                                                            ) : performanceChart.series?.length > 0 ? (
                                                                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3 lg:p-4">
                                                                    <OverallTicketChart
                                                                        collegeName="- PERFORMANCE OVERVIEW"
                                                                        categories={performanceChart.categories}
                                                                        series={performanceChart.series}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 py-14 text-center">
                                                                    <FiTrendingUp size={32} className="text-gray-300 mb-3" />
                                                                    <p className="text-sm font-bold text-gray-500">No performance data available.</p>
                                                                    <p className="text-xs text-gray-400 mt-1">Ticket and attendance performance for this employee will appear here once available.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                </div>
                                            )}

                                            {activeView === 'experience-analysis' && (
                                                <div className="space-y-6">
                                                    <SectionCard
                                                        title="Previous Experience"
                                                        icon={FiBriefcase}
                                                        isEditing={false}
                                                    >
                                                        <div className="space-y-3">
                                                            {(data.previous_experience || []).length > 0 ? (
                                                                <>
                                                                    {(data.previous_experience || []).map((role, idx) => {
                                                                        const period = `${role.start_date ? format(new Date(role.start_date), 'MMM yyyy') : '-'} — ${role.end_date ? format(new Date(role.end_date), 'MMM yyyy') : 'Present'}`;
                                                                        const duration = getDurationText(role.start_date, role.end_date);
                                                                        return (
                                                                            <div
                                                                                key={`prev-exp-view-${idx}`}
                                                                                className="flex justify-between items-center p-3 border-l-4 border-[#C30010] bg-gray-50"
                                                                            >
                                                                                <div>
                                                                                    <div className="font-bold text-gray-800">
                                                                                        {role.job_role || '—'}
                                                                                    </div>
                                                                                    <div className="text-xs font-semibold text-gray-600 mb-0.5">
                                                                                        {role.company_name || '—'}
                                                                                    </div>
                                                                                    <div className="text-xs text-gray-500">
                                                                                        {period}
                                                                                        {duration ? ` • ${duration}` : ''}
                                                                                    </div>
                                                                                </div>
                                                                                {duration && (
                                                                                    <span className="text-xs bg-white border border-red-100 px-2 py-1 rounded text-[#C30010] whitespace-nowrap">
                                                                                        {duration}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}

                                                                    {getTotalDurationText(data.previous_experience) && (
                                                                        <div className="mt-5 pt-4 border-t flex items-center justify-between gap-3">
                                                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Previous Experience</span>
                                                                            <span className="text-xs font-bold text-gray-800 bg-white border px-3 py-1 rounded-full shadow-sm whitespace-nowrap">
                                                                                {getTotalDurationText(data.previous_experience)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="text-gray-400 text-xs italic">No previous company experience found.</div>
                                                            )}
                                                        </div>
                                                    </SectionCard>
                                                </div>
                                            )}

                                            {activeView === 'hr-notes' && (
                                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                                    <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                        <div>
                                                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">HR Notes</h3>
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                Published memos, notifications, events, and celebrations applicable to you.
                                                            </p>
                                                        </div>
                                                        <span className="inline-flex items-center rounded-full bg-rose-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-rose-600 border border-rose-100">
                                                            {hrNotes.length} Note{hrNotes.length === 1 ? '' : 's'}
                                                        </span>
                                                    </div>

                                                    <div className="p-5">
                                                        {hrNotesLoading ? (
                                                            <div className="flex items-center justify-center py-16">
                                                                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-[#C30010]"></div>
                                                            </div>
                                                        ) : hrNotes.length > 0 ? (
                                                            <div className="space-y-4">
                                                                {hrNotes.map((note) => {
                                                                    const isPersonal = note.message_type === 'Individual Employee';
                                                                    const noteTypeLabel = isPersonal ? 'Personal' : 'Global';
                                                                    const noteTypeClass = isPersonal
                                                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                                                        : 'bg-emerald-50 text-emerald-700 border-emerald-100';
                                                                    const priorityClass = note.priority === 'Critical'
                                                                        ? 'bg-red-50 text-red-700 border-red-100'
                                                                        : note.priority === 'Important'
                                                                            ? 'bg-orange-50 text-orange-700 border-orange-100'
                                                                            : 'bg-blue-50 text-blue-700 border-blue-100';
                                                                    const needsAck = parseInt(note.require_acknowledgement, 10) === 1
                                                                        && note.read_status !== 'Acknowledged';

                                                                    return (
                                                                        <div
                                                                            key={note.id}
                                                                            className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                                                                            onMouseEnter={() => handleMarkHrNoteRead(note)}
                                                                        >
                                                                            <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                                                                <div>
                                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                                        <h4 className="text-base font-bold text-gray-800">{note.title}</h4>
                                                                                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${noteTypeClass}`}>
                                                                                            {noteTypeLabel}
                                                                                        </span>
                                                                                        {note.priority && (
                                                                                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClass}`}>
                                                                                                {note.priority}
                                                                                            </span>
                                                                                        )}
                                                                                        {note.category && (
                                                                                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${getHrNoteCategoryClass(note.category)}`}>
                                                                                                {note.category}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                    <p className="mt-2 text-xs text-gray-400">
                                                                                        {isPersonal
                                                                                            ? 'Assigned specifically to you.'
                                                                                            : 'Shared as a published HR announcement.'}
                                                                                    </p>
                                                                                </div>

                                                                                <div className="flex flex-wrap items-center gap-2">
                                                                                    {note.read_status && (
                                                                                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${note.read_status === 'Acknowledged'
                                                                                            ? 'bg-green-50 text-green-700 border border-green-100'
                                                                                            : note.read_status === 'Read'
                                                                                                ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                                                                                : 'bg-amber-50 text-amber-700 border border-amber-100'
                                                                                            }`}>
                                                                                            {note.read_status}
                                                                                        </span>
                                                                                    )}
                                                                                    {parseInt(note.require_acknowledgement, 10) === 1 && (
                                                                                        <span className="inline-flex rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-rose-700">
                                                                                            Acknowledgement Required
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            <div className="px-5 py-4 space-y-4">
                                                                                <div
                                                                                    className="prose prose-sm max-w-none text-gray-700"
                                                                                    dangerouslySetInnerHTML={{ __html: note.description || '<p>No note details available.</p>' }}
                                                                                />

                                                                                {note.attachment_path && (
                                                                                    <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                                                                        <div>
                                                                                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Attachment</p>
                                                                                            <p className="mt-1 text-sm font-semibold text-gray-700">{note.attachment_name || 'HR Note Attachment'}</p>
                                                                                        </div>
                                                                                        <a
                                                                                            href={`${process.env.REACT_APP_IMAGEURL || ''}/${note.attachment_path}`}
                                                                                            target="_blank"
                                                                                            rel="noreferrer"
                                                                                            className="inline-flex items-center justify-center rounded-lg border border-[#f3c7cc] px-3 py-2 text-xs font-bold text-[#C30010] hover:bg-[#ffebee] transition-colors"
                                                                                        >
                                                                                            Open Attachment
                                                                                        </a>
                                                                                    </div>
                                                                                )}

                                                                                <div className="grid gap-3 text-xs text-gray-500 sm:grid-cols-2 xl:grid-cols-4">
                                                                                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                                                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Assigned Type</p>
                                                                                        <p className="mt-1 font-bold text-gray-700">{note.message_type || noteTypeLabel}</p>
                                                                                    </div>
                                                                                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                                                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Author</p>
                                                                                        <p className="mt-1 font-bold text-gray-700">{note.author_name || 'HR Department'}</p>
                                                                                    </div>
                                                                                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                                                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Effective Date</p>
                                                                                        <p className="mt-1 font-bold text-gray-700">{note.effective_date ? new Date(note.effective_date).toLocaleDateString('en-GB') : '-'}</p>
                                                                                    </div>
                                                                                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                                                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Posted On</p>
                                                                                        <p className="mt-1 font-bold text-gray-700">{note.created_at ? new Date(note.created_at).toLocaleDateString('en-GB') : '-'}</p>
                                                                                    </div>
                                                                                </div>

                                                                                {needsAck && (
                                                                                    <div className="flex justify-end pt-1">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => handleAcknowledgeHrNote(note)}
                                                                                            className="inline-flex items-center gap-2 rounded-lg bg-[#C30010] px-4 py-2 text-xs font-bold text-white hover:bg-[#a8000d] transition-colors"
                                                                                        >
                                                                                            <FiCheck size={14} /> Acknowledge
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center">
                                                                <FiBook size={34} className="text-gray-300 mb-3" />
                                                                <p className="text-sm font-bold text-gray-500">No published HR notes available.</p>
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    Memos, notifications, events, and celebrations will appear here when published for you.
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {activeView === 'lms-tests' && (
                                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                                    <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                        <div>
                                                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">LMS Test Assignments</h3>
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                Tests assigned to you through the Learning Management System.
                                                            </p>
                                                        </div>
                                                        <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-blue-600 border border-blue-100">
                                                            {lmsNotifications.length} Test{lmsNotifications.length === 1 ? '' : 's'}
                                                        </span>
                                                    </div>

                                                    <div className="p-5">
                                                        {lmsNotificationsLoading ? (
                                                            <div className="flex items-center justify-center py-16">
                                                                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
                                                            </div>
                                                        ) : lmsNotifications.length > 0 ? (
                                                            <div className="space-y-4">
                                                                {lmsNotifications.map((notif) => {
                                                                    const isUnread = notif.read_status === 'Unread';
                                                                    const hasDate = notif.scheduled_date && notif.scheduled_date !== '0000-00-00';
                                                                    return (
                                                                        <div
                                                                            key={notif.id}
                                                                            className={`rounded-xl border overflow-hidden transition-all duration-200 ${isUnread
                                                                                    ? 'border-blue-200 bg-blue-50/40 shadow-sm'
                                                                                    : 'border-gray-200 bg-white'
                                                                                }`}
                                                                            onMouseEnter={() => handleReadLmsNotification(notif)}
                                                                            onClick={() => handleReadLmsNotification(notif)}
                                                                        >
                                                                            <div className="px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                                                                        <FiAward size={14} className="text-blue-500 shrink-0" />
                                                                                        <h4 className="text-sm font-bold text-gray-800 truncate">{notif.test_name}</h4>
                                                                                        {isUnread && (
                                                                                            <span className="inline-flex rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                                                                                NEW
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                    {notif.batch_name && (
                                                                                        <p className="text-xs text-gray-500">
                                                                                            <span className="font-semibold text-gray-600">Batch:</span> {notif.batch_name}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex flex-wrap items-center gap-2 shrink-0">
                                                                                    {hasDate && (
                                                                                        <div className="flex items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50 px-2.5 py-1.5">
                                                                                            <FiCalendar size={11} className="text-amber-600" />
                                                                                            <span className="text-[11px] font-bold text-amber-700">
                                                                                                {new Date(notif.scheduled_date).toLocaleDateString('en-GB')}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                    {(() => {
                                                                                         let lifecycleLabel = 'ASSIGNED';
                                                                                         let lifecycleBadge = 'border-blue-200 bg-blue-50 text-blue-700';

                                                                                         if (notif.submission_status === 'Completed' || notif.submission_status === 'COMPLETED') {
                                                                                             lifecycleLabel = 'COMPLETED';
                                                                                             lifecycleBadge = 'border-emerald-200 bg-emerald-50 text-emerald-700 font-bold';
                                                                                         } else if (hasDate) {
                                                                                             const now = new Date();
                                                                                             const schedDate = notif.scheduled_date;
                                                                                             const startTimeStr = notif.start_time || '00:00:00';
                                                                                             const endTimeStr = notif.end_time || '23:59:59';
                                                                                             const startDateTime = new Date(`${schedDate}T${startTimeStr}`);
                                                                                             const endDateTime = new Date(`${schedDate}T${endTimeStr}`);

                                                                                             if (!isNaN(startDateTime.getTime()) && !isNaN(endDateTime.getTime())) {
                                                                                                 if (now < startDateTime) {
                                                                                                     lifecycleLabel = 'UPCOMING';
                                                                                                     lifecycleBadge = 'border-amber-200 bg-amber-50 text-amber-700 font-bold';
                                                                                                 } else if (now >= startDateTime && now <= endDateTime) {
                                                                                                     lifecycleLabel = 'LIVE';
                                                                                                     lifecycleBadge = 'border-emerald-300 bg-emerald-100 text-emerald-800 font-black animate-pulse';
                                                                                                 } else {
                                                                                                     lifecycleLabel = 'EXPIRED';
                                                                                                     lifecycleBadge = 'border-rose-200 bg-rose-50 text-rose-700 font-bold';
                                                                                                 }
                                                                                             }
                                                                                         }
                                                                                         return (
                                                                                             <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wide ${lifecycleBadge}`}>
                                                                                                 {lifecycleLabel}
                                                                                             </span>
                                                                                         );
                                                                                     })()}
                                                                                     <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${isUnread
                                                                                            ? 'border-blue-100 bg-blue-50 text-blue-700'
                                                                                            : 'border-gray-100 bg-gray-50 text-gray-500'
                                                                                        }`}>
                                                                                        {notif.read_status}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-2.5 flex items-center justify-between">
                                                                                <span className="text-[11px] text-gray-400">
                                                                                    Assigned {notif.created_at ? new Date(notif.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                                                                                </span>
                                                                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600">
                                                                                    <FiCheckCircle size={11} /> LMS Assignment
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center">
                                                                <FiAward size={34} className="text-gray-300 mb-3" />
                                                                <p className="text-sm font-bold text-gray-500">No LMS tests assigned yet.</p>
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    Tests assigned to you through the LMS module will appear here.
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {activeView === 'educational-qualification' && (
                                                <div>
                                                    <SectionCard
                                                        title="Educational Qualification"
                                                        icon={FiBook}
                                                        isEditing={false}
                                                    >
                                                        <div className="space-y-3">
                                                            {(data.academic_details || []).map((item, idx) => (
                                                                <div key={idx} className="flex justify-between items-center p-3 border-l-4 border-[#C30010] bg-gray-50">
                                                                    <div>
                                                                        <div className="font-bold text-gray-800">
                                                                            {item.qualification} - {item.degree || item.board}
                                                                            {item.specialization && <span className="text-gray-600 font-medium ml-1">({item.specialization})</span>}
                                                                        </div>
                                                                        <div className="text-xs font-semibold text-gray-600 mb-0.5">{item.institute}</div>
                                                                        <div className="text-xs text-gray-500">Passed: {item.year} • Score: {item.percentage} {item.score_type === 'CGPA' ? '(CGPA)' : '%'}</div>
                                                                    </div>
                                                                    {item.file && <a href={item.file} target="_blank" className="text-xs bg-white border border-red-100 px-2 py-1 rounded text-[#C30010] hover:bg-[#ffebee] transition">View Cert</a>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </SectionCard>
                                                </div>
                                            )}

                                            {activeView === 'professional-skill-set' && (
                                                <div>
                                                    {/* 2.5 Skill Set */}
                                                    <SectionCard
                                                        title="Professional Skill Set"
                                                        icon={FiCode}
                                                        isEditing={false}
                                                    >
                                                        <div>
                                                            {(data.skills || []).length > 0 ? (
                                                                <div className="flex flex-wrap gap-4">
                                                                    {['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills', 'Other'].map(cat => {
                                                                        const catSkills = (data.skills || []).filter(s => s.category === cat || (!['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills'].includes(s.category) && cat === 'Other' && !['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills'].includes(s.category)));
                                                                        if (catSkills.length === 0) return null;
                                                                        return (
                                                                            <div key={cat} className="flex-1 min-w-[200px]">
                                                                                <h6 className="text-[10px] uppercase font-bold text-gray-400 mb-2">{cat}</h6>
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {catSkills.map((s, i) => (
                                                                                        <div key={i} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2 shadow-sm">
                                                                                            <span className="font-semibold text-gray-700 text-xs">{s.skill}</span>
                                                                                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${s.proficiency === 'Expert' ? 'bg-[#C30010] text-white shadow-sm shadow-red-100' : s.proficiency === 'Advanced' ? 'bg-[#ffebee] text-[#C30010] border border-red-100' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                                                                                {s.proficiency}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {/* Fallback for undefined categories or customized ones if config changes */}
                                                                    {(() => {
                                                                        const predefined = ['Programming Languages', 'Frameworks & Libraries', 'Databases', 'Tools & IDEs', 'Cloud & DevOps', 'Soft Skills', 'Other'];
                                                                        const customCats = [...new Set((data.skills || []).map(s => s.category).filter(c => !predefined.includes(c)))];
                                                                        return customCats.map(cat => (
                                                                            <div key={cat} className="flex-1 min-w-[200px]">
                                                                                <h6 className="text-[10px] uppercase font-bold text-gray-400 mb-2">{cat}</h6>
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {(data.skills || []).filter(s => s.category === cat).map((s, i) => (
                                                                                        <div key={i} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2 shadow-sm">
                                                                                            <span className="font-semibold text-gray-700 text-xs">{s.skill}</span>
                                                                                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${s.proficiency === 'Expert' ? 'bg-[#C30010] text-white shadow-sm shadow-red-100' : s.proficiency === 'Advanced' ? 'bg-[#ffebee] text-[#C30010] border border-red-100' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                                                                                {s.proficiency}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        ));
                                                                    })()}
                                                                </div>
                                                            ) : (
                                                                <div className="text-gray-400 text-xs italic">No skills added yet.</div>
                                                            )}
                                                        </div>
                                                    </SectionCard>
                                                </div>
                                            )}

                                            {activeView === 'certificates-awards' && (
                                                <div>
                                                    {/* New Section: Certificates & Awards */}
                                                    <SectionCard
                                                        title={
                                                            <div className="flex items-center gap-2">
                                                                <span>Certificates & Awards</span>
                                                                {(data.certificates || []).length > 2 && (
                                                                    <FiStar className="text-yellow-500" size={20} fill="currentColor" />
                                                                )}
                                                            </div>
                                                        }
                                                        icon={FiAward}
                                                        isEditing={false}
                                                    >
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            {(data.certificates || []).map((item, idx) => (
                                                                <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg bg-white shadow-sm hover:shadow-md transition group relative overflow-hidden">
                                                                    <div className={`p-2 rounded-full shrink-0 ${item.type === 'Award' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-[#C30010]'}`}>
                                                                        {item.type === 'Award' ? <FiAward size={20} /> : <FiFileText size={20} />}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <h5 className="font-bold text-gray-800 text-sm truncate" title={item.title}>{item.title}</h5>
                                                                        {item.issuing_authority && <p className="text-xs text-gray-600 font-medium">{item.issuing_authority}</p>}

                                                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[10px] text-gray-500 font-semibold uppercase tracking-wider">
                                                                            <span>{item.type}</span>
                                                                            <span>•</span>
                                                                            <span>Issued: {item.issue_date}</span>
                                                                            {item.validity_type && (
                                                                                <>
                                                                                    <span>•</span>
                                                                                    <span className={item.validity_type === 'Perpetual' ? 'text-green-600' : 'text-[#C30010]'}>{item.validity_type}</span>
                                                                                </>
                                                                            )}
                                                                        </div>

                                                                        {item.validity_type === 'Renewable' && item.expiry_date && (
                                                                            <div className="mt-1 flex items-center gap-2">
                                                                                <p className={`text-[10px] font-bold ${new Date(item.expiry_date) < new Date() ? 'text-red-500' : 'text-gray-500'}`}>
                                                                                    {new Date(item.expiry_date) < new Date() ? 'EXPIRED' : 'Expires'}: {item.expiry_date}
                                                                                </p>
                                                                                {new Date(item.expiry_date) < new Date() && (
                                                                                    <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded border border-red-200 uppercase tracking-tight font-bold">Renewal Required</span>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {item.file && <a href={item.file} target="_blank" className="text-[10px] text-[#C30010] hover:underline flex items-center gap-1 mt-2 font-bold uppercase tracking-wider"><FiCheckCircle size={10} /> View Document</a>}
                                                                    </div>
                                                                    {/* Status Line */}
                                                                    {item.validity_type === 'Renewable' && new Date(item.expiry_date) < new Date() && (
                                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500"></div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {(data.certificates || []).length === 0 && <div className="text-gray-400 text-xs italic col-span-full">No achievements added yet.</div>}
                                                        </div>
                                                    </SectionCard >
                                                </div>
                                            )}

                                            {activeView === 'git-code-performance' && (
                                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                                    <div className="px-5 py-4 border-b border-gray-100">
                                                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Git Code Performance</h3>
                                                        <p className="text-xs text-gray-400 mt-1">Commit activity, contributions, and code performance metrics.</p>
                                                    </div>
                                                    <div className="p-5">
                                                        <EmployeeGitActivity
                                                            isHROrAdmin={isHROrAdmin}
                                                            roleId={roleId}
                                                            profileUserId={getProfileUserId()}
                                                            profileEmail={profileEmail}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {activeView === 'performance-revision' && (
                                                <EmployeePerformanceRevisionModal
                                                    open={true}
                                                    onClose={null}
                                                    isAdmin={roleId === 1}
                                                    profile={getPerformanceRevisionProfile()}
                                                    onSave={handlePerformanceRevisionSave}
                                                    saving={performanceRevisionSaving}
                                                    inline={true}
                                                    performanceRevisions={data.performance_revisions || []}
                                                    onDelete={handlePerformanceRevisionDelete}
                                                    onUpload={handlePerformanceRevisionUpload}
                                                />
                                            )}


                                        </div>
                                    </div>

                                </div>
                            </>
                        </div>
                    </div>
                </div>
            )}

            {attendanceDetailEmp && (
                <div className="fixed inset-0 z-[100] bg-gray-900/60 backdrop-blur-md flex items-center justify-center p-4">
                    {(() => {
                        const statusFilter = attendanceDetailEmp?.statusFilter || 'ALL';
                        const filteredAttendanceDetailData = getAttendanceDetailRowsForDisplay(
                            attendanceDetailData,
                            statusFilter
                        );
                        const filterMeta = getAttendanceFilterMeta(statusFilter);
                        const popupColumns = getAttendanceDetailPopupColumns(statusFilter);
                        const fmtTime = (val) => (val && val !== '-' ? val : '-');
                        const fmtDetail = (d) => {
                            const raw = (d.punch_records || '').trim();
                            if (!raw || raw === 'No Logs' || raw === '-') return '—';
                            return raw;
                        };
                        const renderAttendanceDetailCell = (col, d, idx) => {
                            switch (col.key) {
                                case 'index':
                                    return (
                                        <td key={col.key} className="px-4 py-3.5 text-center text-gray-400 font-mono text-[10px]">
                                            {idx + 1}
                                        </td>
                                    );
                                case 'date':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 font-extrabold text-gray-700 whitespace-nowrap">
                                            {formatDateDDMMYYYY(d.attendance_date)}
                                        </td>
                                    );
                                case 'status':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded-[4px] text-[9px] font-black uppercase tracking-tight ${getAttendanceStatusBadgeClass(d.status)}`}>
                                                {d.status === 'LOP' ? 'ON LEAVE' : (d.status || 'ABSENT').replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                    );
                                case 'check_in':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center font-bold text-emerald-600">
                                            {fmtTime(d.check_in)}
                                        </td>
                                    );
                                case 'check_out':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center font-bold text-rose-600">
                                            {fmtTime(d.check_out)}
                                        </td>
                                    );
                                case 'permission':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center">
                                            {(d.permission_start_time || d.permission_end_time) ? (
                                                <span className="text-[9px] font-black text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-100 whitespace-nowrap">
                                                    {d.permission_start_time || '--:--'} - {d.permission_end_time || '--:--'}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] font-bold text-gray-400">-</span>
                                            )}
                                        </td>
                                    );
                                case 'deviation': {
                                    const late = (d.lateVal !== undefined && d.lateVal !== null) ? d.lateVal : calculateAttendanceLate(d.check_in);
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center font-bold text-amber-600 text-[10px]">
                                            {late && late !== '-' ? late : '—'}
                                        </td>
                                    );
                                }
                                case 'ot': {
                                    const ot = (d.otVal !== undefined && d.otVal !== null) ? d.otVal : calculateAttendanceOvertime(d.check_out);
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center font-bold text-red-600 text-[10px]">
                                            {ot && ot !== '-' ? ot : '—'}
                                        </td>
                                    );
                                }
                                case 'production': {
                                    const prod = calculateAttendanceProduction(d.check_in, d.check_out);
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center">
                                            {prod !== '-' ? (
                                                <span className={`px-2 py-1 rounded-md text-[10px] font-black text-white shadow-sm inline-flex items-center gap-1 ${parseFloat(prod) >= 8 ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                                    <FiClock size={10} /> {prod}
                                                </span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                    );
                                }
                                case 'details':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-[11px] font-semibold text-gray-600">
                                            {fmtDetail(d)}
                                        </td>
                                    );
                                case 'leave_schedule': {
                                    const schedule = d.leave_schedule
                                        || (d.is_unscheduled === 1 || d.is_unscheduled === true
                                            ? 'Unscheduled'
                                            : (d.is_unscheduled === 0 || d.is_unscheduled === false ? 'Scheduled' : null));
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-center whitespace-nowrap">
                                            {schedule ? (
                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide border ${
                                                    schedule === 'Unscheduled'
                                                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                                                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                }`}>
                                                    {schedule}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] font-bold text-gray-300">—</span>
                                            )}
                                        </td>
                                    );
                                }
                                case 'punches':
                                    return (
                                        <td key={col.key} className="px-3 py-3.5 text-[11px] font-medium text-gray-400 max-w-[220px]" title={d.punch_records}>
                                            <div className="truncate">{d.punch_records || 'No Logs'}</div>
                                        </td>
                                    );
                                default:
                                    return null;
                            }
                        };
                        return (
                            <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col border border-white/20">
                                <div className="px-6 py-5 bg-[#c40010] text-white flex justify-between items-start shrink-0">
                                    <div>
                                        <h2 className="text-2xl font-black tracking-tight">{attendanceDetailEmp.name}</h2>
                                        <p className="mt-2 text-lg font-black uppercase tracking-wide">
                                            {statusFilter === 'ALL'
                                                ? `All Attendance – ${filteredAttendanceDetailData.length} Total`
                                                : `${String(filterMeta.label).replace(/ Days$/i, '')} Days – ${filteredAttendanceDetailData.length} Total`}
                                        </p>
                                        <span className="mt-3 inline-flex rounded-full bg-white/15 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-white border border-white/10">
                                            {monthNames[attendanceMonth - 1]?.slice(0, 3)}: {filteredAttendanceDetailData.length}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setAttendanceDetailEmp(null)}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10 transition-all"
                                    >
                                        <FiXCircle size={22} />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-5 sm:p-6 bg-white min-h-[200px]">
                                    {attendanceDetailLoading ? (
                                        <div className="h-40 flex flex-col items-center justify-center space-y-4">
                                            <div className="w-12 h-12 border-4 border-[#C30010] border-t-transparent rounded-full animate-spin" />
                                            <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Compiling Daily Logs...</p>
                                        </div>
                                    ) : filteredAttendanceDetailData.length === 0 ? (
                                        <div className="h-40 flex flex-col items-center justify-center space-y-4">
                                            <p className="text-sm font-black text-gray-400 uppercase tracking-widest">No matching attendance logs found for this period.</p>
                                        </div>
                                    ) : (
                                        <div className="bg-white rounded-[22px] shadow-sm border border-gray-100 overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-[11px] text-left border-collapse">
                                                    <thead className="bg-[#f5f5f7] text-gray-600 text-[10px] font-black uppercase tracking-[0.18em] sticky top-0 border-b border-gray-200">
                                                        <tr>
                                                            {popupColumns.map((col) => (
                                                                <th
                                                                    key={col.key}
                                                                    className={`px-3 py-4 ${col.width || ''} ${col.align === 'center' ? 'text-center' : ''} ${col.key === 'index' ? 'text-gray-400' : ''} whitespace-nowrap`}
                                                                >
                                                                    {col.label}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {filteredAttendanceDetailData.map((d, idx) => (
                                                            <tr
                                                                key={`${d.attendance_date}-${idx}`}
                                                                className={`transition-colors border-b border-gray-50 last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fffdfd]'} hover:bg-red-50/30`}
                                                            >
                                                                {popupColumns.map((col) => renderAttendanceDetailCell(col, d, idx))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="px-5 py-4 bg-[#fafafa] border-t border-gray-200 flex items-center justify-between shrink-0">
                                    <span className="text-sm text-gray-500 font-medium">
                                        Period: {(() => {
                                            const { startDate, endDate } = getMonthDateRange(attendanceYear, attendanceMonth);
                                            return `${formatDateDDMMYYYY(startDate)} – ${formatDateDDMMYYYY(endDate)}`;
                                        })()}
                                    </span>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}
            {wizardOpen && (
                <EmployeeUpdateWizard
                    steps={EMPLOYEE_WIZARD_STEPS}
                    currentStep={wizardStep}
                    completedSteps={wizardCompletedSteps}
                    onClose={closeUpdateWizard}
                    onPrevious={handleWizardPrevious}
                    onNext={handleWizardNext}
                    onSave={handleWizardSave}
                    onStepClick={handleWizardStepClick}
                    isNextDisabled={!isWizardStepValid(wizardStep)}
                    isSaveDisabled={!isWizardSaveValid()}
                >
                    {wizardStep === 1 && (
                        <>
                            <WizardFormShell title="Personal Information" icon={FiUser}>
                                {renderPersonalEditContent(true)}
                            </WizardFormShell>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-stretch">
                                <WizardFormShell title="Community & Languages" icon={FiGlobe} inline>
                                    {renderCommunityEditContent()}
                                </WizardFormShell>
                                <WizardFormShell title="Health & Insurance" icon={FiActivity} inline>
                                    {renderHealthEditContent()}
                                </WizardFormShell>
                                <WizardFormShell title="Previous Experience" icon={FiBriefcase} inline>
                                    {renderExperienceAnalysisContent()}
                                </WizardFormShell>
                            </div>
                        </>
                    )}
                    {wizardStep === 2 && (
                        <div className="emp-wizard-section-gap">
                            <WizardFormShell title="Bank Details" icon={FiCreditCard} inline>
                                {renderBankEditContent()}
                            </WizardFormShell>
                            <div className="emp-wizard-section-divider" aria-hidden="true" />
                            <WizardFormShell title="Identity Proofs (KYC)" icon={FiShield} inline>
                                {renderIdentityWizardContent()}
                            </WizardFormShell>
                        </div>
                    )}
                    {wizardStep === 3 && (
                        <div className="emp-wizard-section-gap">
                            <WizardFormShell title="Educational Qualification" icon={FiBook} inline>
                                {renderAcademicEditContent()}
                            </WizardFormShell>
                            <div className="emp-wizard-section-divider" aria-hidden="true" />
                            <WizardFormShell title="Professional Skill Set" icon={FiCode} inline>
                                {renderSkillsEditContent()}
                            </WizardFormShell>
                            <div className="emp-wizard-section-divider" aria-hidden="true" />
                            <WizardFormShell title="Certificates & Awards" icon={FiAward} inline>
                                {renderCertificatesEditContent()}
                            </WizardFormShell>
                        </div>
                    )}
                </EmployeeUpdateWizard>
            )}
            {summaryModalConfig?.isOpen && (
                <div className="fixed inset-0 z-[110] bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 p-5 sm:p-6"
                    >
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-4">
                            <h3 className="text-xl font-black italic text-gray-900 tracking-tight">{summaryModalConfig.title}</h3>
                            <button
                                onClick={() => setSummaryModalConfig(null)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        <div className="max-h-[350px] overflow-y-auto space-y-3 pr-1">
                            {summaryModalConfig.loading ? (
                                <div className="py-12 flex flex-col items-center justify-center space-y-3">
                                    <div className="w-8 h-8 border-3 border-[#C30010] border-t-transparent rounded-full animate-spin" />
                                    <p className="text-xs font-bold text-gray-400">Loading details...</p>
                                </div>
                            ) : summaryModalConfig.data.length === 0 ? (
                                <div className="py-10 text-center">
                                    <p className="text-xs font-bold text-gray-400">No records found for this period</p>
                                </div>
                            ) : (
                                summaryModalConfig.data.map((item, idx) => {
                                    const itemDate = item.date || item.from_date || item.attendance_date || '-';
                                    const itemDesc = item.reason || item.type || item.detail || 'Details';
                                    
                                    let badgeText = '';
                                    let badgeStyle = 'bg-gray-100 text-gray-600';

                                    if (summaryModalConfig.category === 'late') {
                                        badgeText = item.first_punch && item.first_punch !== 'N/A' ? item.first_punch : 'Late';
                                        badgeStyle = 'bg-red-50 text-[#C30010] border border-red-100 font-mono font-bold';
                                    } else if (summaryModalConfig.category === 'permission') {
                                        badgeText = item.duration_hrs ? `${item.duration_hrs}h` : 'Permission';
                                        badgeStyle = 'bg-cyan-50 text-cyan-700 border border-cyan-100 font-bold';
                                    } else if (summaryModalConfig.category === 'lop') {
                                        badgeText = item.duration ? `${item.duration} LOP` : 'LOP';
                                        badgeStyle = 'bg-rose-50 text-rose-700 border border-rose-100 font-bold';
                                    } else if (summaryModalConfig.category === 'pending') {
                                        badgeText = (item.status || 'Pending').replace('Pending_', '');
                                        badgeStyle = 'bg-purple-50 text-purple-700 border border-purple-100 font-bold';
                                    } else {
                                        badgeText = item.duration ? `${item.duration} Day` : (item.type || 'Leave');
                                        badgeStyle = 'bg-amber-50 text-amber-700 border border-amber-100 font-bold';
                                    }

                                    return (
                                        <div key={idx} className="flex justify-between items-center py-2.5 border-b border-gray-100 last:border-0">
                                            <div>
                                                <p className="text-[11px] font-bold text-gray-400 tracking-wide font-mono">{itemDate}</p>
                                                <p className="text-xs font-semibold text-gray-800 mt-0.5">{itemDesc}</p>
                                            </div>
                                            <span className={`px-2.5 py-1 rounded-full text-xs shrink-0 ml-2 ${badgeStyle}`}>
                                                {badgeText}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                        </div>


                    </motion.div>
                </div>
            )}
        </>
    );
}
